import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../contexts/SidebarContext";
import {
  FaPaperclip,
  FaImage,
  FaCamera,
  FaPlusCircle,
  FaComments,
} from "react-icons/fa";
import {
  FaClone,
  FaThumbsUp,
  FaThumbsDown,
  FaShareAlt,
  FaEdit,
  FaChevronLeft,
  FaInfoCircle,
  FaBookOpen,
  FaFolderOpen,
  FaListUl,
} from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import { FaPencil } from "react-icons/fa6";
import { FaBook } from "react-icons/fa6";
import { FaPlay, FaPause, FaVolumeUp, FaRedo } from "react-icons/fa";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import { FaCloudArrowUp } from "react-icons/fa6"; // Upload file, Gallery, and Camera
import { FaHandPaper } from "react-icons/fa";
import { ChevronDownIcon, CheckIcon } from "lucide-react";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import ShareButton from "./ShareButton";
import Message from "./Message";
import MobileChatUIStandalone from "./MobileChatUI";
const baseUrl = import.meta.env.VITE_APP_BASE_URL;
import { toast } from "react-toastify";
import { MdGraphicEq } from "react-icons/md";
import LiveInteraction from "./LiveInteraction";
import RateLimitModal from "./RateLimitModal";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.min.js?url";
import axios from "axios";
import { fabric } from "fabric";
import mammoth from "mammoth";
import WorkbookEvaluate from "./WorkbookEvaluate";
import { useTeachingMode } from "../contexts/TeachingModeContext";
import { useClearChat } from "../contexts/ClearChatContext";
import { UserContext } from "../contexts/UserContext";
import { SyllabusContext } from "../contexts/SyllabusContext";
import * as Sentry from "@sentry/react";
import SimpleThreeDotsLoader from "./SimpleThreeDotsLoader";
import StorageService from "../services/storageService";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";
import Header from "./common/Header";
// Set up the worker for PDF.js
GlobalWorkerOptions.workerSrc = pdfjsWorker;
// Smooth streaming of bot responses (word-chunked, non-typewriter)
// Appends small chunks at short intervals for a seamless feel
const STREAM_CHUNK_TARGET_CHARS = 40; // target characters per chunk
const STREAM_INTERVAL_MS = 60; // delay between chunks (slowed slightly)
// Loader message pool and helper
const LOADING_MESSAGES = [
  "Please wait, preparing response…",
  "Thinking…",
  "One moment, almost ready…",
  "Analyzing your question…",
];
const getRandomLoadingMessage = () =>
  LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)];
const DOUBT_SOLVER_FILE = "components/DoubtSolver.jsx";

const goalLabels = [
  "School Exams",
  "IIT JEE Mains",
  "IIT JEE Advanced",
  "NEET",
  "CUET",
  "Olympiad",
  "NTSE",
  "SAT",
];

const goalOptions = goalLabels.map((label) => ({
  label,
  value: label.toLowerCase().replace(/\s+/g, "_"),
}));

const RATE_LIMIT_MODAL_SUPPORT_MESSAGE =
  "You've reached the current limit for Solve Text. Please wait a few minutes before trying again or contact support if you need a higher limit.";

const formatFileSize = (size) => {
  if (size === null || size === undefined) {
    return "";
  }

  const normalizedSize =
    typeof size === "number"
      ? size
      : typeof size === "string"
      ? Number(size.trim())
      : Number(size);

  if (!Number.isFinite(normalizedSize)) {
    return typeof size === "string" ? size : "";
  }

  const kilobytes = normalizedSize / 1024;
  const fractionDigits = kilobytes >= 10 ? 0 : 1;

  return `${kilobytes.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} KB`;
};

const getFileCreatedAt = (file) => {
  if (!file) {
    return null;
  }

  if (file.createdAt) {
    return file.createdAt;
  }
  if (file.createdAT) {
    return file.createdAT;
  }
  if (file.created_at) {
    return file.created_at;
  }

  if (file.uploadDate && file.uploadTime) {
    return `${file.uploadDate} ${file.uploadTime}`;
  }

  if (file.uploadDate) {
    return file.uploadDate;
  }

  return null;
};

const parseCreatedAt = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const dateFromNumber = new Date(value);
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const candidates = [normalized];
    if (!normalized.includes("T") && normalized.includes(" ")) {
      candidates.push(normalized.replace(" ", "T"));
    }
    if (!normalized.endsWith("Z") && !normalized.match(/[+-]\d\d:?\d\d$/)) {
      candidates.push(`${normalized}Z`);
    }

    for (const candidate of candidates) {
      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }

  return null;
};
const truncateFileName = (filename, maxLength = 30) => {
  if (
    !filename ||
    typeof filename !== "string" ||
    filename.length <= maxLength
  ) {
    return filename;
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex <= 0 || lastDotIndex === filename.length - 1) {
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const extension = filename.slice(lastDotIndex);
  const baseLength = maxLength - extension.length - 3;

  if (baseLength <= 0) {
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  const baseName = filename.slice(0, baseLength);
  return `${baseName}...${extension}`;
};

function safeStringify(value, max = 5000) {
  try {
    const json = JSON.stringify(value, (key, val) => {
      if (typeof val === "string" && val.length > 2000) {
        return val.slice(0, 2000) + "…[truncated]";
      }
      return val;
    });
    return json.length > max ? json.slice(0, max) + "…[truncated]" : json;
  } catch {
    return "[Unserializable]";
  }
}

const captureSentryError = (heading, error, extra = {}) => {
  let normalizedError;

  if (error instanceof Error) {
    // Keep original message and stack, trim only if message is huge
    const shortMessage =
      error.message.length > 1000
        ? error.message.slice(0, 1000) + "…[truncated]"
        : error.message;

    normalizedError = new Error(shortMessage);
    normalizedError.name = error.name;
    normalizedError.stack = error.stack;
  } else {
    // Non-Error inputs → wrap into Error
    normalizedError = new Error(
      typeof error === "string" ? error.slice(0, 1000) : heading
    );
  }

  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("component", "DoubtSolver");
    scope.setExtra("error_heading", heading);
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    if (!(error instanceof Error)) {
      scope.setExtra("original_error_value", error);
    }
    Sentry.captureException(normalizedError);
  });
};
const captureSentrySuccess = (heading, extra = {}) => {
  Sentry.withScope((scope) => {
    scope.setLevel("info");
    scope.setTag("component", "DoubtSolver");
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    Sentry.captureMessage(heading);
  });
};
const logApiSuccess = (endpoint, response, extra = {}) => {
  if (response?.ok) {
    captureSentrySuccess(`${DOUBT_SOLVER_FILE} | ${endpoint} | success`, {
      status: response.status,
      endpoint,
      ...extra,
    });
  }
};
const logSpeechSynthesisUsage = (context, extra = {}) => {
  const { responseText, ...rest } = extra || {};
  captureSentrySuccess(`${DOUBT_SOLVER_FILE} | speechSynthesis | ${context}`, {
    audioSource: "speech-synthesis",
    responseTextLength: responseText?.length ?? 0,
    ...rest,
  });
};
const parseJsonWithSuccessLog = async (response, endpoint, extra = {}) => {
  if (!response) {
    return null;
  }
  if (!response.ok) {
    let errorText = "";
    try {
      errorText = await response.text();
    } catch (_) {
      errorText = "Unable to read error body";
    }
    throw new Error(
      `${endpoint} responded with ${response.status}: ${errorText}`
    );
  }
  const data = await response.json();
  logApiSuccess(endpoint, response, {
    ...extra,
    hasAudio: Boolean(data?.audio_base64),
  });
  return data;
};
// Function to remove markdown formatting
const removeMarkdownFormatting = (text) => {
  if (!text) return text;
  // Remove HTML tags first
  text = text.replace(/<[^>]+>/g, "");
  // Remove markdown headers
  text = text.replace(/^#{1,6}\s+/gm, "");
  // Remove bold formatting (**text** or __text__)
  text = text.replace(/\*\*(.*?)\*\*/g, "$1");
  text = text.replace(/__(.*?)__/g, "$1");
  // Remove italic formatting (*text* or _text_)
  text = text.replace(/\*(.*?)\*/g, "$1");
  text = text.replace(/_(.*?)_/g, "$1");
  // Remove strikethrough formatting (~~text~~)
  text = text.replace(/~~(.*?)~~/g, "$1");
  // Remove inline code formatting (`code`)
  text = text.replace(/`([^`]+)`/g, "$1");
  // Remove code blocks (```code``` or ~~~code~~~)
  text = text.replace(/```[\s\S]*?```/g, "");
  text = text.replace(/~~~[\s\S]*?~~~/g, "");
  // Remove blockquotes (> text)
  text = text.replace(/^>\s+/gm, "");
  // Remove list markers (-, *, +, 1., 2., etc.)
  text = text.replace(/^[\s]*[-*+]\s+/gm, "");
  text = text.replace(/^[\s]*\d+\.\s+/gm, "");
  // Remove link formatting [text](url) -> text
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Remove image formatting ![alt](url) -> alt
  text = text.replace(/!\[([^\]]*)\]\([^)]+\)/g, "$1");
  // Remove horizontal rules (---, ___, ***)
  text = text.replace(/^[\s]*[-*_]{3,}[\s]*$/gm, "");
  // Remove table formatting (| text | text |)
  text = text.replace(/^\|[\s\S]*?\|$/gm, "");
  // Remove emphasis markers that might be left
  text = text.replace(/\*\*/g, "");
  text = text.replace(/__/g, "");
  text = text.replace(/\*/g, "");
  text = text.replace(/_/g, "");
  // Clean up extra whitespace and newlines
  text = text.replace(/\n\s*\n\s*\n/g, "\n\n"); // Multiple newlines to double newlines
  text = text.replace(/[ \t]+/g, " "); // Multiple spaces to single space
  text = text.trim();
  return text;
};
// Single MathJax configuration - will be reused
const mathJaxConfig = {
  tex: {
    inlineMath: [
      ["$", "$"],
      ["\\(", "\\)"],
    ],
    displayMath: [
      ["$$", "$$"],
      ["\\[", "\\]"],
    ],
    processEscapes: true,
    processEnvironments: true,
    packages: ["base", "ams", "mhchem"],
  },
  options: {
    enableMenu: false, // Disable context menu to reduce memory overhead
  },
  startup: {
    typeset: false, // We'll manually control typesetting
  },
};
// Custom MathJax Renderer component that properly manages memory
const MathJaxRenderer = React.memo(
  ({ children, className = "", style = {} }) => {
    const elementRef = useRef(null);
    const [isRendered, setIsRendered] = useState(false);

    const renderIdRef = useRef(0);
    useEffect(() => {
      if (!elementRef.current || !window.MathJax) return;
      const currentRenderId = ++renderIdRef.current;
      const element = elementRef.current;
      // Clear any existing MathJax content
      if (window.MathJax && window.MathJax.typesetClear) {
        try {
          window.MathJax.typesetClear([element]);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
      // Set the content
      element.innerHTML = children || "";
      // Render MathJax content
      if (window.MathJax && window.MathJax.typesetPromise) {
        window.MathJax.typesetPromise([element])
          .then(() => {
            // Only update state if this is still the current render
            if (currentRenderId === renderIdRef.current) {
              setIsRendered(true);
            }
          })
          .catch((error) => {
            console.warn("MathJax rendering error:", error);
            if (currentRenderId === renderIdRef.current) {
              setIsRendered(true); // Still show content even if MathJax fails
            }
          });
      } else {
        setIsRendered(true);
      }
      // Cleanup function
      return () => {
        if (window.MathJax && window.MathJax.typesetClear && element) {
          try {
            window.MathJax.typesetClear([element]);
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      };
    }, [children]);
    return (
      <div
        ref={elementRef}
        className={className}
        style={{
          ...style,
          opacity: isRendered ? 1 : 0.7,
          transition: "opacity 0.2s ease-in-out",
        }}
      />
    );
  }
);
MathJaxRenderer.displayName = "MathJaxRenderer";
// Three Dots Loader Component
const ThreeDotsLoader = ({ message = "Processing..." }) => {
  return (
    <div className="flex justify-start">
      <div className="p-3 rounded-xl max-w-[90%] sm:max-w-[80%] bg-gray-100 text-gray-800 self-start">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex space-x-1">
            <div
              className="bg-indigo-500 rounded-full animate-pulse-grow"
              style={{
                animationDelay: "0ms",
                animationDuration: "1.2s",
                width: "8px",
                height: "8px",
              }}
            ></div>
            <div
              className="bg-indigo-500 rounded-full animate-pulse-grow"
              style={{
                animationDelay: "400ms",
                animationDuration: "1.2s",
                width: "8px",
                height: "8px",
              }}
            ></div>
            <div
              className="bg-indigo-500 rounded-full animate-pulse-grow"
              style={{
                animationDelay: "800ms",
                animationDuration: "1.2s",
                width: "8px",
                height: "8px",
              }}
            ></div>
          </div>
        </div>
        <div className="text-sm text-gray-600 italic">{message}</div>
      </div>
    </div>
  );
};
// Simple Three Dots Loader - left aligned with no bubble/background
// const SimpleThreeDotsLoader = ({ message = "Processing..." }) => {
//   return (
//     <div className="flex justify-start">
//       <div className="py-2 self-start">
//         <div className="flex flex-col items-center">
//           <div className="flex justify-center space-x-2 mb-1">
//             <div
//               className="bg-indigo-500 rounded-full animate-pulse-grow"
//               style={{
//                 animationDelay: '0ms',
//                 animationDuration: '1.2s',
//                 width: '12px',
//                 height: '12px'
//               }}
//             />
//             <div
//               className="bg-indigo-500 rounded-full animate-pulse-grow"
//               style={{
//                 animationDelay: '400ms',
//                 animationDuration: '1.2s',
//                 width: '12px',
//                 height: '12px'
//               }}
//             />
//             <div
//               className="bg-indigo-500 rounded-full animate-pulse-grow"
//               style={{
//                 animationDelay: '800ms',
//                 animationDuration: '1.2s',
//                 width: '12px',
//                 height: '12px'
//               }}
//             />
//           </div>
//           <div className="text-sm text-gray-600 italic text-center">{message}</div>
//         </div>
//       </div>
//     </div>
//   );
// };
// --- LiveInteraction class-based component for live camera/voice interaction ---
// Original Camera Modal Component (unchanged)
const CameraModal = ({
  isOpen,
  onClose,
  onCapture,
  hideCapture = false,
  silenceTimerActive = false,
  silenceTimerCount = 5,
  triggerCapture = false,
  onAutoCapture = null,
  handleSend,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modalRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 24, y: 24 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [touchOffset, setTouchOffset] = useState({ x: 0, y: 0 });
  // Ensure first-attempt playback succeeds by waiting for metadata and retrying
  const waitForMetadata = (video) =>
    new Promise((resolve) => {
      if (!video) return resolve();
      if (video.readyState >= 1) return resolve();
      const onLoaded = () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        resolve();
      };
      video.addEventListener("loadedmetadata", onLoaded);
    });
  const tryPlayVideo = async (video, attempts = 3) => {
    for (let i = 0; i < attempts; i++) {
      try {
        await waitForMetadata(video);
        const playResult = video.play();
        if (playResult && typeof playResult.then === "function") {
          await playResult;
        }
        return true;
      } catch (e) {
        await new Promise((r) => setTimeout(r, 150 * (i + 1)));
      }
    }
    return false;
  };
  // Start/stop camera when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [isOpen]);
  // Reattach video stream when maximizing (video element remounts)
  useEffect(() => {
    if (!isMinimized && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      tryPlayVideo(videoRef.current, 3).then((ok) => {
        if (!ok) {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | handleMaximizeVideoPlayback | L346`,
            new Error("Video playback failed after maximize retries"),
            {
              isMinimized,
              streamActive: Boolean(stream),
              videoReadyState: videoRef.current?.readyState ?? null,
            }
          );
          setError("Failed to display camera. Please try again.");
        }
      });
    }
  }, [isMinimized, stream]);

  // Trigger capture when triggerCapture prop changes
  useEffect(() => {
    if (triggerCapture && stream && videoRef.current) {
      console.log(
        "[Silence Timer] CameraModal received trigger, capturing photo"
      );
      capturePhoto();
    }
  }, [triggerCapture, stream]);

  // Periodic health check for media stream
  useEffect(() => {
    if (!isOpen || !stream) return;
    const healthCheck = setInterval(() => {
      if (stream) {
        const tracks = stream.getTracks();
        const hasActiveTracks = tracks.some(
          (track) => track.readyState === "live"
        );
        if (!hasActiveTracks) {
          console.log("Media stream tracks are not live, restarting camera");
          stopCamera();
          setTimeout(() => startCamera(), 1000);
        }
      }
    }, 10000); // Check every 10 seconds
    return () => clearInterval(healthCheck);
  }, [isOpen, stream]);
  const startCamera = async () => {
    const tryGetStream = async (constraints) => {
      return navigator.mediaDevices.getUserMedia(constraints);
    };
    try {
      let mediaStream = null;
      try {
        mediaStream = await tryGetStream({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (e1) {
        try {
          mediaStream = await tryGetStream({
            video: { facingMode: { ideal: "user" } },
            audio: false,
          });
        } catch (e2) {
          mediaStream = await tryGetStream({ video: true, audio: false });
        }
      }
      mediaStream.onended = () => {
        console.log("Media stream ended unexpectedly");
        setError(
          "Camera stream disconnected. Please try again or check your camera settings."
        );
        setStream(null);
      };
      mediaStream.onerror = (error) => {
        captureSentryError(
          `${DOUBT_SOLVER_FILE} | startCamera::mediaStream.onerror | L389`,
          error,
          {
            isOpen,
            isMinimized,
            facingMode: "environment",
            videoReadyState: videoRef.current?.readyState ?? null,
          }
        );
        setError(
          "Camera stream error. Please try again or ensure your camera is not blocked by browser settings."
        );
        setStream(null);
      };
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        const ok = await tryPlayVideo(videoRef.current, 3);
        if (!ok) {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | startCamera::initialPlayback | L399`,
            new Error(
              "Video playback failed after retries while starting camera"
            ),
            {
              isOpen,
              isMinimized,
              readyState: videoRef.current?.readyState ?? null,
            }
          );
          setError("Failed to display camera. Please try again.");
        } else {
          setError(null);
        }
      }
      setError(null);
    } catch (err) {
      captureSentryError(`${DOUBT_SOLVER_FILE} | startCamera | L406`, err, {
        constraints: { facingMode: "environment", width: 1280, height: 720 },
        isOpen,
        isMinimized,
      });
      if (err.name === "NotAllowedError") {
        setError(
          "Camera access denied. Please allow camera permissions in your browser settings."
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "Camera not found. Please check if a camera is connected to your device."
        );
      } else if (err.name === "NotReadableError") {
        setError(
          "Camera is in use by another application. Please close other apps using the camera."
        );
      } else {
        setError(
          `Camera error: ${err.message}. Please try again or check your browser's camera permissions.`
        );
      }
    }
  };
  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    // Do not mirror on capture — save in natural orientation
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          // Use auto-capture handler if this is triggered by timer
          if (triggerCapture && onAutoCapture) {
            console.log(
              "[Silence Timer] Using auto-capture handler for file:",
              file.name
            );
            onAutoCapture(file);
          } else {
            console.log(
              "[Silence Timer] Using regular capture handler for file:",
              file.name
            );
            onCapture(file);
          }
          onClose();
        }
      },
      "image/jpeg",
      0.9
    );
    // Reset transform (no-op here since we used identity above)
    context.setTransform(1, 0, 0, 1, 0, 0);
  };
  const handleMouseDown = (e) => {
    if (e.target.closest(".modal-header")) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      e.preventDefault();
    }
  };
  const handleTouchStart = (e) => {
    if (e.target.closest(".modal-header")) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      const touch = e.touches[0];
      setTouchOffset({
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      });
      e.preventDefault();
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - (isMinimized ? 200 : 340);
      const maxY = window.innerHeight - (isMinimized ? 60 : 300);
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    }
  };
  const handleTouchMove = (e) => {
    if (isDragging) {
      const touch = e.touches[0];
      const newX = touch.clientX - touchOffset.x;
      const newY = touch.clientY - touchOffset.y;
      const maxX = window.innerWidth - (isMinimized ? 200 : 340);
      const maxY = window.innerHeight - (isMinimized ? 60 : 300);
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
      e.preventDefault();
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleTouchEnd = () => {
    setIsDragging(false);
  };
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset, touchOffset]);
  if (!isOpen) return null;
  const isMobileViewport =
    typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const modalContent = (
    <div
      data-camera-modal
      ref={modalRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        position: "fixed",
        top: position.y,
        left: position.x,
        width: isMinimized ? 200 : "90vw",
        maxWidth: 340,
        height: isMinimized ? 60 : "auto",
        // Ensure the camera modal always stays on top, especially on mobile
        zIndex: 2147483647,
        background: "white",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        cursor: isDragging ? "grabbing" : "default",
        transition: isDragging ? "none" : "all 0.3s ease",
      }}
    >
      <div
        className="modal-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          backgroundColor: "#f8fafc",
          borderBottom: isMinimized ? "none" : "1px solid #e5e7eb",
          cursor: "grab",
          userSelect: "none",
        }}
      >
        <h3
          className="text-sm font-semibold"
          style={{
            margin: 0,
            color: "#374151",
            fontSize: isMinimized ? "12px" : "14px",
          }}
        >
          📹 Interactive Learning
        </h3>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={toggleMinimize}
            style={{
              background: "none",
              border: "none",
              fontSize: "14px",
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
            }}
            aria-label={isMinimized ? "Maximize" : "Minimize"}
            title={isMinimized ? "Maximize" : "Minimize"}
          >
            {isMinimized ? "🔄" : "➖"}
          </button>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "16px",
              cursor: "pointer",
              color: "#ef4444",
              padding: "4px",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
            }}
            aria-label="Close camera"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>
      {!isMinimized && (
        <div style={{ padding: "16px" }}>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-500 mb-4" style={{ fontSize: "14px" }}>
                {error}
              </p>
              <button
                onClick={startCamera}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                style={{ fontSize: "14px" }}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="text-center w-full">
              <video
                data-role="live-camera"
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-60 rounded-lg mb-4"
                style={{
                  maxHeight: "200px",
                  transform: "scaleX(-1)",
                  transformOrigin: "center",
                }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {/* Silence Timer UI */}
              {silenceTimerActive && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0, 0, 0, 0.8)",
                    color: "white",
                    padding: "16px 24px",
                    borderRadius: "12px",
                    fontSize: "18px",
                    fontWeight: "bold",
                    zIndex: 10,
                    textAlign: "center",
                    minWidth: "120px",
                  }}
                >
                  <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                    No speech detected
                  </div>
                  <div style={{ fontSize: "24px", color: "#fbbf24" }}>
                    {silenceTimerCount}
                  </div>
                  <div
                    style={{ fontSize: "12px", marginTop: "4px", opacity: 0.8 }}
                  >
                    Auto-capturing in...
                  </div>
                </div>
              )}

              {stream && !hideCapture && (
                <button
                  data-capture-button
                  onClick={capturePhoto}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  style={{ fontSize: "14px", marginTop: "8px" }}
                >
                  📸 Capture Photo
                </button>
              )}
              {stream && hideCapture && (
                <p style={{ fontSize: "14px", color: "#374151" }}>
                  Live camera mode active. Point at your problem to get help.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
  if (isMobileViewport || typeof document === "undefined") {
    return modalContent;
  }
  return createPortal(modalContent, document.body);
};
export const DoubtSolver = ({
  disableWorkbookMode = false,
  prefilledTextProp = "",
  autoSendPrefilledText = true,
}) => {
  const { collapsed, toggleSidebar } = useSidebar();
  const { userData } = useContext(UserContext);
  const syllabusContext = useContext(SyllabusContext);
  const { subjects, getChaptersForSubject: getChaptersFromContext } =
    syllabusContext || {};
  const location = useLocation();
  const navigate = useNavigate();
  const workbookParamAppliedRef = useRef(false);
  const workbookParamDismissedRef = useRef(false);
  const teachMePromptSentRef = useRef(false);
  const teachMePendingPromptRef = useRef("");
  const teachMeAutoSolveTriggeredRef = useRef(false);
  const workbookPrefillTrackerRef = useRef({
    signature: "",
    boardApplied: false,
    subjectApplied: false,
    chapterApplied: false,
    docApplied: false,
  });
  const fetchPdfFromIdRef = useRef(null);
  const lastLocationKeyRef = useRef(location.key);
  const { messages: initialMessages, selectedSubject: initialSubject } =
    location.state || {};
  const [workbookQueryParams, setWorkbookQueryParams] = useState(() => ({
    enableWorkbookMode: false,
    tab: "",
    from: "",
    subject: "",
    chapter: "",
    board: "",
    docId: "",
  }));
  const {
    enableWorkbookMode: queryEnableWorkbookMode,
    tab: queryWorkbookTab,
    from: queryWorkbookFrom,
    subject: queryWorkbookSubject,
    chapter: queryWorkbookChapter,
    board: queryWorkbookBoard,
    docId: queryWorkbookDocId,
  } = workbookQueryParams;
  const normalizedWorkbookFrom = (queryWorkbookFrom || "").trim().toLowerCase();
  const isTeachMeWorkbookSource = normalizedWorkbookFrom === "teachme";
  const showSolutionModeButtons = !isTeachMeWorkbookSource;
  const isPracticeTutorRoute = location.pathname.startsWith("/practice");
  const resetTeachMeState = useCallback(() => {
    teachMePromptSentRef.current = false;
    teachMePendingPromptRef.current = "";
    teachMeAutoSolveTriggeredRef.current = false;
  }, []);
  const handleWorkbookCloseNavigation = useCallback(() => {
    // if (isTeachMeWorkbookSource) {
    //   navigate(
    //     isPracticeTutorRoute ? "/practice/teachMeTopic" : "/tutor/teachMeTopic"
    //   );
    //   return;
    // }
    window.history.back();
  }, [isTeachMeWorkbookSource, isPracticeTutorRoute, navigate]);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [currentWorkbookTab, setCurrentWorkbookTab] =
    useState("schoolWorkbook");
  const [workbookPdfSourceTab, setWorkbookPdfSourceTab] = useState(null);

  useEffect(() => {
    console.log("DoubtSolver currentWorkbookTab:", currentWorkbookTab);
  }, [currentWorkbookTab]);

  const { isWorkbookMode: workbookMode, setIsWorkbookMode: setWorkbookMode } =
    useTeachingMode();
  const {
    clearChatTrigger,
    resetClearChatTrigger,
    setChatActive,
    addChatToHistory,
    selectedHistoryEntry,
    clearSelectedHistoryEntry,
  } = useClearChat();
  const [messages, setMessages] = useState(initialMessages || []);
  const [messageReactions, setMessageReactions] = useState({});
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");

  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);

  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  const getMessagePreviewForHistory = useCallback((message) => {
    if (!message) {
      return "";
    }

    const candidateTexts = [
      message.text,
      message.plainText,
      message.content,
      message.summary,
      message.transcript,
    ].filter((value) => typeof value === "string" && value.trim().length > 0);

    if (candidateTexts.length > 0) {
      return candidateTexts[0].replace(/\s+/g, " ").trim();
    }

    if (message.image || message.imageUrl) {
      return "Image shared";
    }

    if (
      message.file ||
      (Array.isArray(message.files) && message.files.length > 0) ||
      message.document
    ) {
      return "File shared";
    }

    if (message.voice || message.audioUrl || message.audio) {
      return "Voice message";
    }

    return "";
  }, []);

  const formatChatHistoryForAPI = useCallback((messages) => {
    return messages.map((msg) => {
      let timestamp = msg.timestamp || Date.now();
      if (typeof timestamp === "string") {
        timestamp = new Date(timestamp).getTime();
      }
      return {
        role: msg.type === "user" ? "user" : "assistant",
        content: msg.text,
        timestamp: timestamp,
        ...(msg.image && {
          hasImage: true,
          imageName: msg.imageName || "uploaded_image",
          imageContext: "User uploaded an image with this message",
        }),
        ...(msg.fileName && {
          hasFile: true,
          fileName: msg.fileName,
          fileType: msg.fileType || "unknown",
          fileContext: "User uploaded a document with this message",
        }),
        ...(msg.isVoiceInput && {
          hasVoice: true,
          voiceContext: "User provided voice input for this message",
        }),
        ...(msg.isLiveCamera && {
          hasLiveCamera: true,
          liveCameraContext:
            "User used live camera assistance for this message",
        }),
        ...(msg.ocrExtractedText &&
          msg.ocrSuccess && {
            hasOCR: true,
            ocrExtractedText: msg.ocrExtractedText,
            ocrConfidence: msg.ocrConfidence,
            ocrSuccess: msg.ocrSuccess,
            ocrContext: "OCR extracted text from uploaded image/document",
          }),
      };
    });
  }, []);

  const getRecentChatHistory = useCallback(
    (allMessages, maxMessages = 15) => {
      const recentMessages = allMessages.slice(-maxMessages);
      const formattedHistory = formatChatHistoryForAPI(recentMessages);
      if (allMessages.length > maxMessages) {
        return {
          recentHistory: formattedHistory,
          totalMessages: allMessages.length,
        };
      }
      return {
        recentHistory: formattedHistory,
        totalMessages: allMessages.length,
      };
    },
    [formatChatHistoryForAPI]
  );

  const buildHistoryEntryFromConversation = useCallback(
    (conversationMessages) => {
      if (
        !Array.isArray(conversationMessages) ||
        conversationMessages.length === 0
      ) {
        return null;
      }

      const firstMeaningfulUserMessage = conversationMessages.find(
        (msg) =>
          msg?.type === "user" && getMessagePreviewForHistory(msg).length > 0
      );

      const lastMeaningfulMessage = [...conversationMessages]
        .reverse()
        .find((msg) => getMessagePreviewForHistory(msg).length > 0);

      const previewText =
        getMessagePreviewForHistory(lastMeaningfulMessage) ||
        (lastMeaningfulMessage && lastMeaningfulMessage.image
          ? "Image shared"
          : "");

      const rawTitle =
        getMessagePreviewForHistory(firstMeaningfulUserMessage) ||
        previewText ||
        "Untitled chat";

      const normalizedTitle =
        rawTitle.length > 60 ? `${rawTitle.slice(0, 57)}...` : rawTitle;

      const normalizedPreview =
        previewText.length > 80
          ? `${previewText.slice(0, 77)}...`
          : previewText;

      const lastMessage =
        conversationMessages[conversationMessages.length - 1] || null;

      const updatedAt = lastMessage?.timestamp ?? Date.now();

      const { recentHistory } = getRecentChatHistory(conversationMessages);

      return {
        title: normalizedTitle,
        preview: normalizedPreview,
        updatedAt,
        messageCount: conversationMessages.length,
        recentHistory,
      };
    },
    [getMessagePreviewForHistory, getRecentChatHistory]
  );

  useEffect(() => {
    return () => {
      if (
        Array.isArray(messagesRef.current) &&
        messagesRef.current.length > 0
      ) {
        const historyEntry = buildHistoryEntryFromConversation(
          messagesRef.current
        );
        if (historyEntry) {
          addChatToHistory(historyEntry);
        }
      }
    };
  }, [addChatToHistory, buildHistoryEntryFromConversation]);

  // Toggle like/dislike functionality
  const toggleMessageReaction = (messageId, reaction) => {
    setMessageReactions((prev) => {
      const current = prev[messageId] || { liked: false, disliked: false };

      if (reaction === "like") {
        if (current.liked) {
          // If already liked, remove like
          return { ...prev, [messageId]: { ...current, liked: false } };
        } else {
          // If not liked, add like and remove dislike
          return { ...prev, [messageId]: { liked: true, disliked: false } };
        }
      } else if (reaction === "dislike") {
        if (current.disliked) {
          // If already disliked, remove dislike
          return { ...prev, [messageId]: { ...current, disliked: false } };
        } else {
          // If not disliked, add dislike and remove like
          return { ...prev, [messageId]: { liked: false, disliked: true } };
        }
      }

      return prev;
    });
  };

  // Listen for clear chat trigger from context
  useEffect(() => {
    if (clearChatTrigger > 0) {
      handleClearChat();
      resetClearChatTrigger();
    }
  }, [clearChatTrigger, resetClearChatTrigger]);

  // Set chat as active when messages are present
  useEffect(() => {
    if (messages && messages.length > 0) {
      setChatActive(true);
    } else {
      setChatActive(false);
    }
  }, [messages, setChatActive]);
  const activeRequestControllerRef = useRef(null);
  const cancelActiveRequest = useCallback(() => {
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
      activeRequestControllerRef.current = null;
    }
  }, []);
  const beginNewRequest = useCallback(() => {
    cancelActiveRequest();
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    return controller;
  }, [cancelActiveRequest]);
  const clearActiveRequest = useCallback((controller) => {
    if (activeRequestControllerRef.current === controller) {
      activeRequestControllerRef.current = null;
    }
  }, []);
  // Treat both fetch and axios cancellations as aborts
  const isAbortError = (error) => {
    if (!error) return false;
    return (
      error.name === "AbortError" ||
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED"
    );
  };
  const [inputText, setInputText] = useState("");
  const [showIntro, setShowIntro] = useState(true);
  const [file, setFile] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [voice, setVoice] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [activeButton, setActiveButton] = useState("homework");
  const [fromQuestions, setFromQuestions] = useState(false);
  const [showCameraOptions, setShowCameraOptions] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showLiveCameraModal, setShowLiveCameraModal] = useState(false);
  const [isMobileCameraActive, setIsMobileCameraActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [communicationMode, setCommunicationMode] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isSystemSpeaking, setIsSystemSpeaking] = useState(false);
  const [userModulationOpacities, setUserModulationOpacities] = useState([
    0.5, 0.8, 0.5,
  ]);
  const [systemModulationOpacities, setSystemModulationOpacities] = useState([
    0.5, 0.8, 0.5,
  ]);
  const [isVoicePaused, setIsVoicePaused] = useState(false);
  const [isSystemAudioPaused, setIsSystemAudioPaused] = useState(true);
  const [pendingPrefilledText, setPendingPrefilledTextState] = useState(
    () => prefilledTextProp || ""
  );
  const pendingPrefilledTextRef = useRef(prefilledTextProp || "");
  const updatePendingPrefilledText = useCallback((value) => {
    pendingPrefilledTextRef.current = value;
    setPendingPrefilledTextState(value);
  }, []);
  useEffect(() => {
    updatePendingPrefilledText(prefilledTextProp || "");
  }, [prefilledTextProp, updatePendingPrefilledText]);
  const getPrefilledTextForSend = useCallback(() => {
    if (autoSendPrefilledText) {
      return "";
    }
    return (pendingPrefilledTextRef.current || "").trim();
  }, [autoSendPrefilledText]);
  const normalizedPrefilledText = (pendingPrefilledText || "").trim();
  const showPrefilledTextPreview = Boolean(
    !autoSendPrefilledText && normalizedPrefilledText
  );

  const getModelApiErrorMessage = (
    error,
    fallback = "Failed to process the input. Please try again."
  ) => {
    if (!error) {
      return fallback;
    }
    const name = error?.name;
    if (
      name === "AbortError" ||
      name === "CanceledError" ||
      name === "TimeoutError" ||
      error?.code === "ERR_CANCELED"
    ) {
      return "The request timed out. Please try again.";
    }
    return fallback;
  };

  const resetVoiceStateAfterModelFailure = useCallback(() => {
    setIsSystemSpeaking(false);
    if (communicationMode) {
      setIsUserSpeaking(false);
      setIsVoicePaused(true);
    }
  }, [communicationMode]);

  const handleModelApiError = (error, context, extra = {}) => {
    const { fallbackMessage, skipToast, ...rest } = extra || {};
    const message = getModelApiErrorMessage(error, fallbackMessage);
    captureSentryError(`${DOUBT_SOLVER_FILE} | ${context}`, error, {
      ...rest,
      communicationMode: Boolean(communicationMode),
      errorMessage: message,
    });
    console.error(`[Model API] ${context} failed:`, error);
    if (!skipToast) {
      toast.error(message);
    }
    return message;
  };
  // Intro hero used in default chat view (non-workbook)
  const introHero = (
    <div
      className={`flex flex-col sm:flex-row items-center gap-6 sm:gap-12 mb-6 flex-shrink-0 ${
        workbookMode ? "h-70/100" : ""
      }`}
    >
      <img
        src="/images/tutor.webp"
        alt="Aanya"
        className="w-24 h-28 sm:w-30 sm:h-34 object-cover object-top rounded-2xl"
      />
      <div className="pt-0 sm:pt-12 text-center sm:text-left">
        <h1 className="text-2xl sm:text-4xl font-semibold text-gray-950 leading-tight">
          Hello, {userData?.name?.trim()?.split(/\s+/)[0] || "Student"}!
          <br />
          How can I help you ?
        </h1>
      </div>
    </div>
  );

  // My Uploads Functions
  // {
  //     "id": 2,
  //     "fileName": "Worksheet_14f24cae.pdf",
  //     "overallMarks": 4,
  //     "subject": "Mathematics",
  //     "totalMarks": 5,
  //     "date": "2025-10-25T13:56:11.705Z",
  //     "questions": [
  //         {
  //             "id": 1,
  //             "question_text": "1. Simplify $-\\frac{4}{5} - \\frac{3}{15} + \\frac{7}{20}$",
  //             "answer_text": "$-\\frac{4}{5} - \\frac{3}{15} + \\frac{7}{20}$ \n $\\frac{-48 - 12 + 21}{60}$ \n $ = \\frac{-39}{60}$",
  //             "marks": 4,
  //             "totalMarks": 5,
  //             "feedback": "Excellent work on finding the common denominator and performing the arithmetic operations correctly! You've shown a strong understanding of combining fractions. The only area for improvement is to always simplify your final answer to its lowest terms. Keep up the great effort!",
  //             "stepBreakdown": [
  //                 {
  //                     "step": 1,
  //                     "feedback": "The student correctly identified the least common multiple (LCM) of the denominators (5, 15, 20) as 60. They then accurately converted all three fractions to equivalent fractions with a denominator of 60. This step is perfectly executed.",
  //                     "marks": 2,
  //                     "maxMarks": 2
  //                 },
  //                 {
  //                     "step": 2,
  //                     "feedback": "The student correctly performed the addition and subtraction of the numerators: $-48 - 12 + 21 = -60 + 21 = -39$. This calculation is accurate.",
  //                     "marks": 2,
  //                     "maxMarks": 2
  //                 },
  //                 {
  //                     "step": 3,
  //                     "feedback": "The final answer, $\\frac{-39}{60}$, is numerically correct. However, it is not in its simplest form. Both the numerator and the denominator are divisible by 3 ($-39 \\div 3 = -13$ and $60 \\div 3 = 20$). The simplified answer should be $-\\frac{13}{20}$. Always remember to simplify fractions to their lowest terms unless otherwise specified.",
  //                     "marks": 0,
  //                     "maxMarks": 1
  //                 }
  //             ]
  //         }
  //     ],
  //     "status": "completed",
  //     "viewed": true,
  //     "requestId": "14f24cae-a435-44fe-9373-27804ea1c863"
  // }
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [filteredUploads, setFilteredUploads] = useState([]);
  const [fetchingPDF, setFetchingPDF] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUploadsCount, setTotalUploadsCount] = useState(0);
  const [uploadFileLimit, setUploadFileLimit] = useState(10);
  const [uploadFilter, setUploadFilter] = useState({
    subject: "",
    year: "",
  });
  const [uploadingUserWorksheet, setUploadingUserWorksheet] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingUploads, setIsLoadingUploads] = useState(false);

  // Sample Papers state
  const [samplePapersFiles, setSamplePapersFiles] = useState([]);
  const [filteredSamplePapers, setFilteredSamplePapers] = useState([]);
  const [samplePapersTotalPages, setSamplePapersTotalPages] = useState(1);
  const [samplePapersTotalCount, setSamplePapersTotalCount] = useState(0);
  const [samplePapersPage, setSamplePapersPage] = useState(1);
  const [isLoadingSamplePapers, setIsLoadingSamplePapers] = useState(false);
  const [samplePapersFilter, setSamplePapersFilter] = useState({
    subject: "",
  });

  // PYQ state
  const [pyqFiles, setPyqFiles] = useState([]);
  const [filteredPyqFiles, setFilteredPyqFiles] = useState([]);
  const [pyqTotalPages, setPyqTotalPages] = useState(1);
  const [pyqTotalCount, setPyqTotalCount] = useState(0);
  const [pyqPage, setPyqPage] = useState(1);
  const [isLoadingPyq, setIsLoadingPyq] = useState(false);
  const [pyqFilter, setPyqFilter] = useState({
    subject: "",
    year: "",
  });

  const formatDateTime = (dateString) => {
    if (!dateString) return "Date not available";
    try {
      // Parse the date string (API already includes timezone info)
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        // Show time if within 24 hours (local timezone)
        return date.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } else if (diffInHours < 24 * 7) {
        // Show date and day with time if within a week (local timezone)
        const dateStr = date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        const dayTimeStr = date.toLocaleDateString(undefined, {
          weekday: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        return `${dateStr}, ${dayTimeStr}`;
      } else {
        // Show full date and time for older notes (local timezone)
        return date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      }
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date not available";
    }
  };

  const fetchUserUploadedWorkbooks = useCallback(
    async (page = 1) => {
      setIsLoadingUploads(true);
      if (!baseUrl) {
        console.error(
          "VITE_APP_BASE_URL is not configured. Cannot load user workbooks."
        );
        setIsLoadingUploads(false);
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const params = new URLSearchParams({
        page: String(Math.max(1, Math.floor(page) || 1)),
        limit: String(Math.max(1, uploadFileLimit || 10)),
      });

      if (uploadFilter.subject) {
        params.append("subject", uploadFilter.subject);
      }
      if (uploadFilter.year) {
        params.append("year", uploadFilter.year);
      }

      try {
        const response = await fetch(
          `${trimmedBaseUrl}/v2/workbook/?${params.toString()}`,
          {
            method: "GET",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Workbook fetch failed (${response.status}): ${errorText}`
          );
        }

        const result = await response.json();
        const items = Array.isArray(result?.items) ? result.items : [];

        const normalizeNumber = (value) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const totalCandidates = [
          result?.totalCount,
          result?.totalItems,
          result?.totalRecords,
          result?.count,
          result?.total,
        ]
          .map((candidate) => normalizeNumber(candidate))
          .filter((candidate) => candidate !== null && candidate >= 0);

        const totalRecords =
          totalCandidates.length > 0
            ? Math.max(0, Math.floor(totalCandidates[0]))
            : items.length;

        const pageCountFromRecords =
          totalRecords > 0
            ? Math.floor(
                (totalRecords + (uploadFileLimit || 10) - 1) /
                  (uploadFileLimit || 10)
              )
            : items.length > 0
            ? Math.floor(
                (items.length + (uploadFileLimit || 10) - 1) /
                  (uploadFileLimit || 10)
              )
            : 0;

        const totalPagesCandidate = normalizeNumber(result?.totalPages);
        const resolvedTotalPages =
          totalPagesCandidate && totalPagesCandidate > 0
            ? Math.floor(totalPagesCandidate)
            : Math.max(1, pageCountFromRecords || 1);

        const pageCandidate = normalizeNumber(result?.page);
        const resolvedPage =
          pageCandidate && pageCandidate > 0
            ? Math.floor(pageCandidate)
            : Math.max(1, Math.floor(page) || 1);
        const safeTotalPages = Math.max(1, resolvedTotalPages || 1);
        const safeResolvedPage = Math.min(
          Math.max(resolvedPage, 1),
          safeTotalPages
        );
        setUploadedFiles(items);
        setFilteredUploads(items);
        setTotalUploadsCount(totalRecords > 0 ? totalRecords : items.length);
        setTotalPages(safeTotalPages);
        setCurrentPage((prev) =>
          prev === safeResolvedPage ? prev : safeResolvedPage
        );
      } catch (error) {
        console.error("Error fetching user uploads:", error);
        toast.error("Failed to load your uploads. Please try again.");
      } finally {
        setIsLoadingUploads(false);
      }
    },
    [baseUrl, uploadFilter.year, uploadFilter.subject, uploadFileLimit]
  );

  // Fetch PYQ
  const fetchPyq = useCallback(
    async (page = 1) => {
      setIsLoadingPyq(true);
      if (!baseUrl) {
        console.error("VITE_APP_BASE_URL is not configured. Cannot load PYQ.");
        setIsLoadingPyq(false);
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const params = new URLSearchParams({
        page: String(Math.max(1, Math.floor(page) || 1)),
        limit: String(Math.max(1, uploadFileLimit || 20)),
        type: "PYQ",
      });

      // Get class from userData, default to 10
      const userClass = userData?.grade || userData?.class || 10;
      params.append("class", String(userClass));

      if (pyqFilter.subject) {
        params.append("subject", pyqFilter.subject);
      }
      if (pyqFilter.year) {
        params.append("year", pyqFilter.year);
      }

      try {
        const response = await fetch(
          `${trimmedBaseUrl}/v2/pyq-sample/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `PYQ fetch failed (${response.status}): ${errorText}`
          );
        }

        const result = await response.json();
        const items = Array.isArray(result?.items) ? result.items : [];

        const normalizeNumber = (value) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const totalCandidates = [
          result?.totalCount,
          result?.totalItems,
          result?.totalRecords,
          result?.count,
          result?.total,
        ]
          .map((candidate) => normalizeNumber(candidate))
          .filter((candidate) => candidate !== null && candidate >= 0);

        const totalRecords =
          totalCandidates.length > 0
            ? Math.max(0, Math.floor(totalCandidates[0]))
            : items.length;

        const pageCountFromRecords =
          totalRecords > 0
            ? Math.floor(
                (totalRecords + (uploadFileLimit || 20) - 1) /
                  (uploadFileLimit || 20)
              )
            : items.length > 0
            ? Math.floor(
                (items.length + (uploadFileLimit || 20) - 1) /
                  (uploadFileLimit || 20)
              )
            : 0;

        const totalPagesCandidate = normalizeNumber(result?.totalPages);
        const resolvedTotalPages =
          totalPagesCandidate && totalPagesCandidate > 0
            ? Math.floor(totalPagesCandidate)
            : Math.max(1, pageCountFromRecords || 1);

        const safeResolvedPage = Math.max(
          1,
          Math.min(
            Math.floor(normalizeNumber(result?.page) || page || 1),
            resolvedTotalPages
          )
        );

        setPyqFiles(items);
        setFilteredPyqFiles(items);
        setPyqTotalCount(totalRecords > 0 ? totalRecords : items.length);
        setPyqTotalPages(resolvedTotalPages);
        setPyqPage((prev) =>
          prev === safeResolvedPage ? prev : safeResolvedPage
        );
      } catch (error) {
        console.error("Error fetching PYQ:", error);
        toast.error("Failed to load PYQ. Please try again.");
      } finally {
        setIsLoadingPyq(false);
      }
    },
    [baseUrl, pyqFilter.subject, pyqFilter.year, uploadFileLimit, userData]
  );

  // Fetch Sample Papers
  const fetchSamplePapers = useCallback(
    async (page = 1) => {
      setIsLoadingSamplePapers(true);
      if (!baseUrl) {
        console.error(
          "VITE_APP_BASE_URL is not configured. Cannot load sample papers."
        );
        setIsLoadingSamplePapers(false);
        return;
      }

      const trimmedBaseUrl = baseUrl.replace(/\/+$/, "");
      const params = new URLSearchParams({
        page: String(Math.max(1, Math.floor(page) || 1)),
        limit: String(Math.max(1, uploadFileLimit || 20)),
        type: "sample",
      });

      // Get class from userData, default to 10
      const userClass = userData?.grade || userData?.class || 10;
      params.append("class", String(userClass));

      if (samplePapersFilter.subject) {
        params.append("subject", samplePapersFilter.subject);
      }

      try {
        const response = await fetch(
          `${trimmedBaseUrl}/v2/pyq-sample/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Sample papers fetch failed (${response.status}): ${errorText}`
          );
        }

        const result = await response.json();
        const items = Array.isArray(result?.items) ? result.items : [];

        const normalizeNumber = (value) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const totalCandidates = [
          result?.totalCount,
          result?.totalItems,
          result?.totalRecords,
          result?.count,
          result?.total,
        ]
          .map((candidate) => normalizeNumber(candidate))
          .filter((candidate) => candidate !== null && candidate >= 0);

        const totalRecords =
          totalCandidates.length > 0
            ? Math.max(0, Math.floor(totalCandidates[0]))
            : items.length;

        const pageCountFromRecords =
          totalRecords > 0
            ? Math.floor(
                (totalRecords + (uploadFileLimit || 10) - 1) /
                  (uploadFileLimit || 10)
              )
            : items.length > 0
            ? Math.floor(
                (items.length + (uploadFileLimit || 10) - 1) /
                  (uploadFileLimit || 10)
              )
            : 0;

        const totalPagesCandidate = normalizeNumber(result?.totalPages);
        const resolvedTotalPages =
          totalPagesCandidate && totalPagesCandidate > 0
            ? Math.floor(totalPagesCandidate)
            : Math.max(1, pageCountFromRecords || 1);

        const safeResolvedPage = Math.max(
          1,
          Math.min(
            Math.floor(normalizeNumber(result?.page) || page || 1),
            resolvedTotalPages
          )
        );

        setSamplePapersFiles(items);
        setFilteredSamplePapers(items);
        setSamplePapersTotalCount(
          totalRecords > 0 ? totalRecords : items.length
        );
        setSamplePapersTotalPages(resolvedTotalPages);
        setSamplePapersPage((prev) =>
          prev === safeResolvedPage ? prev : safeResolvedPage
        );
      } catch (error) {
        console.error("Error fetching sample papers:", error);
        toast.error("Failed to load sample papers. Please try again.");
      } finally {
        setIsLoadingSamplePapers(false);
      }
    },
    [baseUrl, samplePapersFilter.subject, uploadFileLimit, userData]
  );

  useEffect(() => {
    if (currentWorkbookTab === "pyq") {
      fetchPyq(pyqPage);
    }
  }, [
    pyqPage,
    fetchPyq,
    currentWorkbookTab,
    pyqFilter.subject,
    pyqFilter.year,
  ]);

  useEffect(() => {
    if (currentWorkbookTab === "samplePapers") {
      fetchSamplePapers(samplePapersPage);
    }
  }, [
    samplePapersPage,
    fetchSamplePapers,
    currentWorkbookTab,
    samplePapersFilter.subject,
  ]);

  useEffect(() => {
    fetchUserUploadedWorkbooks(currentPage);
  }, [currentPage, fetchUserUploadedWorkbooks]);
  // Handle workbook chapter change
  const handleWorkBookUploadNew = async () => {
    setUploadingUserWorksheet(true);
    const { file, subject, chapter, goals } = newFile;
    if (!file) return;

    console.log("New file uploaded:", newFile);
    const previousUrl = workbookPdfUrl;
    let uploadRecorded = false;
    let uploadResponseData = null;

    try {
      setIsWorkbookLoading(true);
      await resetWorkbookEvaluationState();
      await closeWorkbookMarkerSelection();
      const headers = new Headers();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("subject", subject || "Uncategorized");
      formData.append("chapter", chapter || "General");
      if (goals && Array.isArray(goals) && goals.length > 0) {
        formData.append("goals", JSON.stringify(goals));
      }
      const uploadResponse = await fetch(`${baseUrl}/v2/workbook/upload`, {
        method: "POST",
        headers,
        body: formData,
        redirect: "follow",
      });
      const uploadResponseText = await uploadResponse.text();
      if (!uploadResponse.ok) {
        throw new Error(
          `Workbook upload failed (${uploadResponse.status}): ${uploadResponseText}`
        );
      }
      try {
        uploadResponseData = JSON.parse(uploadResponseText);
      } catch {
        uploadResponseData = { raw: uploadResponseText };
      }
      console.log("Workbook upload API response:", uploadResponseData);

      fetchPDFfromID(uploadResponseData.data.id, "myUploads");
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | handleWorkBookUploadNew`,
        error,
        {
          fileName: file?.name ?? null,
          fileType: file?.type ?? null,
          previousUrl,
        }
      );
      console.error("Error loading uploaded file:", error);
      toast.error("Failed to load uploaded file");
      if (isMobile) {
        setShowWorkbookControls(true);
      }
    } finally {
      if (!file.type.startsWith("image/")) {
        setIsWorkbookLoading(false);
      }
    }
  };
  // Utility to generate a downloadable PDF file from base64 input
  const generatePDFFromBase64 = async (base64Data) => {
    try {
      if (!base64Data || typeof base64Data !== "string") {
        throw new Error("base64Data must be a non-empty string");
      }

      const normalizedBase64 = base64Data.includes(",")
        ? base64Data.split(",")[1]
        : base64Data;

      const byteCharacters = atob(normalizedBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let index = 0; index < byteCharacters.length; index += 1) {
        byteNumbers[index] = byteCharacters.charCodeAt(index);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const objectUrl = URL.createObjectURL(blob);
      return objectUrl;
    } catch (error) {
      console.error("Error generating PDF from base64:", error);
    }
    return "";
  };

  const fetchPDFfromID = async (pdfID, tabOverride = null) => {
    const targetTab = tabOverride || currentWorkbookTab;
    console.log(
      "DoubtSolver currentWorkbookTab fetchPDFfromID targetTab:",
      targetTab
    );
    try {
      if (targetTab === "schoolWorkbook") {
        setCurrentWorkbookTab("loadingPDF");
        const requestOptions = {
          method: "GET",
          redirect: "follow",
        };

        fetch(`${baseUrl}/v2/school-workbooks/${pdfID}`, requestOptions)
          .then((response) => response.json())
          .then(async (result) => {
            const pdfURL = await generatePDFFromBase64(result.base64_data);
            if (messages && messages.length > 0) {
              handleClearChat(true);
            }
            await resetWorkbookEvaluationState();
            setWorkbookPdfUrl(pdfURL);
            await closeWorkbookMarkerSelection();
            await loadWorkbookPdf(pdfURL, "schoolWorkbook");
          })
          .catch((error) => console.error(error));
      } else if (targetTab === "pyq" || targetTab === "samplePapers") {
        setCurrentWorkbookTab("loadingPDF");
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          redirect: "follow",
        };

        fetch(`${baseUrl}/v2/pyq-sample/${pdfID}`, requestOptions)
          .then((response) => response.json())
          .then(async (result) => {
            const pdfURL = await generatePDFFromBase64(result.base64_data);
            if (messages && messages.length > 0) {
              handleClearChat(true);
            }
            await resetWorkbookEvaluationState();
            setWorkbookPdfUrl(pdfURL);
            await closeWorkbookMarkerSelection();
            await loadWorkbookPdf(pdfURL, targetTab);
            setUploadingUserWorksheet(false);
            await setFetchingPDF(false);
          })
          .catch((error) => {
            console.error("Error fetching PYQ/Sample Paper PDF:", error);
            toast.error("Failed to load PDF. Please try again.");
          });
      } else {
        setCurrentWorkbookTab("loadingPDF");
        const requestOptions = {
          method: "GET",
          redirect: "follow",
        };

        fetch(`${baseUrl}/v2/workbook/${pdfID}`, requestOptions)
          .then((response) => response.json())
          .then(async (result) => {
            const pdfURL = await generatePDFFromBase64(result.base64_data);
            if (messages && messages.length > 0) {
              handleClearChat(true);
            }
            await resetWorkbookEvaluationState();
            setWorkbookPdfUrl(pdfURL);
            await closeWorkbookMarkerSelection();
            await loadWorkbookPdf(pdfURL, targetTab);
            setUploadingUserWorksheet(false);
            await setFetchingPDF(false);
            await setNewFile({
              file: null,
              subject: "",
              chapter: "",
              goals: [],
            });
          })
          .catch((error) => console.error(error));
      }
    } catch (error) {
      console.error("Error fetching PDF from ID:", error);
    }
  };
  fetchPdfFromIdRef.current = fetchPDFfromID;
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const lastAudioUrlRef = useRef(null);
  // Cached pre-capture image from live assistance (taken before final send)
  // Rolling frame cache for live camera captures (<= 5s, 3s interval → max 2)
  const frameCacheRef = useRef([]); // [{ file: File, ts: number }]
  const captureIntervalRef = useRef(null);
  const isCapturingRef = useRef(false);
  const purgeFrameCache = () => {
    const now = Date.now();
    // Drop frames older than 5 seconds
    frameCacheRef.current = frameCacheRef.current.filter(
      (f) => now - f.ts <= 5000
    );
    // Trim to at most 10 frames
    if (frameCacheRef.current.length > 10) {
      frameCacheRef.current.splice(0, frameCacheRef.current.length - 10);
    }
  };
  const captureLiveFrame = () => {
    if (isCapturingRef.current) return;
    const videoElem =
      document.querySelector('video[data-role="live-camera"]') ||
      document.querySelector("video");
    if (!videoElem || !videoElem.videoWidth || !videoElem.videoHeight) return;
    isCapturingRef.current = true;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = videoElem.videoWidth;
      canvas.height = videoElem.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0); // ensure no mirror on capture
      ctx.drawImage(videoElem, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `live-frame-${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            frameCacheRef.current.push({ file, ts: Date.now() });
            purgeFrameCache();
          }
          isCapturingRef.current = false;
        },
        "image/jpeg",
        0.9
      );
    } catch (e) {
      isCapturingRef.current = false;
    }
  };
  // (moved below after state declarations to avoid TDZ issues)
  const pickFrameForSend = (offsetMs = 5000) => {
    purgeFrameCache();
    if (frameCacheRef.current.length === 0) return null;
    const target = Date.now() - offsetMs;
    // Prefer the latest frame at or before target
    let chosen = null;
    for (let i = frameCacheRef.current.length - 1; i >= 0; i--) {
      const f = frameCacheRef.current[i];
      if (f.ts <= target) {
        chosen = f;
        break;
      }
    }
    // Fallback to the oldest available if none older than target
    if (!chosen) {
      chosen = frameCacheRef.current[0];
    }
    return chosen?.file || null;
  };
  // Add refs to track resources for cleanup
  const currentRecognitionRef = useRef(null);
  const skipRecognitionOnEndRef = useRef(false);
  const skipVoiceSendOnEndRef = useRef(null);
  const currentUtteranceRef = useRef(null);
  const audioBlobRef = useRef(null);
  const activeEvaluationSessionRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const wasProcessingRef = useRef(false);
  const messagesContainerRef = useRef(null);
  const inputTextRef = useRef(inputText);
  const imageRef = useRef(image);
  const fileRef = useRef(file);
  const imagePreviewRef = useRef(imagePreview);
  const handleSendRef = useRef(null);
  const awaitingResponseSinceRef = useRef(null);
  const historyLoadTimeoutRef = useRef(null);
  // Track last chat mode to clear chat on mode switches
  const lastChatModeRef = useRef(null);
  const modeInitRef = useRef(false);
  const [liveAssistance, setLiveAssistance] = useState(false);
  const [silenceTimerActive, setSilenceTimerActive] = useState(false);
  const [silenceTimerCount, setSilenceTimerCount] = useState(5);
  const [triggerCameraCapture, setTriggerCameraCapture] = useState(false);
  const [pendingAutoSend, setPendingAutoSend] = useState(false);

  // Handle silence timer countdown and automatic screenshot
  useEffect(() => {
    let timerInterval;
    if (silenceTimerActive && silenceTimerCount > 0) {
      timerInterval = setInterval(() => {
        setSilenceTimerCount((prev) => {
          if (prev <= 1) {
            // Timer finished, capture and send screenshot
            setSilenceTimerActive(false);
            setSilenceTimerCount(5);
            // Trigger automatic screenshot capture
            if (showCameraModal && liveAssistance) {
              console.log(
                "[Silence Timer] Timer finished, triggering camera capture"
              );
              // Trigger camera capture
              setTriggerCameraCapture(true);
            }
            return 5; // Reset to 5
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [silenceTimerActive, silenceTimerCount, showCameraModal, liveAssistance]);

  // Reset trigger after it's been used
  useEffect(() => {
    if (triggerCameraCapture) {
      console.log("[Silence Timer] Trigger received, will reset in 100ms");
      // Add a small delay to ensure CameraModal has time to process the trigger
      setTimeout(() => {
        console.log("[Silence Timer] Resetting trigger to false");
        setTriggerCameraCapture(false);
      }, 100);
    }
  }, [triggerCameraCapture]);

  // Workbook mode states - now using context instead of local state
  const [workbookBoard, setWorkbookBoard] = useState("");
  const [workbookSubject, setWorkbookSubject] = useState("");
  const [workbookChapter, setWorkbookChapter] = useState("");
  const [workbookPdfUrl, setWorkbookPdfUrl] = useState("");
  const [workbookSyllabusData, setWorkbookSyllabusData] = useState([]);
  const [workbookSubjects, setWorkbookSubjects] = useState([]);
  const [workbookChapters, setWorkbookChapters] = useState([]);
  const [isWorkbookLoading, setIsWorkbookLoading] = useState(false);
  const [isSyllabusLoading, setIsSyllabusLoading] = useState(false);
  const [workbookPdfDocument, setWorkbookPdfDocument] = useState(null);
  const [workbookCurrentPage, setWorkbookCurrentPage] = useState(1);
  const [workbookNumPages, setWorkbookNumPages] = useState(0);
  const [workbookPageInput, setWorkbookPageInput] = useState("1");
  const [workbookZoomLevel, setWorkbookZoomLevel] = useState(1.0);
  // Force re-mount of workbook UI (selects/buttons) when entering workbook mode
  const [workbookResetKey, setWorkbookResetKey] = useState(0);
  const [workbookEvaluateResetKey, setWorkbookEvaluateResetKey] = useState(0);
  const [showWorkbookControls, setShowWorkbookControls] = useState(true);
  const [showMobileChatActions, setShowMobileChatActions] = useState(false);
  const handleWorkbookChapterChangeRef = useRef(null);

  // Keep the manual page jump input in sync with the current page
  useEffect(() => {
    setWorkbookPageInput(String(workbookCurrentPage));
  }, [workbookCurrentPage]);

  // Handlers for manual workbook page navigation
  const handleWorkbookPageInputChange = useCallback(
    (event) => {
      setWorkbookPageInput(event.target.value);
    },
    [setWorkbookPageInput]
  );

  const handleWorkbookPageInputCommit = useCallback(() => {
    if (workbookNumPages < 1) {
      setWorkbookPageInput(String(workbookCurrentPage));
      return;
    }

    const trimmedValue = workbookPageInput.trim();

    if (!trimmedValue) {
      setWorkbookPageInput(String(workbookCurrentPage));
      return;
    }

    const nextPage = parseInt(trimmedValue, 10);

    if (Number.isNaN(nextPage) || nextPage < 1 || nextPage > workbookNumPages) {
      setWorkbookPageInput(String(workbookCurrentPage));
      return;
    }

    if (nextPage !== workbookCurrentPage) {
      setWorkbookCurrentPage(nextPage);
    } else if (String(nextPage) !== workbookPageInput) {
      setWorkbookPageInput(String(nextPage));
    }
  }, [
    workbookNumPages,
    workbookPageInput,
    workbookCurrentPage,
    setWorkbookPageInput,
    setWorkbookCurrentPage,
  ]);

  const handleWorkbookPageInputKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === "NumpadEnter") {
        event.preventDefault();
        handleWorkbookPageInputCommit();
      }
    },
    [handleWorkbookPageInputCommit]
  );

  // My Uploads tab states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newFile, setNewFile] = useState({
    file: null,
    subject: "",
    chapter: "",
    goals: [],
  });
  const [goalsDropdownOpen, setGoalsDropdownOpen] = useState(false);
  const [goalsSearchTerm, setGoalsSearchTerm] = useState("");
  const goalsDropdownRef = useRef(null);

  // Close goals dropdown when modal closes
  useEffect(() => {
    if (!showUploadModal) {
      setGoalsDropdownOpen(false);
      setGoalsSearchTerm("");
    }
  }, [showUploadModal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        goalsDropdownRef.current &&
        !goalsDropdownRef.current.contains(event.target)
      ) {
        setGoalsDropdownOpen(false);
      }
    };

    if (goalsDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [goalsDropdownOpen]);

  const itemsPerPage = uploadFileLimit || 10;

  // Helper functions for My Uploads tab - use syllabus context
  const getFilteredChapters = () => {
    if (!getChaptersFromContext) {
      return [];
    }
    return getChaptersFromContext(uploadFilter.subject);
  };

  const getUploadsPaginationSummary = () => {
    if (
      !totalUploadsCount ||
      totalUploadsCount <= 0 ||
      !filteredUploads ||
      filteredUploads.length === 0
    ) {
      return "Showing 0 of 0 files";
    }

    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(
      startIndex + filteredUploads.length - 1,
      totalUploadsCount
    );

    return `Showing ${startIndex} to ${endIndex} of ${totalUploadsCount} files`;
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [uploadFilter]);

  const getSamplePapersPaginationSummary = () => {
    if (
      !samplePapersTotalCount ||
      samplePapersTotalCount <= 0 ||
      !filteredSamplePapers ||
      filteredSamplePapers.length === 0
    ) {
      return "Showing 0 of 0 files";
    }

    const startIndex = (samplePapersPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(
      startIndex + filteredSamplePapers.length - 1,
      samplePapersTotalCount
    );

    return `Showing ${startIndex} to ${endIndex} of ${samplePapersTotalCount} files`;
  };

  // Reset pagination when sample papers filter changes
  useEffect(() => {
    setSamplePapersPage(1);
  }, [samplePapersFilter]);

  const getPyqPaginationSummary = () => {
    if (
      !pyqTotalCount ||
      pyqTotalCount <= 0 ||
      !filteredPyqFiles ||
      filteredPyqFiles.length === 0
    ) {
      return "Showing 0 of 0 files";
    }

    const startIndex = (pyqPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(
      startIndex + filteredPyqFiles.length - 1,
      pyqTotalCount
    );

    return `Showing ${startIndex} to ${endIndex} of ${pyqTotalCount} files`;
  };

  // Reset pagination when PYQ filter changes
  useEffect(() => {
    setPyqPage(1);
  }, [pyqFilter]);

  /*
                            {messages.map((msg, index) =>
                              msg.type === 'bot' ? (
                                <div key={`m-bot-${index}-${msg.timestamp || Date.now()}`} className="flex justify-start">
                                  <div className="p-3 whitespace-pre-line rounded-xl max-w-[90%] sm:max-w-[80%] bg-gray-100 text-gray-800 self-start">
                                    <style>{`
                                      .ai-table-bordered table { border-collapse: collapse; width: 100%; margin-bottom: 1em; }
                                      .ai-table-bordered th, .ai-table-bordered td { border: 1px solid #888; padding: 6px 12px; text-align: left; }
                                      .ai-table-bordered th { background: #f3f4f6; }
                                    `}</style>
                                    <div className="ai-table-bordered">
                                      {(msg.text || '').includes('<table>') ? (
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: applyBasicMarkdown(msg.text || '')
                                              .replace(/(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*|<table>)/g, '<br>$2')
                                          }}
                                        />
                                      ) : (
                                        <MathJaxRenderer className="whitespace-pre-line">
                                          {applyBasicMarkdown(msg.text || '')
                                            .replace(/(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*)/g, '\n$2')
                                            .replace(/<br\s*\/?>/gi, '\n')
                                          }
                                        </MathJaxRenderer>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div key={`m-user-${index}-${msg.timestamp || Date.now()}`} className="flex justify-end">
                                  <div className="p-3 rounded-xl max-w-[90%] bg-indigo-50 text-gray-900 self-end">
                                    {msg.questionImage && (
                                      <div className="mb-2">
                                        <div className="text-xs text-gray-600 mb-1">Question:</div>
                                        <img src={msg.questionImage} alt="Question" className="max-h-24 mb-2 rounded shadow border" />
                                      </div>
                                    )}
                                    {msg.image && (
                                      <div className="mb-2">
                                        <img src={msg.image} alt="Answer" className="max-h-24 mb-2 rounded shadow border" />
                                      </div>
                                    )}
                                    <div><MathJaxRenderer className="whitespace-pre-line">{msg.text}</MathJaxRenderer></div>
                                  </div>
                                </div>
                              )
                            )}
                            {showProcessingIndicator && (
                              <SimpleThreeDotsLoader message={loadingMessage} />
                            )}
                            <div ref={chatEndRef}></div>
                          </div>
        )}
                          {communicationMode && renderCommunicationPanel('mt-2 mb-2')}
                          {/* Input Area * /}
                          <div className="relative mt-2 flex-shrink-0">
                            {imagePreview && (
                              <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                                <img src={imagePreview} alt="Preview" className="max-w-full rounded-lg" style={{ maxHeight: '100px' }} />
                                <button onClick={handleRemoveImage} className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70">
                                  <FaTimes size={14} />
                                </button>
                                <div className="text-xs text-gray-500 mt-1">{image?.name}</div>
                              </div>
                            )}
                            {file && !imagePreview && (
                              <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                                <div className="flex items-center"><FaPaperclip className="mr-2 text-indigo-500" /><span className="text-sm">{file.name}</span></div>
                                <button onClick={() => setFile(null)} className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70">
                                  <FaTimes size={14} />
                                </button>
                              </div>
                            )}
                            <div className="relative">
                              <textarea
                                ref={textareaRef}
                                placeholder={fromQuestions ? "Your question is already loaded above. Click 'Get Step-by-Step Help' to start!" : "Enter Your Doubt"}
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                className={`w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${fromQuestions ? 'min-h-[100px] sm:min-h-[200px]' : 'min-h-[60px] sm:min-h-[80px]'}`}
                                disabled={isProcessing}
                              />
                              <div className="absolute bottom-3 left-4 flex gap-4 text-gray-500">
                                <div className="relative flex items-center gap-2">
                                  <FaPlusCircle size={20} className={`cursor-pointer ${isProcessing ? 'opacity-50' : ''}`} onClick={!isProcessing ? handleImageClick : undefined} />
                                  {showCameraOptions && (
                                    <div className="absolute bottom-8 left-0 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex flex-col gap-2 z-10">
                                      <button onClick={handleFileClick} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"><FaPaperclip size={16} /> Upload file</button>
                                      <input type="file" accept=".pdf,.doc,.docx,.txt" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} disabled={isProcessing} />
                                      <button onClick={handleGalleryClick} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"><FaImage size={16} />Gallery</button>
                                      <button onClick={handleCameraClick} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"><FaCamera size={16} />Camera</button>
                                      {(!isMobile || !workbookMode) && (
                                        {(!isMobile || !workbookMode) && (
                                          {(!isMobile || !workbookMode) && (
                                            {(!isMobile || !workbookMode) && (
                                              {(!isMobile || !workbookMode) && (
                                                {(!isMobile || !workbookMode) && (
                                                  {(!isMobile || !workbookMode) && (
                                                    {(!isMobile || !workbookMode) && (
                                                      {(!isMobile || !workbookMode) && (
                                                        <button onClick={handleWorkbookClick} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"><FaBook size={16} />Workbook</button>
                                                      )}
                                                    )}
                                                  )}
                                                )}
                                              )}
                                            )}
                                          )}
                                        )}
                                      )}
                                    </div>
                                  )}
                                </div>
                                <input type="file" accept="image/*" ref={imageInputRef} onChange={handleImageUpload} style={{ display: 'none' }} disabled={isProcessing} />
                              </div>
                              <div className={`absolute bottom-3 right-3 ${isMobile ? 'flex flex-col items-end gap-1' : 'flex gap-2 items-center'}`}>
                                {isMobile ? (
                                  <>
                                    <div className="flex gap-2 items-center">
                                      <span title="Live Assistance">
                                        <img
                                          src="/assets/video-camera-outline.jpg"
                                          alt="Live Assistance"
                                          width={24}
                                          height={24}
                                          style={{ marginRight: 0, verticalAlign: 'middle', cursor: 'pointer' }}
                                          onClick={() => {
                                            if (isProcessing) return;
                                            // Close any open camera/gallery menu to avoid overlay conflicts
                                            setShowCameraOptions(false);
                                            if (!liveAssistance) {
                                              setLiveAssistance(true);
                                              if (!communicationMode) setCommunicationMode(true);
                                              setShowCameraModal(true);
                                            } else {
                                              setLiveAssistance(false);
                                              setShowCameraModal(false);
                                            }
                                          }}
                                        />
                                      </span>
                                      {!isVoicePaused && !showProcessingIndicator && (
                                        <button
                                          type="button"
                                          className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                                          onClick={!isProcessing ? (isVoicePaused ? handleResumeListening : handleVoiceClick) : undefined}
                                          disabled={isProcessing}
                                          title={isVoicePaused ? "Voice" : "Voice"}
                                          style={{ minWidth: 32 }}
                                        >
                                          <MdGraphicEq size={18} style={{ display: "block" }} />                                         </button>
                                      )}
                                    </div>
                                    {((!showIntro && messages.length > 0) || isVoicePaused) && (
                                      <div className="flex gap-2 items-center">
                                        {!showIntro && messages.length > 0 && (
                                          <button
                                            onClick={handleClearChat}
                                            className="px-3 py-1 bg-rose-200 hover:bg-rose-300 text-rose-800 border border-rose-300 text-xs rounded-full transition-colors"
                                            title="Clear all messages and start fresh"
                                          >
                                            Clear Chat
                                          </button>
                                        )}
                                        {isVoicePaused && (
                                          <button
                                            type="button"
                                            className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                                            onClick={!isProcessing && !showProcessingIndicator ? (isVoicePaused ? handleResumeListening : handleVoiceClick) : undefined}
                                            disabled={isProcessing || showProcessingIndicator}
                                            title={isVoicePaused ? "Voice" : "Voice"}
                                            style={{ minWidth: 32 }}
                                          >
                                            <MdGraphicEq size={18} style={{ display: "block" }} />                                           </button>
                                        )}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    {!showIntro && messages.length > 0 && (
                                      <button
                                        onClick={handleClearChat}
                                        className="px-3 py-1 bg-rose-200 hover:bg-rose-300 text-rose-800 border border-rose-300 text-xs rounded-full transition-colors"
                                        title="Clear all messages and start fresh"
                                      >
                                        Clear Chat
                                      </button>
                                    )}
                                    <span title="Live Assistance">
                                      <img
                                        src="/assets/video-camera-outline.jpg"
                                        alt="Live Assistance"
                                        width={24}
                                        height={24}
                                        style={{ marginRight: 0, verticalAlign: 'middle', cursor: 'pointer' }}
                                        onClick={() => {
                                          if (isProcessing) return;
                                          setShowCameraOptions(false);
                                          if (!liveAssistance) {
                                            setLiveAssistance(true);
                                            if (!communicationMode) setCommunicationMode(true);
                                            setShowCameraModal(true);
                                          } else {
                                            setLiveAssistance(false);
                                            setShowCameraModal(false);
                                          }
                                        }}
                                      />
                                    </span>
                                    <button
                                      type="button"
                                      className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                                      onClick={!isProcessing ? (isVoicePaused ? handleResumeListening : handleVoiceClick) : undefined}
                                      disabled={isProcessing}
                                      title={isVoicePaused ? "Voice" : "Voice"}
                                      style={{ minWidth: 32 }}
                                    >
                                      <MdGraphicEq size={18} style={{ display: "block" }} />                                     </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {/* Action buttons * /}
                            <div
                              className={`${
                                isMobile && showModeDropdownOnMobile
                                  ? "w-full flex flex-col gap-3 mt-4 flex-shrink-0"
                                  : `mt-4 flex items-center ${
                                      showSolutionModeButtons
                                        ? "justify-between"
                                        : "justify-end"
                                    } gap-2 flex-wrap`
                              }`}
                            >
                              {isMobile && showModeDropdownOnMobile ? (
                                <>
                                  <div className="w-full">
                                    <div className="flex flex-wrap items-center gap-2">
                                      {showSolutionModeButtons && (
                                        <button
                                          onClick={() => setShowMobileChatActions(prev => !prev)}
                                          className="flex-1 min-w-[160px] flex items-center justify-between h-9 px-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-full transition-colors text-sm"
                                        >
                                          <span>Mode</span>
                                          <span className="text-xs">
                                            {showMobileChatActions ? '\u25B2' : '\u25BC'}
                                          </span>
                                        </button>
                                      )}
                                      <div
                                        className={`${
                                          showSolutionModeButtons
                                            ? "flex-1 min-w-[160px]"
                                            : "w-full"
                                        } flex gap-2 ${
                                          showSolutionModeButtons
                                            ? ""
                                            : "justify-end"
                                        }`}
                                      >
                                        {fromQuestions && (
                                          <button
                                            onClick={() => window.history.back()}
                                            className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                                          >
                                            Back to Quiz
                                          </button>
                                        )}
                                        <button
                                          className={`flex-1 px-4 py-2 ${isProcessing ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} text-white font-bold rounded-full transition-colors text-sm`}
                                          onClick={handleSend}
                                          disabled={isProcessing || (!inputText.trim() && !file && !image && !voice)}
                                        >
                                          {isProcessing ? 'Processing...' : fromQuestions ? 'Get Step-by-Step Help' : 'Solve My Doubt'}
                                        </button>
                                      </div>
                                    </div>
                                    {showMobileChatActions && showSolutionModeButtons && (
                                      <div className="mt-2 flex flex-col gap-2 border-2 border-indigo-700 rounded-[25px] p-2.5">
                                        <div className="relative w-full ">
                                          <button
                                            onClick={() => handleModeToggle('homework')}
                                            onMouseEnter={() => setShowHomeworkTooltipNormal(true)}
                                            onMouseLeave={() => setShowHomeworkTooltipNormal(false)}
                                            className={`flex w-full justify-center items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-all ${activeButton === 'homework'
                                              ? 'border border-gray-400 bg-indigo-50 text-indigo-700'
                                              : 'text-gray-700 hover:bg-gray-50'
                                              }`}
                                          >
                                            <FaBook />Homework Help
                                          </button>
                                          {/* {showHomeworkTooltipNormal && (
                                            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
                                              Get Full Solution
                                            </div>
                                          )} * /}
                                        </div>
                                        <div className="relative w-full">
                                          <button
                                            onClick={() => handleModeToggle('stepbystep')}
                                            onMouseEnter={() => setShowStepTooltipNormal(true)}
                                            onMouseLeave={() => setShowStepTooltipNormal(false)}
                                            className={`flex w-full justify-center items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-all ${activeButton === 'stepbystep'
                                              ? 'border border-gray-400 bg-indigo-50 text-indigo-700'
                                              : 'text-gray-700 hover:bg-gray-50'
                                              }`}
                                          >
                                            <FaPencil /> Step by Step Doubt Clearing
                                          </button>
                                          {/* {showStepTooltipNormal && (
                                            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
                                              Get Step-by-Step Help
                                            </div>
                                          )} * /}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  {showSolutionModeButtons && (
                                    <>
                                      <div className="flex gap-2 relative">
                                        <button
                                          onClick={() => handleModeToggle('homework')}
                                          onMouseEnter={() => setShowHomeworkTooltipNormal(true)}
                                          onMouseLeave={() => setShowHomeworkTooltipNormal(false)}
                                          className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all ${activeButton === 'homework'
                                            ? 'border border-gray-400 bg-indigo-50 text-indigo-700'
                                            : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                          <FaBook />Homework Help
                                        </button>
                                        {showHomeworkTooltipNormal && (
                                          <div className={`absolute ${workbookMode ? 'bottom-full mb-2' : 'top-full mt-2'} left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs  px-2 py-1 whitespace-nowrap z-50  `}>
                                            Get Full Solution
                                          </div>
                                        )}
                                      </div>
                                      <div className="relative">
                                        <button
                                          onClick={() => handleModeToggle('stepbystep')}
                                          onMouseEnter={() => setShowStepTooltipNormal(true)}
                                          onMouseLeave={() => setShowStepTooltipNormal(false)}
                                          className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all ${activeButton === 'stepbystep'
                                            ? 'border border-gray-400 bg-indigo-50 text-indigo-700'
                                            : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                          <FaPencil /> Step by Step Doubt Clearing
                                        </button>
                                        {showStepTooltipNormal && (
                                          <div className={`absolute ${workbookMode ? 'bottom-full mb-2' : 'top-full mt-2'} left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}>
                                            Get Step-by-Step Help
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                  <div
                                    className={`flex ${
                                      showSolutionModeButtons
                                        ? ""
                                        : "w-full justify-end"
                                    } gap-2 sm:gap-4 mt-4 sm:mt-0`}
                                  >
                                    {fromQuestions && (
                                      <button
                                        onClick={() => window.history.back()}
                                        className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                                      >
                                        Back to Quiz
                                      </button>
                                    )}
                                    <button
                                      className={`px-4 py-2 sm:px-6 sm:py-3 ${isProcessing ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'} text-white font-bold rounded-full transition-colors text-sm`}
                                      onClick={handleSend}
                                      disabled={isProcessing || (!inputText.trim() && !file && !image && !voice)}
                                    >
                                      {isProcessing ? 'Processing...' : fromQuestions ? 'Get Step-by-Step Help' : 'Solve My Doubt'}
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                            {/* Click outside to close camera options * /}
                            {showCameraOptions && (<div className="fixed inset-0 z-5" onClick={() => setShowCameraOptions(false)} />)}
                            {/* Camera Modal * /}
                            <CameraModal
                              isOpen={showCameraModal}
                              onClose={() => {
                                setShowCameraModal(false);
                                setIsMobileCameraActive(false);
                                if (liveAssistance) {
                                  setLiveAssistance(false);
                                }
                                // Reset timer when closing modal
                                setSilenceTimerActive(false);
                                setSilenceTimerCount(5);
                                setTriggerCameraCapture(false);
                              }}
                              onCapture={handleCameraCapture}
                              hideCapture={liveAssistance}
                              silenceTimerActive={silenceTimerActive}
                              silenceTimerCount={silenceTimerCount}
                              triggerCapture={triggerCameraCapture}
                              onAutoCapture={handleAutoCameraCapture}
                              handleSend={handleSend}
                            />
            {audioUrl && (
              <audio
                                key={audioUrl}
                                ref={audioRef}
                                src={audioUrl}
                                preload="auto"
                controls={false}
                autoPlay
                                style={{ display: 'none' }}
                                onLoadStart={() => { }}
                                onCanPlay={() => { }}
                                onPlay={() => {
                                  setIsSystemAudioPaused(false);
                                  setIsSystemSpeaking(true);
                                }}
                                onPause={(event) => {
                                  setIsSystemAudioPaused(true);
                                  if (!event?.target?.ended) {
                                    setIsSystemSpeaking(false);
                                  }
                                }}
                                onEnded={() => {
                                  if (lastAudioUrlRef.current) { URL.revokeObjectURL(lastAudioUrlRef.current); lastAudioUrlRef.current = null; }
                                  if (audioBlobRef.current) { audioBlobRef.current = null; }
                                  setAudioUrl(null);
                                  setIsSystemSpeaking(false);
                                  setIsSystemAudioPaused(true);
                                  if (communicationMode) { setIsUserSpeaking(true); startUserRecognition(); }
                                }}
                                onError={(e) => {
                                  if (lastAudioUrlRef.current) { URL.revokeObjectURL(lastAudioUrlRef.current); lastAudioUrlRef.current = null; }
                                  if (audioBlobRef.current) { audioBlobRef.current = null; }
                                  setAudioUrl(null);
                                  setIsSystemSpeaking(false);
                                  setIsSystemAudioPaused(true);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
  */
  const [leftPanelWidth, setLeftPanelWidth] = useState(50);
  const workbookCanvasRef = useRef(null);
  const resizeRef = useRef(null);
  // Canvas functionality states for workbook
  const [workbookFabricCanvas, setWorkbookFabricCanvas] = useState(null);
  const [workbookSelectedRect, setWorkbookSelectedRect] = useState(null);
  const [workbookInputText, setWorkbookInputText] = useState("");
  const [workbookMarkerActive, setWorkbookMarkerActive] = useState(true);
  const [workbookPdfDims, setWorkbookPdfDims] = useState({
    width: 800,
    height: 600,
  });
  const [workbookIsCanvasReady, setWorkbookIsCanvasReady] = useState(false);
  const workbookFabricCanvasRef = useRef(null);
  const workbookRenderTaskRef = useRef(null); // Add render task ref
  const skipWorkbookBoardResetRef = useRef(false);
  const [hasEvaluateAttachment, setHasEvaluateAttachment] = useState(false);
  const [workbookQuestionImage, setWorkbookQuestionImage] = useState(null);
  const hasActiveWorkbookSelection = Boolean(workbookSelectedRect);
  useEffect(() => {
    // if (!isTeachMeWorkbookSource) {
    //   return;
    // }
    setWorkbookSelectedRect(null);
    setWorkbookQuestionImage(null);
    if (workbookFabricCanvas) {
      const existingRects = workbookFabricCanvas.getObjects("rect");
      if (existingRects.length > 0) {
        existingRects.forEach((rect) => workbookFabricCanvas.remove(rect));
        workbookFabricCanvas.renderAll();
      }
    }
  }, [isTeachMeWorkbookSource, workbookFabricCanvas]);
  const [showHomeworkTooltip, setShowHomeworkTooltip] = useState(false);
  const [showHomeworkTooltipNormal, setShowHomeworkTooltipNormal] =
    useState(false);
  // Hand tool: enable touch pan/zoom on mobile and disable selection
  const [handToolEnabled, setHandToolEnabled] = useState(false);
  // Mobile touch/pinch handling for PDF viewer
  const pdfScrollRef = useRef(null);
  const pdfWrapperRef = useRef(null);
  const isPinchingRef = useRef(false);
  const pinchStartDistanceRef = useRef(0);
  const pinchStartZoomRef = useRef(1);
  const pinchStartMidRef = useRef({ x: 0, y: 0 });
  const scrollStartRef = useRef({ left: 0, top: 0 });
  const lastZoomTsRef = useRef(0);
  const smoothedDistanceRef = useRef(0);
  const pendingTouchEventRef = useRef(null);
  const rafIdRef = useRef(null);
  const panDeltaRef = useRef({ dx: 0, dy: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
  // Maintain a 0.5s capture cadence while user can speak and interactive camera is on
  useEffect(() => {
    const canCapture =
      communicationMode &&
      !isSystemSpeaking &&
      !isVoicePaused &&
      liveAssistance &&
      (showCameraModal || isMobileCameraActive);
    if (canCapture) {
      if (!captureIntervalRef.current) {
        captureIntervalRef.current = setInterval(captureLiveFrame, 500);
      }
    } else if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
    return () => {
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
        captureIntervalRef.current = null;
      }
    };
  }, [
    communicationMode,
    isSystemSpeaking,
    isVoicePaused,
    liveAssistance,
    showCameraModal,
    isMobileCameraActive,
  ]);
  // One-time guide modal for workbook usage
  const [showWorkbookGuideModal, setShowWorkbookGuideModal] = useState(false);
  // Check if workbook guide has been shown in this session
  const hasShownWorkbookGuide = () => {
    return StorageService.getItem("workbookGuideShown") === "true";
  };
  const setWorkbookGuideShown = (shown) => {
    StorageService.setItem("workbookGuideShown", shown.toString());
  };
  // Mobile + chat modal controls for workbook mode
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Attach single-touch pan and two-finger pinch-zoom for mobile PDF viewing when Hand tool is enabled
  useEffect(() => {
    if (!isMobile || !workbookPdfDocument || !handToolEnabled) return;
    const wrapper = pdfWrapperRef.current;
    const scroller = pdfScrollRef.current;
    if (!wrapper || !scroller) return;

    const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
    const getDistance = (t1, t2) => {
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      return Math.hypot(dx, dy);
    };
    const getMid = (t1, t2) => ({
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    });

    const onTouchStart = (e) => {
      if (e.touches && e.touches.length === 2) {
        isPinchingRef.current = true;
        isPanningRef.current = false;
        const [t1, t2] = [e.touches[0], e.touches[1]];
        pinchStartDistanceRef.current = getDistance(t1, t2);
        smoothedDistanceRef.current = pinchStartDistanceRef.current;
        pinchStartZoomRef.current = workbookZoomLevel;
        pinchStartMidRef.current = getMid(t1, t2);
        scrollStartRef.current = {
          left: scroller.scrollLeft,
          top: scroller.scrollTop,
        };
        panDeltaRef.current = { dx: 0, dy: 0 };
        try {
          e.preventDefault();
        } catch (_) {}
      } else if (e.touches && e.touches.length === 1) {
        // Single-finger pan
        isPanningRef.current = true;
        isPinchingRef.current = false;
        const t = e.touches[0];
        panStartRef.current = {
          x: t.clientX,
          y: t.clientY,
          left: scroller.scrollLeft,
          top: scroller.scrollTop,
        };
        try {
          e.preventDefault();
        } catch (_) {}
      }
    };

    const processTouch = () => {
      const e = pendingTouchEventRef.current;
      if (!e) {
        rafIdRef.current = null;
        return;
      }
      // Handle pinch
      if (isPinchingRef.current && e.touches && e.touches.length === 2) {
        const [t1, t2] = [e.touches[0], e.touches[1]];
        const rawDist = getDistance(t1, t2);
        const alpha = 0.25; // smoothing factor
        const prevSmoothed = smoothedDistanceRef.current || rawDist;
        const smoothed = prevSmoothed + alpha * (rawDist - prevSmoothed);
        smoothedDistanceRef.current = smoothed;
        const scale = smoothed / (pinchStartDistanceRef.current || smoothed);
        const newZoom = clamp(pinchStartZoomRef.current * scale, 0.5, 3);
        const now = Date.now();
        if (now - (lastZoomTsRef.current || 0) > 16) {
          lastZoomTsRef.current = now;
          setWorkbookZoomLevel((prev) =>
            Math.abs(prev - newZoom) > 0.003 ? newZoom : prev
          );
        }
        const mid = getMid(t1, t2);
        const targetDx = mid.x - pinchStartMidRef.current.x;
        const targetDy = mid.y - pinchStartMidRef.current.y;
        const k = 0.35; // pan easing
        panDeltaRef.current.dx += k * (targetDx - panDeltaRef.current.dx);
        panDeltaRef.current.dy += k * (targetDy - panDeltaRef.current.dy);
        scroller.scrollLeft =
          scrollStartRef.current.left - panDeltaRef.current.dx;
        scroller.scrollTop =
          scrollStartRef.current.top - panDeltaRef.current.dy;
      }
      rafIdRef.current = requestAnimationFrame(processTouch);
    };

    const onTouchMove = (e) => {
      // Pinch move
      if (isPinchingRef.current && e.touches && e.touches.length === 2) {
        pendingTouchEventRef.current = e;
        if (!rafIdRef.current) {
          rafIdRef.current = requestAnimationFrame(processTouch);
        }
        try {
          e.preventDefault();
        } catch (_) {}
        return;
      }
      // Single-finger pan
      if (isPanningRef.current && e.touches && e.touches.length === 1) {
        const t = e.touches[0];
        const dx = t.clientX - panStartRef.current.x;
        const dy = t.clientY - panStartRef.current.y;
        scroller.scrollLeft = panStartRef.current.left - dx;
        scroller.scrollTop = panStartRef.current.top - dy;
        try {
          e.preventDefault();
        } catch (_) {}
        return;
      }
    };

    const onTouchEnd = (e) => {
      if (!e.touches || e.touches.length < 2) {
        isPinchingRef.current = false;
        pendingTouchEventRef.current = null;
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      }
      if (!e.touches || e.touches.length === 0) {
        isPanningRef.current = false;
      }
    };

    wrapper.addEventListener("touchstart", onTouchStart, { passive: false });
    wrapper.addEventListener("touchmove", onTouchMove, { passive: false });
    wrapper.addEventListener("touchend", onTouchEnd, { passive: false });
    wrapper.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      wrapper.removeEventListener("touchstart", onTouchStart);
      wrapper.removeEventListener("touchmove", onTouchMove);
      wrapper.removeEventListener("touchend", onTouchEnd);
      wrapper.removeEventListener("touchcancel", onTouchEnd);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isMobile, workbookPdfDocument, handToolEnabled]);
  useEffect(() => {
    if (!isChatModalOpen || !workbookMode) {
      setShowMobileChatActions(false);
    }
  }, [isChatModalOpen, workbookMode]);
  // Keep chat modal open; do not auto-close when selection clears
  useEffect(() => {
    // no-op: we intentionally keep chat modal state until user closes it
  }, [isMobile, workbookMode, workbookSelectedRect]);
  // Stop showing loader once processing ends or a bot response arrives
  useEffect(() => {
    if (!isProcessing) {
      awaitingResponseSinceRef.current = null;
      setIsAwaitingResponse(false);
    }
  }, [isProcessing]);
  useEffect(() => {
    if (!isAwaitingResponse) return;
    const since = awaitingResponseSinceRef.current;
    if (!since) return;
    const hasRecentBotResponse = messages.some(
      (msg) => msg.type === "bot" && (msg.timestamp || 0) >= since
    );
    if (hasRecentBotResponse) {
      awaitingResponseSinceRef.current = null;
      setIsAwaitingResponse(false);
    }
  }, [messages, isAwaitingResponse]);
  // Helper: open chat modal and post current selection screenshot as a user message (mobile only)
  const handleOpenChatFromSelection = async () => {
    setIsChatModalOpen(true);
    // Prepare the selection image for later send, but do not push a message yet
    if (!isMobile || !workbookMode || !hasActiveWorkbookSelection) return;
    try {
      const questionImageFile = await createQuestionImageFromSelection(
        workbookSelectedRect
      );
      if (questionImageFile) {
        setWorkbookQuestionImage(questionImageFile);
      }
    } catch (e) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | handleOpenChatFromSelection | L962`,
        e,
        {
          isMobile,
          workbookMode,
          hasSelection: Boolean(workbookSelectedRect),
          selectionMetadata: workbookSelectedRect
            ? {
                left: workbookSelectedRect.left ?? null,
                top: workbookSelectedRect.top ?? null,
                width: workbookSelectedRect.width ?? null,
                height: workbookSelectedRect.height ?? null,
              }
            : null,
        }
      );
      console.error("Failed to create selection image for chat:", e);
    }
  };
  const [showStepTooltipNormal, setShowStepTooltipNormal] = useState(false);
  useEffect(() => {
    inputTextRef.current = inputText;
  }, [inputText]);
  useEffect(() => {
    imageRef.current = image;
  }, [image]);
  useEffect(() => {
    fileRef.current = file;
  }, [file]);
  useEffect(() => {
    imagePreviewRef.current = imagePreview;
  }, [imagePreview]);
  // Reset transient attachment state when switching between modes (homework/stepbystep)
  // This avoids stale file inputs preventing onChange when the same file is chosen again
  useEffect(() => {
    // Revoke existing preview URL if present
    if (imagePreviewRef.current) {
      try {
        URL.revokeObjectURL(imagePreviewRef.current);
      } catch (e) {}
    }
    // Clear current selections
    setImage(null);
    setImagePreview(null);
    setFile(null);
    // Reset hidden input elements' values
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Close any open camera/gallery menu
    setShowCameraOptions(false);
  }, [activeButton]);
  const stopUserRecognition = ({ skipOnEnd = true } = {}) => {
    const recognition = currentRecognitionRef.current;
    if (!recognition) {
      skipRecognitionOnEndRef.current = false;
      return;
    }
    skipRecognitionOnEndRef.current = skipOnEnd ? recognition : false;
    try {
      recognition.stop();
    } catch (_) {}
    try {
      recognition.abort?.();
    } catch (_) {}
    currentRecognitionRef.current = null;
    setIsUserSpeaking(false);
  };
  // Cleanup function for all audio and speech resources
  const cleanupAudioResources = () => {
    // Clean up audio URL
    if (lastAudioUrlRef.current) {
      URL.revokeObjectURL(lastAudioUrlRef.current);
      lastAudioUrlRef.current = null;
    }
    // Clean up audio blob
    if (audioBlobRef.current) {
      audioBlobRef.current = null;
    }
    // Only stop recognition if user is not actively speaking
    if (currentRecognitionRef.current && !isUserSpeaking) {
      stopUserRecognition();
    }
    // Stop speech synthesis
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    // Clean up current utterance
    if (currentUtteranceRef.current) {
      currentUtteranceRef.current = null;
    }
    setAudioUrl(null);
    setIsSystemSpeaking(false);
    // Don't set isUserSpeaking to false if they're actively speaking
    if (!isUserSpeaking) {
      setIsUserSpeaking(false);
    }
  };
  const stopActiveSystemSpeech = useCallback(() => {
    let interrupted = false;
    if (audioRef.current) {
      try {
        const wasPaused = audioRef.current.paused;
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        interrupted = interrupted || !wasPaused;
      } catch (error) {
        console.warn("Failed to pause system audio:", error);
      }
    }
    if (lastAudioUrlRef.current) {
      try {
        URL.revokeObjectURL(lastAudioUrlRef.current);
      } catch (error) {
        console.warn("Failed to revoke previous audio URL:", error);
      }
      lastAudioUrlRef.current = null;
      interrupted = true;
    }
    if (audioBlobRef.current) {
      audioBlobRef.current = null;
      interrupted = true;
    }
    setAudioUrl(null);
    try {
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.cancel();
        interrupted = true;
      }
    } catch (error) {
      console.warn("Failed to cancel speech synthesis:", error);
    }
    currentUtteranceRef.current = null;
    if (interrupted || isSystemSpeaking) {
      setIsSystemSpeaking(false);
      setIsSystemAudioPaused(true);
    }
  }, [isSystemSpeaking]);
  const speakUserTextIfNeeded = useCallback(
    (text, context = "user-text") => {
      const trimmed = text?.trim();
      if (!communicationMode || !trimmed) {
        return;
      }
      if (!window?.speechSynthesis) {
        return;
      }
      try {
        const utterance = new window.SpeechSynthesisUtterance(trimmed);
        currentUtteranceRef.current = utterance;
        setIsSystemSpeaking(true);
        setIsSystemAudioPaused(true);
        utterance.onend = () => {
          if (currentUtteranceRef.current === utterance) {
            currentUtteranceRef.current = null;
          }
          setIsSystemSpeaking(false);
        };
        utterance.onerror = (event) => {
          if (currentUtteranceRef.current === utterance) {
            currentUtteranceRef.current = null;
          }
          console.warn(
            "Speech synthesis error while reading user text:",
            event?.error ?? event
          );
          setIsSystemSpeaking(false);
        };
        logSpeechSynthesisUsage("user-text", {
          context,
          textLength: trimmed.length,
          conversationMode: Boolean(communicationMode),
        });
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.warn("Failed to speak user text:", error);
        setIsSystemSpeaking(false);
      }
    },
    [communicationMode]
  );
  // Cleanup function for message blob URLs and limit message history
  const cleanupMessagesMemory = useCallback((newMessages) => {
    const MAX_MESSAGES = 30; // Limit to prevent memory issues
    if (newMessages.length > MAX_MESSAGES) {
      // Get messages to remove (oldest ones)
      const messagesToRemove = newMessages.slice(
        0,
        newMessages.length - MAX_MESSAGES
      );
      // Clean up blob URLs from removed messages
      messagesToRemove.forEach((msg) => {
        if (
          msg.image &&
          typeof msg.image === "string" &&
          msg.image.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(msg.image);
          } catch (e) {
            // Ignore cleanup errors for already revoked URLs
          }
        }
        if (
          msg.questionImage &&
          typeof msg.questionImage === "string" &&
          msg.questionImage.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(msg.questionImage);
          } catch (e) {
            // Ignore cleanup errors for already revoked URLs
          }
        }
      });
      // Return truncated messages
      return newMessages.slice(-MAX_MESSAGES);
    }
    return newMessages;
  }, []);
  // Function to clear all messages and reset chat state
  const handleClearChat = (silent = false) => {
    cancelActiveRequest();
    if (messages.length > 0) {
      const historyEntry = buildHistoryEntryFromConversation(messages);
      if (historyEntry) {
        addChatToHistory(historyEntry);
      }
    }
    // Clean up all blob URLs from current messages
    messages.forEach((msg) => {
      if (
        msg.image &&
        typeof msg.image === "string" &&
        msg.image.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(msg.image);
        } catch (e) {
          // Ignore cleanup errors for already revoked URLs
        }
      }
      if (
        msg.questionImage &&
        typeof msg.questionImage === "string" &&
        msg.questionImage.startsWith("blob:")
      ) {
        try {
          URL.revokeObjectURL(msg.questionImage);
        } catch (e) {
          // Ignore cleanup errors for already revoked URLs
        }
      }
    });
    // Reset all chat-related states
    setMessages([]);
    setMessageReactions({});
    setShowIntro(true);
    setInputText("");
    setFile(null);
    setImage(null);
    setImagePreview(null);
    setVoice(null);
    setIsProcessing(false);
    setLoadingMessage("");
    setFromQuestions(false);
    // Clear any ongoing audio/speech resources
    cleanupAudioResources();
    // Reset textarea height
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }, 0);
    // Show confirmation toast (unless silent)
    console.log("silent", silent);
    if (!silent) {
      toast.success("New Chat started !");
    }
  };
  // Wrapper function for setMessages that applies memory cleanup
  const setMessagesWithCleanup = useCallback(
    (newMessagesOrUpdater) => {
      if (typeof newMessagesOrUpdater === "function") {
        // Handle updater function pattern: setMessages(prev => [...prev, newMessage])
        setMessages((prev) => {
          const newMessages = newMessagesOrUpdater(prev);
          return cleanupMessagesMemory(newMessages);
        });
      } else {
        // Handle direct array pattern: setMessages([...messages, newMessage])
        const cleanedMessages = cleanupMessagesMemory(newMessagesOrUpdater);
        setMessages(cleanedMessages);
      }
    },
    [cleanupMessagesMemory, setMessages]
  );

  useEffect(() => {
    if (!selectedHistoryEntry) {
      if (historyLoadTimeoutRef.current) {
        clearTimeout(historyLoadTimeoutRef.current);
        historyLoadTimeoutRef.current = null;
      }
      setIsHistoryLoading(false);
      return;
    }

    const historyMessages = Array.isArray(selectedHistoryEntry.recentHistory)
      ? selectedHistoryEntry.recentHistory
      : [];

    if (historyMessages.length === 0) {
      setIsHistoryLoading(false);
      clearSelectedHistoryEntry();
      return;
    }

    if (historyLoadTimeoutRef.current) {
      clearTimeout(historyLoadTimeoutRef.current);
      historyLoadTimeoutRef.current = null;
    }

    setIsHistoryLoading(true);
    setShowIntro(false);
    setFromQuestions(false);

    const timeoutId = setTimeout(() => {
      const normalizedMessages = historyMessages.map(
        (historyMessage, index) => {
          let timestamp = historyMessage.timestamp;
          if (typeof timestamp === "string") {
            timestamp = new Date(timestamp).getTime();
          }
          if (!Number.isFinite(timestamp)) {
            timestamp = Date.now() + index;
          }

          const isAssistant = historyMessage.role === "assistant";
          const hasImage = Boolean(historyMessage.hasImage);
          const text = hasImage
            ? `${historyMessage.imageContext || "Image shared"}${
                historyMessage.imageName ? ` - ${historyMessage.imageName}` : ""
              }`
            : historyMessage.content || "";

          return {
            type: isAssistant ? "bot" : "user",
            sender: isAssistant ? "assistant" : "user",
            text,
            timestamp,
          };
        }
      );

      setMessagesWithCleanup(normalizedMessages);
      setChatActive(true);
      setIsHistoryLoading(false);
      clearSelectedHistoryEntry();
      historyLoadTimeoutRef.current = null;
    }, 3000);

    historyLoadTimeoutRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (historyLoadTimeoutRef.current === timeoutId) {
        historyLoadTimeoutRef.current = null;
      }
    };
  }, [
    clearSelectedHistoryEntry,
    selectedHistoryEntry,
    setChatActive,
    setFromQuestions,
    setMessagesWithCleanup,
    setShowIntro,
  ]);
  // Ref to manage ongoing streaming timers
  const botStreamTimerRef = useRef(null);
  // Helper: add a bot message and stream it smoothly token-by-token
  const addBotMessageStreaming = (fullText) => {
    if (!fullText) {
      setMessagesWithCleanup((prev) => [
        ...prev,
        { type: "bot", text: "", timestamp: Date.now(), contextual: true },
      ]);
      return;
    }
    // Clear any previous stream timer
    if (botStreamTimerRef.current) {
      clearTimeout(botStreamTimerRef.current);
      botStreamTimerRef.current = null;
    }
    // Reuse existing in-progress streaming bubble if present; otherwise create a new one
    let streamId = Date.now() + Math.random();
    setMessagesWithCleanup((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i--) {
        const msg = next[i];
        if (msg && msg.type === "bot" && msg._streamId) {
          streamId = msg._streamId; // reuse the same bubble
          next[i] = { ...msg, text: "" };
          return next;
        }
      }
      return [
        ...prev,
        {
          type: "bot",
          text: "",
          timestamp: Date.now(),
          contextual: true,
          _streamId: streamId,
        },
      ];
    });
    const words = fullText.split(/(\s+)/); // keep spaces for natural spacing
    let index = 0;
    const step = () => {
      let chunk = "";
      while (index < words.length && chunk.length < STREAM_CHUNK_TARGET_CHARS) {
        chunk += words[index++];
      }
      if (chunk) {
        setMessagesWithCleanup((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i]._streamId === streamId) {
              next[i] = { ...next[i], text: (next[i].text || "") + chunk };
              break;
            }
          }
          return next;
        });
      }
      if (index < words.length) {
        botStreamTimerRef.current = setTimeout(step, STREAM_INTERVAL_MS);
      } else {
        botStreamTimerRef.current = null;
        // Finalize this streaming bubble: remove the internal _streamId so future
        // responses create a new bubble rather than reusing this one.
        setMessagesWithCleanup((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i]._streamId === streamId) {
              const { _streamId, ...rest } = next[i];
              next[i] = rest;
              break;
            }
          }
          return next;
        });
      }
    };
    step();
  };
  // Helper: append a full bot message, but if the last message
  // is a bot streaming placeholder or a short prefix of this text,
  // merge/replace it instead of creating a duplicate bubble.
  const appendBotMessageOnce = (fullText) => {
    setMessagesWithCleanup((prev) => {
      const next = [...prev];
      if (next.length > 0) {
        const last = next[next.length - 1];
        if (last && last.type === "bot" && last._streamId) {
          // Only merge if the last bubble is an in-progress stream; otherwise add a new bubble
          next[next.length - 1] = { ...last, text: (fullText || "").trim() };
          delete next[next.length - 1]._streamId;
          return next;
        }
      }
      return [
        ...prev,
        {
          type: "bot",
          text: fullText,
          timestamp: Date.now(),
          contextual: true,
        },
      ];
    });
  };
  // Cleanup any pending stream timer on unmount
  useEffect(() => {
    return () => {
      if (botStreamTimerRef.current) clearTimeout(botStreamTimerRef.current);
    };
  }, []);
  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupAudioResources();
    };
  }, []);
  // Debug workbook mode changes
  useEffect(() => {
    console.log("workbookMode state changed:", workbookMode);
  }, [workbookMode]);
  // Show guide modal first time a workbook PDF is opened
  useEffect(() => {
    if (workbookPdfDocument && !hasShownWorkbookGuide()) {
      setWorkbookGuideShown(true); // mark as shown for this session
      setShowWorkbookGuideModal(true);
    }
  }, [workbookPdfDocument]);
  // Cleanup when communication mode is disabled
  useEffect(() => {
    if (!communicationMode) {
      cleanupAudioResources();
    }
  }, [communicationMode]);
  // Cleanup audio resources when audioUrl becomes null
  useEffect(() => {
    if (!audioUrl) {
      // Ensure audio element is properly cleaned up
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = "";
      }
    }
  }, [audioUrl]);
  // Add custom scrollbar styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f7fafc;
        border-radius: 3px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #cbd5e0;
        border-radius: 3px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #a0aec0;
      }
      @keyframes pulse-grow {
        0% {
          transform: scale(0);
          opacity: 0;
        }
        50% {
          transform: scale(1);
          opacity: 1;
        }
        100% {
          transform: scale(0);
          opacity: 0;
        }
      }
      .animate-pulse-grow {
        animation: pulse-grow 1.2s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  // Function to auto-resize textarea
  const autoResizeTextarea = (reset = false) => {
    const textarea = textareaRef.current;
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Read textarea value directly instead of relying on state
      const textareaValue = textarea.value || "";
      // Calculate the new height based on content
      const minHeight = reset
        ? 80
        : fromQuestions && textareaValue.length > 0
        ? 200
        : 80; // Minimum height
      const maxHeight = 400; // Maximum height before scrolling
      const newHeight = Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight)
      );
      // Set the new height
      textarea.style.height = reset ? "80px" : `${newHeight}px`;
    }
  };
  // Handle text change with auto-resize w w
  const handleTextChange = (e) => {
    setInputText(e.target.value);
    // Auto-resize after state update
    setTimeout(autoResizeTextarea, 0);
  };
  // Handle pre-filled data from Questions component
  useEffect(() => {
    if (prefilledTextProp != "" && disableWorkbookMode) {
      console.log("Setting prefilled text from prop 1:", prefilledTextProp);
      // Clear any existing attachments
      setImage(null);
      setImagePreview(null);
      setFile(null);
      setVoice(null);
      // Set up the required states
      setActiveButton("homework");
      setIsProcessing(false);

      const hasExistingMessages =
        Array.isArray(messagesRef.current) && messagesRef.current.length > 0;

      if (autoSendPrefilledText) {
        setInputText(prefilledTextProp);
        setShowIntro(false);
        setFromQuestions(true);

        // Small delay to ensure all state updates are complete
        setTimeout(() => {
          handleSend();
          textareaRef.current?.focus();
          autoResizeTextarea();
        }, 100);
      } else {
        setInputText("Help me with this doubt");
        setFromQuestions(false);
        if (!hasExistingMessages) {
          setShowIntro(true);
        }
        setTimeout(() => {
          textareaRef.current?.focus();
          autoResizeTextarea();
        }, 100);
      }
    }
  }, [prefilledTextProp, disableWorkbookMode, autoSendPrefilledText]);
  // Handle pre-filled data from Questions component
  useEffect(() => {
    if (location.state) {
      if (
        prefilledTextProp != "" &&
        disableWorkbookMode &&
        autoSendPrefilledText
      ) {
        console.log("Setting prefilled text from prop 2:", prefilledTextProp);
        setInputText(prefilledTextProp);
        setShowIntro(false);
        setFromQuestions(true);
        setActiveButton("homework");
        setTimeout(async () => {
          textareaRef.current?.focus();
          autoResizeTextarea();
        }, 500);
      }
      const { prefilledText, activeMode, fromQuestions } = location.state;
      if (prefilledText) {
        setInputText(prefilledText);
        setShowIntro(false);
        setFromQuestions(true);
      }
      if (activeMode) {
        setActiveButton(activeMode);
      } else {
        setActiveButton("homework"); // Default fallback
      }
      // If coming from Questions page, focus the textarea after a short delay
      if (fromQuestions) {
        setTimeout(() => {
          textareaRef.current?.focus();
          autoResizeTextarea();
        }, 500);
      }
    }
  }, [location.state]);

  // Reset workbook mode and related state when navigating away from tutor module
  useEffect(() => {
    const isInTutorModule =
      location.pathname.startsWith("/tutor") ||
      location.pathname.startsWith("/practice/tutor");

    // If not in tutor module and workbook mode is active, reset all workbook state
    if (!isInTutorModule && workbookMode) {
      setWorkbookMode(false);
      // Reset all workbook-related state to default values
      setWorkbookBoard("");
      setWorkbookSubject("");
      setWorkbookChapter("");
      setWorkbookPdfUrl("");
      setWorkbookSyllabusData([]);
      setWorkbookSubjects([]);
      setWorkbookChapters([]);
      setIsWorkbookLoading(false);
      setWorkbookPdfDocument(null);
      setWorkbookCurrentPage(1);
      setWorkbookNumPages(0);
      setWorkbookZoomLevel(1.0);
      setWorkbookResetKey(0);
      setWorkbookEvaluateResetKey(0);
      setShowWorkbookControls(true);
      setWorkbookFabricCanvas(null);
      setWorkbookSelectedRect(null);
      setWorkbookInputText("");
      setWorkbookMarkerActive(true);
      setWorkbookPdfDims({ width: 800, height: 600 });
      setWorkbookIsCanvasReady(false);
      setWorkbookQuestionImage(null);
      setShowWorkbookGuideModal(false);
    }
  }, [location.pathname, workbookMode, setWorkbookMode]);
  // Reset workbook flag tracking on navigation changes
  useEffect(() => {
    if (location.key !== lastLocationKeyRef.current) {
      lastLocationKeyRef.current = location.key;
      workbookParamAppliedRef.current = false;
      workbookParamDismissedRef.current = false;
    }
  }, [location.key]);
  // Auto-resize when fromQuestions changes or inputText is set
  useEffect(() => {
    autoResizeTextarea();
  }, [inputText, fromQuestions]);
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      if (isNearBottom) {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages]);
  const handleFileClick = () => fileInputRef.current.click();
  const handleImageClick = () => {
    setShowCameraOptions(!showCameraOptions);
  };
  const handleGalleryClick = () => {
    imageInputRef.current.click();
    setShowCameraOptions(false);
  };
  const handleCameraClick = () => {
    if (isProcessing) return;
    // Always open our capture modal (mobile and desktop).
    // The modal will handle unsupported camera gracefully.
    setShowCameraModal(true);
    setShowCameraOptions(false);
  };
  // New function for live camera with voice
  const handleLiveCameraClick = () => {
    setShowLiveCameraModal(true);
    setShowCameraOptions(false);
  };
  // Toggle live assistance like desktop inline control
  const handleLiveAssistToggle = () => {
    if (isProcessing) return;
    setShowCameraOptions(false);
    if (!liveAssistance) {
      setLiveAssistance(true);
      if (!communicationMode) setCommunicationMode(true);
      setShowCameraModal(true);
    } else {
      setLiveAssistance(false);
      setShowCameraModal(false);
      setIsMobileCameraActive(false);
    }
  };
  // Handler for workbook click
  const handleWorkbookClick = () => {
    setShowCameraOptions(false);
    !collapsed ? toggleSidebar() : null;
    setShowLeftPanel(true);
    setWorkbookMode(true);
    setShowWorkbookControls(true);
    // Don't fetch syllabus here - wait for board selection
  };
  // Enable workbook mode automatically when the query flag is present
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      workbookParamAppliedRef.current = false;
      workbookParamDismissedRef.current = false;
      resetTeachMeState();
      return;
    }

    if (workbookParamDismissedRef.current) {
      return;
    }

    const normalizedTab = (queryWorkbookTab || "").toLowerCase();
    const tabFromQuery =
      normalizedTab === "samplepaper"
        ? "samplePapers"
        : normalizedTab === "pastyear"
        ? "pyq"
        : normalizedTab === "myuploads"
        ? "myUploads"
        : null;
    const hasPrefilledWorkbookSelection =
      (queryWorkbookBoard || "").trim() &&
      (queryWorkbookSubject || "").trim() &&
      (queryWorkbookChapter || "").trim();
    const shouldDefaultToSchoolWorkbook =
      isTeachMeWorkbookSource || hasPrefilledWorkbookSelection;
    const computedDefaultTab = tabFromQuery
      ? tabFromQuery
      : shouldDefaultToSchoolWorkbook
      ? "loadingPDF"
      : "pyq";
    const isFirstApplication =
      !workbookParamAppliedRef.current || !workbookMode;

    if (isFirstApplication) {
      workbookParamAppliedRef.current = true;
      setShowCameraOptions(false);
      if (!collapsed) {
        toggleSidebar();
      }
      setShowLeftPanel(true);
      setWorkbookMode(true);
      setShowWorkbookControls(true);
      setCurrentWorkbookTab(computedDefaultTab);
      return;
    }

    if (tabFromQuery) {
      setCurrentWorkbookTab(tabFromQuery);
    }
  }, [
    queryEnableWorkbookMode,
    queryWorkbookTab,
    collapsed,
    workbookMode,
    toggleSidebar,
    setShowCameraOptions,
    setShowLeftPanel,
    setWorkbookMode,
    setShowWorkbookControls,
    resetTeachMeState,
    queryWorkbookBoard,
    queryWorkbookSubject,
    queryWorkbookChapter,
    isTeachMeWorkbookSource,
  ]);
  // Track workbook-related query parameters for downstream effects
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const enableWorkbookParam = (
      searchParams.get("enableWorkbookmode") || ""
    ).trim();
    const tabParam = (searchParams.get("tab") || "").trim();
    const fromParam = (searchParams.get("from") || "").trim();
    const subjectParam = (searchParams.get("subject") || "").trim();
    const chapterParam = (searchParams.get("chapter") || "").trim();
    const boardParam = (searchParams.get("board") || "").trim();
    const docIdParam = (searchParams.get("docId") || "").trim();
    const normalizedEnableParam = enableWorkbookParam.toLowerCase();
    const enableWorkbook =
      normalizedEnableParam !== "" &&
      normalizedEnableParam !== "false" &&
      normalizedEnableParam !== "0" &&
      normalizedEnableParam !== "no";

    setWorkbookQueryParams((prev) => {
      if (
        prev.enableWorkbookMode === enableWorkbook &&
        prev.tab === tabParam &&
        prev.from === fromParam &&
        prev.subject === subjectParam &&
        prev.chapter === chapterParam &&
        prev.board === boardParam &&
        prev.docId === docIdParam
      ) {
        return prev;
      }
      return {
        enableWorkbookMode: enableWorkbook,
        tab: tabParam,
        from: fromParam,
        subject: subjectParam,
        chapter: chapterParam,
        board: boardParam,
        docId: docIdParam,
      };
    });
  }, [location.search]);
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      workbookPrefillTrackerRef.current = {
        signature: "",
        boardApplied: false,
        subjectApplied: false,
        chapterApplied: false,
        docApplied: false,
      };
      resetTeachMeState();
      return;
    }

    const signature = `${queryWorkbookBoard || ""}|${
      queryWorkbookSubject || ""
    }|${queryWorkbookChapter || ""}|${queryWorkbookDocId || ""}`;
    if (workbookPrefillTrackerRef.current.signature !== signature) {
      workbookPrefillTrackerRef.current = {
        signature,
        boardApplied: false,
        subjectApplied: false,
        chapterApplied: false,
        docApplied: false,
      };
      resetTeachMeState();
    } else if (!isTeachMeWorkbookSource) {
      resetTeachMeState();
    }
  }, [
    queryEnableWorkbookMode,
    queryWorkbookBoard,
    queryWorkbookSubject,
    queryWorkbookChapter,
    queryWorkbookDocId,
    resetTeachMeState,
    isTeachMeWorkbookSource,
  ]);
  // Sync sample paper / PYQ subject filter from query params when provided
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      return;
    }
    const subjectFromQuery = (queryWorkbookSubject || "").trim();
    if (!subjectFromQuery) {
      return;
    }
    const normalizedTab = (queryWorkbookTab || "").toLowerCase();
    if (normalizedTab === "samplepaper") {
      setSamplePapersFilter((prev) =>
        prev.subject === subjectFromQuery
          ? prev
          : { ...prev, subject: subjectFromQuery }
      );
    } else if (normalizedTab === "pastyear") {
      setPyqFilter((prev) =>
        prev.subject === subjectFromQuery
          ? prev
          : { ...prev, subject: subjectFromQuery }
      );
    }
  }, [
    queryEnableWorkbookMode,
    queryWorkbookTab,
    queryWorkbookSubject,
    setSamplePapersFilter,
    setPyqFilter,
  ]);
  // Helper to reset workbook dropdowns when switching to uploaded worksheets without clearing the current PDF
  const resetWorkbookDropdownsForUpload = useCallback(() => {
    skipWorkbookBoardResetRef.current = true;
    setWorkbookBoard("");
    setWorkbookSubject("");
    setWorkbookChapter("");
    setWorkbookSubjects([]);
    setWorkbookChapters([]);
    setWorkbookSyllabusData([]);
    setShowWorkbookControls(true);
  }, []);
  // Stop all ongoing async/streaming/audio/voice processes
  const stopAllOngoingProcesses = useCallback(() => {
    // Cancel any in-flight API call
    cancelActiveRequest();
    // Stop bot streaming timer
    if (botStreamTimerRef.current) {
      clearTimeout(botStreamTimerRef.current);
      botStreamTimerRef.current = null;
    }
    // Stop any audio playback and release blobs
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = "";
      }
      if (lastAudioUrlRef.current) {
        URL.revokeObjectURL(lastAudioUrlRef.current);
        lastAudioUrlRef.current = null;
      }
      if (audioBlobRef.current) {
        audioBlobRef.current = null;
      }
    } catch (_) {}
    // Stop any active speech recognition sessions
    try {
      stopUserRecognition();
      if (recognitionRef && recognitionRef.current) {
        try {
          recognitionRef.current.abort?.();
        } catch {}
        try {
          recognitionRef.current.stop?.();
        } catch {}
        recognitionRef.current = null;
      }
    } catch (_) {}
    // Reset UI processing/awaiting states
    setIsProcessing(false);
    setLoadingMessage("");
    try {
      finishAwaitingResponse();
    } catch (_) {}
    // Reset evaluation widget to cancel any internal work
    setWorkbookEvaluateResetKey((k) => k + 1);
  }, [cancelActiveRequest]);
  const handleWorkbookBoardChange = useCallback(
    (nextBoard) => {
      // Preemptively stop anything in-flight before switching board
      stopAllOngoingProcesses();
      // Mark that we'll fetch manually to avoid double-fetch via effect
      if (!window.__wbManualFetchFlag)
        window.__wbManualFetchFlag = { current: false };
      window.__wbManualFetchFlag.current = true;

      // Reset all dependent state immediately to prevent stale selections
      setWorkbookSubject("");
      setWorkbookChapter("");
      setWorkbookSubjects([]);
      setWorkbookChapters([]);
      setWorkbookPdfUrl("");

      setWorkbookBoard(nextBoard);
      // Trigger fetch immediately with the selected board
      fetchWorkbookSyllabus(nextBoard);
    },
    [stopAllOngoingProcesses]
  );
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      return;
    }
    const desiredBoard = (queryWorkbookBoard || "").trim();
    if (!desiredBoard) {
      return;
    }
    const tracker = workbookPrefillTrackerRef.current;
    if (tracker.boardApplied) {
      return;
    }
    if (workbookBoard === desiredBoard) {
      tracker.boardApplied = true;
      return;
    }
    handleWorkbookBoardChange(desiredBoard);
    tracker.boardApplied = true;
  }, [
    queryEnableWorkbookMode,
    queryWorkbookBoard,
    workbookBoard,
    handleWorkbookBoardChange,
  ]);
  // Fetch workbook syllabus data
  const fetchWorkbookSyllabus = async (boardOverride = null) => {
    const board = boardOverride ?? workbookBoard;
    if (!board) {
      console.log("No board selected, skipping syllabus fetch");
      return;
    }

    setIsSyllabusLoading(true);
    const controller = beginNewRequest();
    try {
      const response = await axios.get(`${baseUrl}/api/v2/syllabus`, {
        signal: controller.signal,
      });
      const syllabusData = response.data || [];
      captureSentrySuccess(
        `${DOUBT_SOLVER_FILE} | fetchWorkbookSyllabus success`,
        {
          board,
          standard: 10,
          school: "DPS",
          status: response?.status ?? null,
          items: Array.isArray(syllabusData) ? syllabusData.length : 0,
        }
      );
      setWorkbookSyllabusData(syllabusData);
      // Extract unique subjects
      const uniqueSubjects = [
        ...new Set(syllabusData.map((item) => item.subject)),
      ];
      setWorkbookSubjects(uniqueSubjects);
      // Don't auto-select - let user choose
    } catch (error) {
      if (isAbortError(error)) {
        // Swallow aborts silently
        return;
      }
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | fetchWorkbookSyllabus | L1595`,
        error,
        {
          board,
          boardOverride,
          workbookBoard,
          requestUrl: `${baseUrl}/api/v2/syllabus`,
        }
      );
      console.error("Error fetching workbook syllabus:", error);
      toast.error("Failed to load workbook data");
    } finally {
      setIsSyllabusLoading(false);
      clearActiveRequest(controller);
    }
  };
  // Handle workbook subject change
  const handleWorkbookSubjectChange = (selectedSubject) => {
    setWorkbookSubject(selectedSubject);
    // Find chapters for the selected subject
    // console.log("workbookSyllabusData", workbookSyllabusData);
    // console.log("selectedSubject", selectedSubject);
    const subjectData = workbookSyllabusData.find(
      (item) => item.subject === selectedSubject
    );
    //console.log("subjectData", subjectData);
    if (subjectData && subjectData.chapters) {
      setWorkbookChapters(subjectData.chapters);
      // Don't auto-select - let user choose chapter
      setWorkbookChapter("");
    } else {
      setWorkbookChapters([]);
      setWorkbookChapter("");
    }
  };
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      return;
    }

    const desiredSubject = (queryWorkbookSubject || "").trim();
    if (!desiredSubject) {
      return;
    }

    if (workbookSyllabusData.length === 0 || workbookSubjects.length === 0) {
      return;
    }

    const tracker = workbookPrefillTrackerRef.current;
    if (tracker.subjectApplied) {
      return;
    }

    const subjectData = workbookSyllabusData.find(
      (item) => item.subject === desiredSubject
    );

    if (
      !subjectData ||
      !Array.isArray(subjectData.chapters) ||
      subjectData.chapters.length === 0
    ) {
      return;
    }

    setWorkbookSubject(desiredSubject);
    setWorkbookChapters(subjectData.chapters);
    setWorkbookChapter("");
    tracker.subjectApplied = true;
  }, [
    queryEnableWorkbookMode,
    queryWorkbookSubject,
    workbookSyllabusData,
    workbookSubjects,
  ]);
  // Handle workbook chapter change
  const handleWorkbookChapterChange = async (selectedChapter) => {
    setCurrentWorkbookTab("loadingPDF");

    setWorkbookChapter(selectedChapter);
    setShowLeftPanel(true);
    // Guard: Only proceed if chapters are populated and we have a valid selection
    if (!selectedChapter || workbookChapters.length === 0) {
      setWorkbookPdfUrl("");
      setCurrentWorkbookTab("schoolWorkbook");
      return;
    }

    // Find PDF URL for the selected chapter
    const chapterData = workbookChapters.find(
      (chap) => chap.name === selectedChapter
    );
    if (chapterData?.docId) {
      fetchPDFfromID(chapterData?.docId, "schoolWorkbook");
    } else if (chapterData?.workBookUrl) {
      // If there's any chat in progress, clear it for a fresh session
      if (messages && messages.length > 0) {
        handleClearChat(true);
      }
      await resetWorkbookEvaluationState();
      setWorkbookPdfUrl(chapterData.workBookUrl);
      await closeWorkbookMarkerSelection();
      // Load immediately (no button needed)
      await loadWorkbookPdf(chapterData.workBookUrl, "schoolWorkbook");
    } else {
      // Silent reset for transient mismatches - no toast error
      setWorkbookPdfUrl("");
      setCurrentWorkbookTab("schoolWorkbook");
      console.log("No workbook URL found for chapter:", selectedChapter);
    }
  };
  handleWorkbookChapterChangeRef.current = handleWorkbookChapterChange;

  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      return;
    }

    const desiredChapter = (queryWorkbookChapter || "").trim();
    if (!desiredChapter) {
      return;
    }

    if (workbookChapters.length === 0) {
      return;
    }

    const tracker = workbookPrefillTrackerRef.current;
    if (tracker.chapterApplied) {
      return;
    }

    const hasChapter = workbookChapters.some(
      (chapter) => chapter.name === desiredChapter
    );
    if (!hasChapter) {
      return;
    }

    const applyChapter = handleWorkbookChapterChangeRef.current;
    if (!applyChapter) {
      return;
    }

    tracker.chapterApplied = true;
    applyChapter(desiredChapter);
  }, [queryEnableWorkbookMode, queryWorkbookChapter, workbookChapters]);
  useEffect(() => {
    if (!queryEnableWorkbookMode) {
      return;
    }

    const docIdFromQuery = (queryWorkbookDocId || "").trim();
    if (!docIdFromQuery) {
      return;
    }

    const tracker = workbookPrefillTrackerRef.current;
    if (tracker.docApplied) {
      return;
    }

    const fetchById = fetchPdfFromIdRef.current;
    if (!fetchById) {
      return;
    }

    tracker.docApplied = true;
    fetchById(docIdFromQuery, "schoolWorkbook");
  }, [queryEnableWorkbookMode, queryWorkbookDocId]);
  useEffect(() => {
    if (
      !isTeachMeWorkbookSource ||
      teachMeAutoSolveTriggeredRef.current ||
      !workbookMode
    ) {
      return;
    }
    if (
      !workbookBoard ||
      !workbookSubject ||
      !workbookChapter ||
      workbookPdfDocument
    ) {
      return;
    }
    teachMeAutoSolveTriggeredRef.current = true;
    handleSolveWorkbookClickRef.current?.();
  }, [
    isTeachMeWorkbookSource,
    workbookMode,
    workbookBoard,
    workbookSubject,
    workbookChapter,
    workbookPdfDocument,
  ]);
  useEffect(() => {
    if (
      !queryEnableWorkbookMode ||
      !isTeachMeWorkbookSource ||
      teachMePromptSentRef.current ||
      !workbookPdfDocument
    ) {
      return;
    }
    const subjectName = (queryWorkbookSubject || "").trim();
    const chapterName = (queryWorkbookChapter || "").trim();
    if (!subjectName || !chapterName) {
      return;
    }
    const prompt = `Please teach me about ${chapterName}`;
    teachMePromptSentRef.current = true;
    teachMePendingPromptRef.current = prompt;
    setInputText(prompt);
    setShowIntro(false);
    setTimeout(() => {
      handleSendRef.current?.();
    }, 250);
  }, [
    queryEnableWorkbookMode,
    isTeachMeWorkbookSource,
    queryWorkbookSubject,
    queryWorkbookChapter,
    workbookPdfDocument,
  ]);

  const openUploadedSheet = async (uploadedFile) => {
    console.log("Opening uploaded worksheet:", uploadedFile);
    await setFetchingPDF(true);

    if (!uploadedFile?.id) {
      toast.error("This upload does not have an accessible file path.");
      return;
    }
    try {
      setShowLeftPanel(true);
      setWorkbookMode(true);
      setIsWorkbookLoading(true);
      const targetTab = currentWorkbookTab; //;
      console.log(
        "DoubtSolver currentWorkbookTab openUploadedSheet targetTab:",
        targetTab
      );
      fetchPDFfromID(uploadedFile.id, targetTab);

      await resetWorkbookEvaluationState();
      await closeWorkbookMarkerSelection();

      if (messages && messages.length > 0) {
        handleClearChat(true);
      }

      resetWorkbookDropdownsForUpload();

      if (isMobile) {
        setShowWorkbookControls(false);
      }
    } catch (error) {
      captureSentryError(`${DOUBT_SOLVER_FILE} | openUploadedSheet`, error, {
        uploadedFileId: uploadedFile?.id ?? null,
      });
      console.error("Error opening uploaded worksheet:", error);
      toast.error("Failed to load uploaded worksheet");
      if (isMobile) {
        setShowWorkbookControls(true);
      }
    } finally {
      setIsWorkbookLoading(false);
    }
  };
  // Handle upload worksheet button click
  const handleUploadWorksheetClick = () => {
    closeWorkbookMarkerSelection();
    resetWorkbookEvaluationState();
    // Trigger file upload without altering current workbook state until a file is selected
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx";
    fileInput.onchange = (e) => handleWorksheetUpload(e);
    fileInput.click();
  };
  // Handle worksheet file upload
  const handleWorksheetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    console.log("Uploading file:", file.name, "Type:", file.type);
    const previousUrl = workbookPdfUrl;
    setIsWorkbookLoading(true);
    try {
      if (file.type === "application/pdf") {
        // Handle PDF files
        const fileUrl = URL.createObjectURL(file);
        const pdfDoc = await getDocument(fileUrl).promise;
        setWorkbookPdfDocument(pdfDoc);
        setWorkbookNumPages(pdfDoc.numPages);
        setWorkbookCurrentPage(1);
        setWorkbookPdfUrl(fileUrl);
        resetWorkbookDropdownsForUpload();
        if (previousUrl && previousUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previousUrl);
        }
        console.log(
          "Uploaded PDF loaded successfully, pages:",
          pdfDoc.numPages
        );
        // Clear chat on new uploaded worksheet
        if (messages && messages.length > 0) {
          handleClearChat(true);
        }
        if (isMobile) {
          setShowWorkbookControls(false);
        }
      } else if (file.type.startsWith("image/")) {
        // Handle image files - create a mock PDF document that renders the image
        const imageUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          // Calculate dimensions to fit the image properly
          const maxWidth = 800;
          const maxHeight = 600;
          let { width, height } = img;
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          // Create a mock PDF document for image that actually renders
          setWorkbookPdfDocument({
            numPages: 1,
            getPage: () =>
              Promise.resolve({
                getViewport: ({ scale }) => ({
                  width: width * scale,
                  height: height * scale,
                }),
                render: (renderContext) => {
                  const { canvasContext, viewport } = renderContext;
                  const canvas = canvasContext.canvas;
                  // Clear and resize canvas
                  canvas.width = viewport.width;
                  canvas.height = viewport.height;
                  canvasContext.clearRect(
                    0,
                    0,
                    viewport.width,
                    viewport.height
                  );
                  // Draw the image
                  canvasContext.drawImage(
                    img,
                    0,
                    0,
                    viewport.width,
                    viewport.height
                  );
                  return { promise: Promise.resolve() };
                },
              }),
          });
          setWorkbookPdfDims({ width, height });
          setWorkbookPdfUrl(imageUrl);
          setWorkbookNumPages(1);
          setWorkbookCurrentPage(1);
          resetWorkbookDropdownsForUpload();
          if (previousUrl && previousUrl.startsWith("blob:")) {
            URL.revokeObjectURL(previousUrl);
          }
          console.log(
            "Image loaded successfully, dimensions:",
            width,
            "x",
            height
          );
          setIsWorkbookLoading(false); // Move loading state here for images
          // Clear chat on new uploaded worksheet image
          if (messages && messages.length > 0) {
            handleClearChat(true);
          }
          if (isMobile) {
            setShowWorkbookControls(false);
          }
        };
        img.onerror = (event) => {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | handleWorksheetUpload::image.onerror | L1752`,
            new Error("Image upload failed to load into preview"),
            {
              fileName: file.name,
              fileType: file.type,
              eventType: event?.type ?? null,
            }
          );
          console.error("Error loading uploaded image");
          toast.error("Failed to load uploaded image");
          URL.revokeObjectURL(imageUrl);
          setIsWorkbookLoading(false); // Handle error case
        };
        img.src = imageUrl;
        return; // Don't call setIsWorkbookLoading(false) in finally for images
      } else if (
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.name.toLowerCase().endsWith(".doc") ||
        file.name.toLowerCase().endsWith(".docx")
      ) {
        // Handle DOC/DOCX files
        console.log("Processing DOCX file:", file.name);
        const docUrl = await handleDocumentFile(file);
        if (!docUrl) {
          return;
        }
        resetWorkbookDropdownsForUpload();
        if (previousUrl && previousUrl.startsWith("blob:")) {
          URL.revokeObjectURL(previousUrl);
        }
        // Clear chat on new uploaded document
        if (messages && messages.length > 0) {
          handleClearChat(false);
        }
        console.log("DOCX file processed, workbookPdfDocument should be set");
        if (isMobile) {
          setShowWorkbookControls(false);
        }
      } else {
        toast.error("Please upload a PDF, image, DOC, or DOCX file");
      }
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | handleWorksheetUpload | L1788`,
        error,
        {
          fileName: file?.name ?? null,
          fileType: file?.type ?? null,
          previousUrl,
        }
      );
      console.error("Error loading uploaded file:", error);
      toast.error("Failed to load uploaded file");
      if (isMobile) {
        setShowWorkbookControls(true);
      }
    } finally {
      // Only set loading to false if we're not handling an image (which has its own loading handling)
      if (!file.type.startsWith("image/")) {
        setIsWorkbookLoading(false);
      }
    }
  };
  // Handle DOC/DOCX files
  const handleDocumentFile = async (file) => {
    let docUrl = "";
    try {
      console.log("handleDocumentFile called for:", file.name);
      // Convert file to ArrayBuffer for mammoth
      const arrayBuffer = await file.arrayBuffer();
      // Use mammoth to extract HTML content from DOCX
      const result = await mammoth.convertToHtml({ arrayBuffer });
      const htmlContent = result.value;
      console.log("DOCX content extracted:", htmlContent.length, "characters");
      if (result.messages.length > 0) {
        console.log("Mammoth parsing messages:", result.messages);
      }
      const width = 800;
      const height = 1200; // Increased height for document content
      // Create a mock PDF document that renders the extracted HTML content
      console.log("Setting workbookPdfDocument for DOCX file");
      setWorkbookPdfDocument({
        numPages: 1,
        getPage: () =>
          Promise.resolve({
            getViewport: ({ scale }) => ({
              width: width * scale,
              height: height * scale,
            }),
            render: (renderContext) => {
              const { canvasContext, viewport } = renderContext;
              const canvas = canvasContext.canvas;
              // Clear and resize canvas
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              canvasContext.clearRect(0, 0, viewport.width, viewport.height);
              // Scale factor for rendering
              const scale = viewport.width / width;
              // White background
              canvasContext.fillStyle = "#ffffff";
              canvasContext.fillRect(0, 0, viewport.width, viewport.height);
              // Create a temporary div to parse the HTML content
              const tempDiv = document.createElement("div");
              tempDiv.innerHTML = htmlContent;
              tempDiv.style.position = "absolute";
              tempDiv.style.left = "-9999px";
              tempDiv.style.width = `${width - 40}px`; // Account for padding
              tempDiv.style.fontFamily = "Arial, sans-serif";
              tempDiv.style.fontSize = "14px";
              tempDiv.style.lineHeight = "1.5";
              tempDiv.style.color = "#333";
              document.body.appendChild(tempDiv);
              // Get text content and render it
              const textContent =
                tempDiv.innerText || tempDiv.textContent || "";
              document.body.removeChild(tempDiv);
              // Set up text rendering
              canvasContext.fillStyle = "#1f2937";
              canvasContext.font = `${14 * scale}px Arial`;
              canvasContext.textAlign = "left";
              // Split text into lines that fit the canvas width
              const maxWidth = (width - 40) * scale; // Account for padding
              const lineHeight = 20 * scale;
              let y = 30 * scale;
              const x = 20 * scale;
              // Split content into paragraphs
              const paragraphs = textContent
                .split("\n")
                .filter((p) => p.trim().length > 0);
              for (const paragraph of paragraphs) {
                if (y > viewport.height - 30 * scale) break; // Stop if we run out of space
                const words = paragraph.split(" ");
                let currentLine = "";
                for (const word of words) {
                  const testLine = currentLine + word + " ";
                  const metrics = canvasContext.measureText(testLine);
                  if (metrics.width > maxWidth && currentLine !== "") {
                    // Draw the current line
                    canvasContext.fillText(currentLine.trim(), x, y);
                    y += lineHeight;
                    currentLine = word + " ";
                    if (y > viewport.height - 30 * scale) break; // Stop if we run out of space
                  } else {
                    currentLine = testLine;
                  }
                }
                // Draw the last line of the paragraph
                if (currentLine.trim() !== "") {
                  canvasContext.fillText(currentLine.trim(), x, y);
                  y += lineHeight;
                }
                // Add extra space between paragraphs
                y += lineHeight * 0.5;
                if (y > viewport.height - 30 * scale) break; // Stop if we run out of space
              }
              // If content was truncated, show indication
              if (y > viewport.height - 50 * scale) {
                canvasContext.fillStyle = "#6b7280";
                canvasContext.font = `${12 * scale}px Arial`;
                canvasContext.fillText(
                  "... (content continues - use area selection to analyze specific parts)",
                  x,
                  viewport.height - 20 * scale
                );
              }
              // Border
              canvasContext.strokeStyle = "#e5e7eb";
              canvasContext.lineWidth = 2 * scale;
              canvasContext.strokeRect(
                10 * scale,
                10 * scale,
                (width - 20) * scale,
                (height - 20) * scale
              );
              return { promise: Promise.resolve() };
            },
          }),
      });
      docUrl = URL.createObjectURL(file);
      setWorkbookPdfDims({ width, height });
      setWorkbookPdfUrl(docUrl);
      setWorkbookNumPages(1);
      setWorkbookCurrentPage(1);
      console.log("DOCX file processing complete, states set:", {
        workbookPdfDims: { width, height },
        numPages: 1,
        currentPage: 1,
      });
      toast.success(
        "Document loaded successfully! You can now select areas and ask questions about the content."
      );
      return docUrl;
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | handleDocumentFile | L1971`,
        error,
        {
          fileName: file?.name ?? null,
          fileType: file?.type ?? null,
          docUrlGenerated: Boolean(docUrl),
        }
      );
      console.error("Error handling document file:", error);
      toast.error("Failed to process document file");
      if (docUrl && docUrl.startsWith("blob:")) {
        URL.revokeObjectURL(docUrl);
      }
      return "";
    }
  };
  // Handle solve workbook button click
  const handleSolveWorkbookClick = async () => {
    setCurrentWorkbookTab("loadingPDF");
    closeWorkbookMarkerSelection();
    setHasEvaluateAttachment(false);
    console.log("handleSolveWorkbookClick called with values:", {
      workbookBoard,
      workbookSubject,
      workbookChapter,
      workbookPdfUrl,
    });
    // Validate that board, subject, and chapter are selected
    if (!workbookBoard) {
      console.log("Board validation failed");
      toast.error("Please select a board first");
      setCurrentWorkbookTab("schoolWorkbook");
      return;
    }
    if (!workbookSubject) {
      console.log("Subject validation failed");
      toast.error("Please select a subject first");
      setCurrentWorkbookTab("schoolWorkbook");
      return;
    }
    if (!workbookChapter) {
      console.log("Chapter validation failed");
      toast.error("Please select a chapter first");
      setCurrentWorkbookTab("schoolWorkbook");
      return;
    }

    // Compute chapter data from current state instead of relying on workbookPdfUrl
    const chapterData = workbookChapters.find(
      (chap) => chap.name === workbookChapter
    );

    if (!chapterData?.workBookUrl) {
      console.log("No workbook URL found for selected chapter");
      toast.error("No workbook found for this chapter");
      setCurrentWorkbookTab("schoolWorkbook");
      return;
    }

    console.log("All validations passed, loading PDF");
    if (chapterData?.docId) {
      fetchPDFfromID(chapterData.docId, "schoolWorkbook");
      return;
    }
    setCurrentWorkbookTab("loadingPDF");
    await loadWorkbookPdf(chapterData.workBookUrl, "schoolWorkbook");
  };
  const handleSolveWorkbookClickRef = useRef(handleSolveWorkbookClick);
  useEffect(() => {
    handleSolveWorkbookClickRef.current = handleSolveWorkbookClick;
  }, [handleSolveWorkbookClick]);
  const handleWorkbookBackClick = () => {
    if (isTeachMeWorkbookSource) {
      handleCloseWorkbook();
      handleWorkbookCloseNavigation();
      setWorkbookPdfSourceTab(null);
      return;
    }
    const tabToReturn =
      workbookPdfSourceTab ||
      (currentWorkbookTab === "loadingPDF"
        ? "schoolWorkbook"
        : currentWorkbookTab) ||
      "schoolWorkbook";
    {
      // Cancel ongoing render task
      if (workbookRenderTaskRef.current) {
        workbookRenderTaskRef.current.cancel();
        workbookRenderTaskRef.current = null;
      }
      // Clean up fabric.js canvas more thoroughly
      if (workbookFabricCanvas) {
        try {
          // Clear all objects from canvas
          workbookFabricCanvas.clear();
          // Dispose of the canvas
          workbookFabricCanvas.dispose();
        } catch (error) {
          console.log("Error disposing workbook fabric canvas:", error);
        }
        setWorkbookFabricCanvas(null);
      }
      // Clean up fabric canvas ref
      if (workbookFabricCanvasRef.current) {
        try {
          if (workbookFabricCanvasRef.current.dispose) {
            workbookFabricCanvasRef.current.dispose();
          }
        } catch (error) {
          console.log("Error disposing fabric canvas ref:", error);
        }
        workbookFabricCanvasRef.current = null;
      }
      // Clean up question image blob URL
      if (workbookQuestionImage) {
        try {
          URL.revokeObjectURL(URL.createObjectURL(workbookQuestionImage));
        } catch (error) {
          // Ignore cleanup errors for non-blob objects
        }
        setWorkbookQuestionImage(null);
      }
      // Clean up PDF URL if it's a blob URL
      if (workbookPdfUrl && workbookPdfUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(workbookPdfUrl);
        } catch (error) {
          console.log("Error cleaning up PDF blob URL:", error);
        }
      }
      fetchUserUploadedWorkbooks(1);
      setShowUploadModal(false);
      setWorkbookBoard("");
      setWorkbookSubject("");
      setWorkbookChapter("");
      setWorkbookSubjects([]);
      setWorkbookChapters([]);
      setWorkbookPdfDocument(null);
      setWorkbookCurrentPage(1);
      setWorkbookNumPages(0);
      setWorkbookSelectedRect(null);
      setWorkbookInputText("");
      setWorkbookIsCanvasReady(false);
      setWorkbookPdfDims({ width: 800, height: 600 });
      setShowWorkbookControls(true);
      setCurrentWorkbookTab(tabToReturn);
      setWorkbookPdfSourceTab(null);
    }
  };
  // Load workbook PDF
  const loadWorkbookPdf = async (pdfUrlOverride, nextTab = null) => {
    const urlToLoad = pdfUrlOverride || workbookPdfUrl;
    if (!urlToLoad) return;
    setIsWorkbookLoading(true);
    try {
      const pdfDoc = await getDocument(urlToLoad).promise;
      setWorkbookPdfDocument(pdfDoc);
      setWorkbookNumPages(pdfDoc.numPages);
      setWorkbookCurrentPage(1);
      console.log("Workbook PDF loaded successfully, pages:", pdfDoc.numPages);
      if (nextTab) {
        setWorkbookPdfSourceTab(nextTab);
        setCurrentWorkbookTab(nextTab);
      }
      // Keep workbook controls open by default in mobile view after PDF loads
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | loadWorkbookPdf | L2045`,
        error,
        {
          urlToLoad,
          pdfUrlOverride: pdfUrlOverride ?? null,
          isMobile,
        }
      );
      console.error("Error loading workbook PDF:", error);
      toast.error("Failed to load PDF");
      if (isMobile) {
        setShowWorkbookControls(true);
      }
    } finally {
      setIsWorkbookLoading(false);
    }
  };
  // Handle panel resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const container = resizeRef.current?.parentElement;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const percentage =
      ((e.clientX - containerRect.left) / containerRect.width) * 100;
    // Limit the resize range (between 30% and 70%)
    if (percentage >= 30 && percentage <= 70) {
      setLeftPanelWidth(percentage);
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };
  // Close workbook mode
  const handleCloseWorkbook = () => {
    // Cancel ongoing render task
    if (workbookRenderTaskRef.current) {
      workbookRenderTaskRef.current.cancel();
      workbookRenderTaskRef.current = null;
    }
    // Clean up fabric.js canvas more thoroughly
    if (workbookFabricCanvas) {
      try {
        // Clear all objects from canvas
        workbookFabricCanvas.clear();
        // Dispose of the canvas
        workbookFabricCanvas.dispose();
      } catch (error) {
        console.log("Error disposing workbook fabric canvas:", error);
      }
      setWorkbookFabricCanvas(null);
    }
    // Clean up fabric canvas ref
    if (workbookFabricCanvasRef.current) {
      try {
        if (workbookFabricCanvasRef.current.dispose) {
          workbookFabricCanvasRef.current.dispose();
        }
      } catch (error) {
        console.log("Error disposing fabric canvas ref:", error);
      }
      workbookFabricCanvasRef.current = null;
    }
    // Clean up question image blob URL
    if (workbookQuestionImage) {
      try {
        URL.revokeObjectURL(URL.createObjectURL(workbookQuestionImage));
      } catch (error) {
        // Ignore cleanup errors for non-blob objects
      }
      setWorkbookQuestionImage(null);
    }
    // Clean up PDF URL if it's a blob URL
    if (workbookPdfUrl && workbookPdfUrl.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(workbookPdfUrl);
      } catch (error) {
        console.log("Error cleaning up PDF blob URL:", error);
      }
    }
    setWorkbookMode(false); // This now uses the context setIsWorkbookMode
    setWorkbookPdfUrl("");
    setWorkbookPdfDocument(null);
    setWorkbookPdfSourceTab(null);
    setWorkbookCurrentPage(1);
    setWorkbookNumPages(0);
    setWorkbookSelectedRect(null);
    setWorkbookInputText("");
    setWorkbookIsCanvasReady(false);
    setWorkbookPdfDims({ width: 800, height: 600 });
    setShowWorkbookControls(true);
    resetTeachMeState();
    workbookParamDismissedRef.current = true;
    toggleSidebar();
  };
  // Workbook canvas functions
  const handleWorkbookTextChange = (e) => setWorkbookInputText(e.target.value);
  const finishAwaitingResponse = useCallback(() => {
    awaitingResponseSinceRef.current = null;
    setIsAwaitingResponse(false);
    setIsProcessing(false);
    setLoadingMessage("");
  }, []);
  const handleWorkbookSolve = async () => {
    if (!hasActiveWorkbookSelection || !workbookCanvasRef.current) {
      console.log("handleWorkbookSolve: Missing required elements:", {
        hasSelectedRect: !!workbookSelectedRect,
        hasPdfCanvas: !!workbookCanvasRef.current,
      });
      return;
    }
    const rect = workbookSelectedRect;
    const { left, top, width, height } = rect;
    // Validate coordinates
    if (width <= 0 || height <= 0) {
      console.log("Invalid rectangle dimensions:", { width, height });
      return;
    }
    if (
      left < 0 ||
      top < 0 ||
      left + width > workbookCanvasRef.current.width ||
      top + height > workbookCanvasRef.current.height
    ) {
      console.log("Rectangle coordinates out of bounds");
      return;
    }
    if (communicationMode) {
      stopActiveSystemSpeech();
    }
    let controller;
    try {
      const workbookUserText = inputText;
      const trimmedWorkbookText = workbookUserText.trim();
      let baseMessages = messages;
      if (workbookMode) {
        // Don't clear chat if teachMe flow is active
        if (!teachMePromptSentRef.current) {
          handleClearChat(true);
          baseMessages = [];
        }
      }
      // Show processing state
      setIsProcessing(true);
      setLoadingMessage(getRandomLoadingMessage());
      startAwaitingResponse();
      // On mobile, ensure chat shows in modal
      if (isMobile) {
        setIsChatModalOpen(true);
      }
      // Crop from the PDF canvas
      const pdfCanvas = workbookCanvasRef.current;
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext("2d");
      // Scale coordinates if DPR is being used
      const dpr = workbookPdfDims.dpr || 1;
      const scaledLeft = left * dpr;
      const scaledTop = top * dpr;
      const scaledWidth = width * dpr;
      const scaledHeight = height * dpr;
      ctx.drawImage(
        pdfCanvas,
        scaledLeft,
        scaledTop,
        scaledWidth,
        scaledHeight, // scaled source rect
        0,
        0,
        width,
        height // destination rect (keep original size)
      );
      // Optionally resize if too large
      const maxWidth = 800;
      let finalCanvas = tempCanvas;
      if (width > maxWidth) {
        const scale = maxWidth / width;
        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = width * scale;
        resizedCanvas.height = height * scale;
        const resizedCtx = resizedCanvas.getContext("2d");
        resizedCtx.drawImage(
          tempCanvas,
          0,
          0,
          resizedCanvas.width,
          resizedCanvas.height
        );
        finalCanvas = resizedCanvas;
      }
      // Convert canvas to blob and create File object
      finalCanvas.toBlob(async (blob) => {
        if (!blob) {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | handleWorkbookSolve::canvasToBlob | L2242`,
            new Error("Canvas toBlob returned null for workbook selection"),
            {
              width,
              height,
              scaledWidth,
              scaledHeight,
              dpr,
              selectionRect: { left, top },
            }
          );
          console.error(
            "Failed to generate image blob from workbook selection"
          );
          toast.error("Could not capture the selected area. Please try again.");
          finishAwaitingResponse();
          return;
        }
        try {
          const imageFile = new File(
            [blob],
            `workbook_image_${Date.now()}.png`,
            {
              type: "image/png",
            }
          );
          // Create user message with image preview first (like in main handleSend)
          const imageUrl = URL.createObjectURL(imageFile);
          const userMessage = {
            type: "user",
            text: trimmedWorkbookText || "",
            timestamp: Date.now(),
            image: imageUrl,
            imageName: imageFile.name,
          };
          // Add message to chat and get updated messages for context (like in main handleSend)
          const updatedMessages = [...baseMessages, userMessage];
          setMessagesWithCleanup(updatedMessages);
          setShowIntro(false);
          setInputText(""); // Clear input text like in main handleSend
          // speakUserTextIfNeeded(trimmedWorkbookText, "handleWorkbookSolve-user-text");
          // Get chat context with updated messages (like in main handleSend)
          const chatContext = getRecentChatHistory(updatedMessages);
          // Create FormData with the same structure as main handleSend
          const formData = new FormData();
          formData.append("image", imageFile);
          formData.append("query", trimmedWorkbookText || "");
          formData.append("mode", activeButton);
          formData.append("chatHistory", JSON.stringify(chatContext));
          formData.append("conversationMode", communicationMode);
          formData.append("prompt", null);
          formData.append("board", userData?.board || "");
          formData.append("grade", userData?.grade || "");
          // Call the API directly
          controller = beginNewRequest();
          let res;
          try {
            res = await fetch(`${baseUrl}/doubt/solve-image-text`, {
              method: "POST",
              body: formData,
              signal: controller.signal,
            });
          } catch (error) {
            if (isAbortError(error)) {
              throw error;
            }
            throw error;
          }
          const workbookRateLimitMessage =
            await extractRateLimitMessageFromResponse(res);
          if (workbookRateLimitMessage) {
            toast.warn(workbookRateLimitMessage);
            finishAwaitingResponse();
            showRateLimitModal(workbookRateLimitMessage);
            return;
          }
          const data = await parseJsonWithSuccessLog(
            res,
            "/doubt/solve-image-text",
            {
              context: "workbook-selection",
              mode: activeButton,
              conversationMode: Boolean(communicationMode),
              hasImage: true,
            }
          );
          const responseText = data?.solution || "Sorry, no explanation found.";
          // Update user message with OCR data if available
          updateUserMessageWithOCR(data);
          // Add bot response (user message already added above)
          const botMessage = {
            type: "bot",
            text: responseText,
            timestamp: Date.now(),
            contextual: true,
          };
          // Stream bot response smoothly
          addBotMessageStreaming(responseText);
          // Ensure chat UI re-enables immediately after receiving response
          finishAwaitingResponse();
          // Clear the selection (input already cleared above)
          setWorkbookSelectedRect(null);
          // Remove the rectangle from the canvas
          if (workbookFabricCanvas) {
            workbookFabricCanvas.remove(rect);
            workbookFabricCanvas.renderAll();
          }
          // Play audio if in communication mode
          if (communicationMode && data?.audio_base64) {
            playBase64Audio(data.audio_base64);
          }
        } catch (error) {
          if (isAbortError(error)) {
            // Request was intentionally cancelled; skip error toast
            return;
          }
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | handleWorkbookSolve::solveRequest | L2361`,
            error,
            {
              responseStatus: res?.status ?? null,
              selectionRect: rect
                ? {
                    left: rect.left ?? null,
                    top: rect.top ?? null,
                    width: rect.width ?? null,
                    height: rect.height ?? null,
                  }
                : null,
              workbookMode,
              communicationMode,
              activeButton,
              queryLength: workbookUserText?.trim()?.length ?? 0,
            }
          );
          console.error("Error calling workbook solve API:", error);
          toast.error("Failed to solve the question. Please try again.");
          finishAwaitingResponse();
        } finally {
          if (controller) {
            clearActiveRequest(controller);
          }
          finishAwaitingResponse();
        }
      }, "image/png");
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | handleWorkbookSolve::canvasCrop | L2377`,
        error,
        {
          width,
          height,
          left,
          top,
          workbookMode,
        }
      );
      console.error("Error during workbook canvas crop:", error);
      toast.error("Failed to crop selected area");
      finishAwaitingResponse();
    }
  };
  const resetWorkbookEvaluationState = useCallback(() => {
    setHasEvaluateAttachment(false);
    setWorkbookEvaluateResetKey((key) => key + 1);
    finishAwaitingResponse();
    activeEvaluationSessionRef.current = null;
    setMessagesWithCleanup((prev) => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].type === "user" && updated[i].isEvaluationPending) {
          updated[i] = { ...updated[i], isEvaluationPending: false };
          break;
        }
      }
      return updated;
    });
  }, [finishAwaitingResponse, setMessagesWithCleanup]);
  const formatWorkbookEvaluationResponse = (evaluation) => {
    if (!evaluation) {
      return "";
    }
    let formattedEvaluation = "";
    const detailedAnalysis = evaluation.detailed_analysis;
    if (detailedAnalysis) {
      if (detailedAnalysis.problem_understanding) {
        formattedEvaluation += `**Problem Understanding:** ${detailedAnalysis.problem_understanding}\n\n`;
      }
      if (detailedAnalysis.approach_evaluation) {
        formattedEvaluation += `**Approach Evaluation:** ${detailedAnalysis.approach_evaluation}\n\n`;
      }
      if (detailedAnalysis.conceptual_understanding) {
        formattedEvaluation += `**Conceptual Understanding:** ${detailedAnalysis.conceptual_understanding}\n\n`;
      }
      if (
        Array.isArray(detailedAnalysis.step_by_step_analysis) &&
        detailedAnalysis.step_by_step_analysis.length > 0
      ) {
        formattedEvaluation += `### 📊 Step-by-Step Analysis\n`;
        detailedAnalysis.step_by_step_analysis.forEach((step) => {
          formattedEvaluation += `**Step ${step.step_number}:** ${step.step_description}\n`;
          formattedEvaluation += `- **Your Work:** ${
            step.student_work || "Not provided"
          }\n`;
          formattedEvaluation += `- **Correctness:** ${
            step.correctness || "N/A"
          }\n`;
          if (step.explanation) {
            formattedEvaluation += `- **Explanation:** ${step.explanation}\n`;
          }
          if (step.correction) {
            formattedEvaluation += `- **Correction:** ${step.correction}\n`;
          }
          formattedEvaluation += "\n";
        });
      }
      if (
        Array.isArray(detailedAnalysis.scoring_breakdown) &&
        detailedAnalysis.scoring_breakdown.length > 0
      ) {
        formattedEvaluation += `### 📈 Scoring Breakdown\n`;
        detailedAnalysis.scoring_breakdown.forEach((item) => {
          formattedEvaluation += `- **${item.component}:** ${
            item.marks_awarded || 0
          }/${item.marks_possible || "N/A"}`;
          if (item.justification) {
            formattedEvaluation += ` (${item.justification})`;
          }
          formattedEvaluation += "\n";
        });
        formattedEvaluation += "\n";
      }
      if (detailedAnalysis.correct_solution) {
        formattedEvaluation += `### ✅ Correct Solution\n${detailedAnalysis.correct_solution}\n\n`;
      }
      if (
        Array.isArray(detailedAnalysis.improvement_suggestions) &&
        detailedAnalysis.improvement_suggestions.length > 0
      ) {
        formattedEvaluation += `### 💡 Improvement Suggestions\n`;
        detailedAnalysis.improvement_suggestions.forEach((suggestion) => {
          formattedEvaluation += `- ${suggestion}\n`;
        });
        formattedEvaluation += "\n";
      }
    }
    if (evaluation.formatted_feedback) {
      formattedEvaluation += `${evaluation.formatted_feedback}\n\n`;
    } else if (evaluation.feedback) {
      formattedEvaluation += `### 📋 Feedback\n${evaluation.feedback}\n\n`;
    }
    return formattedEvaluation.trim();
  };
  const handleWorkbookEvaluationComplete = ({
    sessionToken,
    evaluation,
    ocrExtractedText,
    ocrSuccess,
    ocrConfidence,
  }) => {
    updateUserMessageWithOCR({
      ocr_extracted_text: ocrExtractedText,
      ocr_success: ocrSuccess,
      ocr_confidence: ocrConfidence,
    });
    const formattedEvaluation = formatWorkbookEvaluationResponse(evaluation);
    setMessagesWithCleanup((prev) => [
      ...prev,
      {
        type: "bot",
        text: formattedEvaluation || "No evaluation details available.",
        score: evaluation?.score,
        timestamp: Date.now(),
        isMarkdown: true,
      },
    ]);
    setShowIntro(false);
    finishAwaitingResponse();
    handleEvaluationFinalize(sessionToken);
    setWorkbookSelectedRect(null);
    setInputText("");
    setWorkbookQuestionImage(null);
    if (workbookFabricCanvas) {
      const activeObj = workbookFabricCanvas.getActiveObject();
      if (activeObj) {
        workbookFabricCanvas.remove(activeObj);
        workbookFabricCanvas.renderAll();
      }
    }
  };
  useEffect(() => {
    if (!workbookMode) {
      setShowWorkbookControls(true);
    }
  }, [workbookMode]);
  useEffect(() => {
    // Keep workbook controls open by default in mobile view after PDF loads
    // Removed: setShowWorkbookControls(false) when workbookPdfDocument loads on mobile
  }, [isMobile, workbookPdfDocument]);
  useEffect(() => {
    if (!isMobile) {
      setShowWorkbookControls(true);
    }
  }, [isMobile]);
  const closeWorkbookMarkerSelection = () => {
    setWorkbookSelectedRect(null);
    setInputText("");
    setWorkbookQuestionImage(null); // Clear question image when selection is closed
    resetWorkbookEvaluationState();
    if (workbookFabricCanvas) {
      const activeObj = workbookFabricCanvas.getActiveObject();
      if (activeObj) {
        workbookFabricCanvas.remove(activeObj);
        workbookFabricCanvas.renderAll();
      }
    }
  };
  // Function to create question image from selected area
  const createQuestionImageFromSelection = async (rect) => {
    if (!rect || !workbookCanvasRef.current) {
      return null;
    }
    const { left, top, width, height } = rect;
    // Validate coordinates
    if (width <= 0 || height <= 0) {
      return null;
    }
    try {
      // Crop from the PDF canvas
      const pdfCanvas = workbookCanvasRef.current;
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext("2d");
      // Scale coordinates if DPR is being used
      const dpr = workbookPdfDims.dpr || 1;
      const scaledLeft = left * dpr;
      const scaledTop = top * dpr;
      const scaledWidth = width * dpr;
      const scaledHeight = height * dpr;
      ctx.drawImage(
        pdfCanvas,
        scaledLeft,
        scaledTop,
        scaledWidth,
        scaledHeight, // scaled source rect
        0,
        0,
        width,
        height // destination rect (keep original size)
      );
      // Convert canvas to blob and create File object
      return new Promise((resolve) => {
        tempCanvas.toBlob((blob) => {
          if (blob) {
            const questionImageFile = new File(
              [blob],
              `workbook_question_${Date.now()}.png`,
              {
                type: "image/png",
              }
            );
            resolve(questionImageFile);
          } else {
            resolve(null);
          }
        }, "image/png");
      });
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | createQuestionImageFromSelection | L2618`,
        error,
        {
          rect: rect
            ? {
                left: rect.left ?? null,
                top: rect.top ?? null,
                width: rect.width ?? null,
                height: rect.height ?? null,
              }
            : null,
          workbookPdfDims,
        }
      );
      console.error("Error creating question image:", error);
      return null;
    }
  };
  // Handle response from live camera modal
  const handleLiveCameraResponse = (responseData) => {
    const { solution, image, transcript, hasAudio } = responseData;
    // Create user message
    let messageText = "";
    if (transcript) {
      messageText = transcript;
    } else {
      messageText = "Asked a question using live camera";
    }
    const newUserMessage = {
      type: "user",
      text: messageText,
      timestamp: Date.now(),
      isLiveCamera: true, // Mark as live camera input
    };
    // Add image to message if present
    if (image) {
      const imageUrl = URL.createObjectURL(image);
      newUserMessage.image = imageUrl;
      newUserMessage.imageName = image.name;
    }
    // Add message with context
    const updatedMessages = [...messages, newUserMessage];
    setMessagesWithCleanup(updatedMessages);
    setShowIntro(false);
    // Add bot response with context awareness (smooth streaming)
    setTimeout(() => {
      addBotMessageStreaming(solution);
    }, 200);
  };
  // New function to handle camera capture
  const handleCameraCapture = (file) => {
    setImage(file);
    // Create URL for image preview
    const imageUrl = URL.createObjectURL(file);
    setImagePreview(imageUrl);
    setShowIntro(false);
    // Focus on text area for additional clarification
    setTimeout(() => {
      textareaRef.current?.focus();
      autoResizeTextarea();
    }, 100);
  };

  // Special handler for auto-capture in live assistance mode
  const handleAutoCameraCapture = (file) => {
    console.log(
      "[Silence Timer] Auto-capture triggered, setting up image and sending message"
    );
    console.log(
      "[Silence Timer] handleAutoCameraCapture called with file:",
      file.name
    );
    setImage(file);
    // Create URL for image preview
    const imageUrl = URL.createObjectURL(file);
    setImagePreview(imageUrl);
    setShowIntro(false);

    console.log("[Silence Timer] Auto-send scheduled once image state updates");
    console.log(
      "[Silence Timer] Image file:",
      file.name,
      "Size:",
      file.size,
      "bytes"
    );
    console.log("[Silence Timer] Image preview URL:", imageUrl);
    setPendingAutoSend(true);
  };
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setInputText(`Uploaded document: ${uploadedFile.name}`);
      // Focus on text area for additional clarification
      setTimeout(() => {
        textareaRef.current?.focus();
        autoResizeTextarea();
      }, 100);
      // Clear the file input value so selecting the same file later re-triggers onChange
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  const handleImageUpload = (e) => {
    const uploadedImage = e.target.files[0];
    if (uploadedImage) {
      setImage(uploadedImage);
      // Create URL for image preview
      const imageUrl = URL.createObjectURL(uploadedImage);
      setImagePreview(imageUrl);
      setShowIntro(false);
      // Focus on text area for additional clarification
      setTimeout(() => {
        textareaRef.current?.focus();
        autoResizeTextarea();
      }, 100);
      // Clear the file input value so selecting the same image later re-triggers onChange
      if (imageInputRef.current) {
        imageInputRef.current.value = "";
        try {
          imageInputRef.current.removeAttribute("capture");
        } catch (_) {}
      }
    }
  };
  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImage(null);
    setImagePreview(null);
    // Also clear the hidden input's value to allow re-selecting the same file
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
      try {
        imageInputRef.current.removeAttribute("capture");
      } catch (_) {}
    }
  };
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        const file = items[i].getAsFile();
        // Create a synthetic event object to pass to handleImageUpload
        handleImageUpload({ target: { files: [file] } });
        e.preventDefault(); // Prevent the image from being pasted as text
        break;
      }
    }
  };
  const formatAsSteps = (text) => {
    // Check if text is already formatted with steps
    if (/Step [0-9]+:/i.test(text)) {
      return text;
    }
    // Split by paragraphs
    const paragraphs = text.split(/\n\n+/);
    // If there are multiple paragraphs, format them as steps
    if (paragraphs.length > 1) {
      // Skip introduction paragraph
      const intro = paragraphs[0];
      const steps = paragraphs.slice(1, -1);
      const conclusion = paragraphs[paragraphs.length - 1];
      // Format steps
      const formattedSteps = steps.map((step, index) => `${step}`).join("\n");
      return `${intro}\n${formattedSteps}\n${conclusion}`;
    }
    return text;
  };
  // Basic markdown to HTML for display (limited to bold **...**)
  const applyBasicMarkdown = (text) => {
    if (!text) return text;
    try {
      return text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    } catch {
      return text;
    }
  };

  // Utility to play base64 audio
  const playBase64Audio = (base64String) => {
    if (!base64String) return;
    stopActiveSystemSpeech();
    console.log("🎵 Starting playBase64Audio, length:", base64String.length);
    // Clean up previous audio resources
    if (lastAudioUrlRef.current) {
      URL.revokeObjectURL(lastAudioUrlRef.current);
      lastAudioUrlRef.current = null;
    }
    if (audioBlobRef.current) {
      audioBlobRef.current = null;
    }
    // Stop current audio element but don't clear src aggressively
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    try {
      // Convert base64 to Blob
      const base64Data = base64String.includes(",")
        ? base64String.split(",")[1]
        : base64String;
      const byteString = atob(base64Data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      // Try to detect container from data prefix if present; default to wav
      let mime = "audio/wav";
      try {
        if (
          typeof base64String === "string" &&
          base64String.startsWith("data:")
        ) {
          const m = base64String.match(/^data:([^;]+);base64,/);
          if (m && m[1]) {
            mime = m[1];
          }
        }
      } catch {}
      const blob = new Blob([ab], { type: mime });
      console.log("🎵 Audio blob created, size:", blob.size);
      // Store blob reference for cleanup
      audioBlobRef.current = blob;
      const url = URL.createObjectURL(blob);
      lastAudioUrlRef.current = url;
      setAudioUrl(url);
      setIsSystemAudioPaused(false);
      setIsSystemSpeaking(true);
      console.log("🎵 Audio URL set:", url);
      // Store URL locally to prevent cleanup from clearing it
      const currentAudioUrl = url;
      // Use a timeout to ensure the audio element is properly updated
      setTimeout(() => {
        console.log(
          "🎵 Attempting to play audio, audioRef.current:",
          !!audioRef.current
        );
        console.log("🎵 currentAudioUrl:", currentAudioUrl);
        console.log(
          "🎵 lastAudioUrlRef.current:",
          !!lastAudioUrlRef.current,
          lastAudioUrlRef.current
        );
        if (audioRef.current && currentAudioUrl) {
          console.log("🎵 Audio element src:", audioRef.current.src);
          // Wait for the audio element to actually have the correct src
          const checkAndPlay = () => {
            if (audioRef.current && audioRef.current.src === currentAudioUrl) {
              console.log("🎵 Audio src matches, attempting to play");
              audioRef.current
                .play()
                .then(() => {
                  console.log("🎵 Audio play started successfully");
                })
                .catch((error) => {
                  captureSentryError(
                    `${DOUBT_SOLVER_FILE} | playBase64Audio::play.catch | L2970`,
                    error,
                    {
                      attempts: checkAndPlay.attempts ?? 1,
                      currentAudioUrl,
                      audioReadyState: audioRef.current?.readyState ?? null,
                      audioRefExists: Boolean(audioRef.current),
                    }
                  );
                  console.error("❌ Audio play error:", error);
                  // Clean up on play error
                  if (lastAudioUrlRef.current) {
                    URL.revokeObjectURL(lastAudioUrlRef.current);
                    lastAudioUrlRef.current = null;
                  }
                  if (audioBlobRef.current) {
                    audioBlobRef.current = null;
                  }
                  setAudioUrl(null);
                  setIsSystemSpeaking(false);
                });
            } else {
              console.log("🎵 Audio src not ready yet, waiting...", {
                audioRefSrc: audioRef.current?.src,
                expectedSrc: currentAudioUrl,
              });
              // Try again after a short delay (max 10 attempts)
              if (!checkAndPlay.attempts) checkAndPlay.attempts = 0;
              checkAndPlay.attempts++;
              if (checkAndPlay.attempts < 10) {
                setTimeout(checkAndPlay, 50);
              } else {
                captureSentryError(
                  `${DOUBT_SOLVER_FILE} | playBase64Audio::srcMismatch | L2994`,
                  new Error("Audio src never matched after 10 attempts"),
                  {
                    currentAudioUrl,
                    audioRefSrc: audioRef.current?.src ?? null,
                    attempts: checkAndPlay.attempts,
                  }
                );
                console.error("❌ Audio src never matched after 10 attempts");
                setIsSystemSpeaking(false);
              }
            }
          };
          checkAndPlay();
        } else {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | playBase64Audio::missingAudioElement | L3002`,
            new Error("No audio element available for playback"),
            {
              audioRefExists: !!audioRef.current,
              currentAudioUrlExists: !!currentAudioUrl,
              currentAudioUrl,
            }
          );
          console.error("❌ No audio1 element available for playback", {
            audioRefExists: !!audioRef.current,
            currentAudioUrlExists: !!currentAudioUrl,
            currentAudioUrl: currentAudioUrl,
          });
        }
      }, 100);
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | playBase64Audio | L3010`,
        error,
        {
          base64Length: base64String?.length ?? 0,
        }
      );
      console.error("❌ Error processing audio:", error);
      setIsSystemSpeaking(false);
    }
  };
  // Helper function to update the latest user message with OCR data
  const updateUserMessageWithOCR = (ocrData = {}) => {
    setMessagesWithCleanup((prev) => {
      const updatedMessages = [...prev];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].type === "user") {
          updatedMessages[i] = {
            ...updatedMessages[i],
            ...(ocrData.ocr_extracted_text || ocrData.ocrExtractedText
              ? {
                  ocrExtractedText:
                    ocrData.ocr_extracted_text || ocrData.ocrExtractedText,
                }
              : {}),
            ...(ocrData.ocr_confidence !== undefined ||
            ocrData.ocrConfidence !== undefined
              ? {
                  ocrConfidence:
                    ocrData.ocr_confidence ?? ocrData.ocrConfidence,
                }
              : {}),
            ...(ocrData.ocr_success !== undefined ||
            ocrData.ocrSuccess !== undefined
              ? {
                  ocrSuccess: ocrData.ocr_success ?? ocrData.ocrSuccess,
                }
              : {}),
            isEvaluationPending: false,
          };
          break;
        }
      }
      return updatedMessages;
    });
  };
  const handleSend = async () => {
    console.log("handleSend called");

    const trimmedInput = inputText.trim();
    const prefilledForSend = getPrefilledTextForSend();
    const combinedInput = prefilledForSend
      ? trimmedInput
        ? `${prefilledForSend}\n\n${trimmedInput}`
        : prefilledForSend
      : trimmedInput;
    const hasTypedOrPrefilledInput = Boolean(combinedInput);
    if (
      (!hasTypedOrPrefilledInput && !file && !image && !voice) ||
      isProcessing
    )
      return;
    setIsProcessing(true);
    if (fromQuestions) {
      autoResizeTextarea(true);
    }

    setLoadingMessage(getRandomLoadingMessage());
    const activeRecognition = currentRecognitionRef.current;
    const shouldSkipVoiceSend =
      communicationMode && activeRecognition && hasTypedOrPrefilledInput;
    // Disable listening while waiting for a response. If the user manually
    // sends while recognition is active, allow onend to run but skip its send.
    if (shouldSkipVoiceSend) {
      skipVoiceSendOnEndRef.current = activeRecognition;
      stopUserRecognition({ skipOnEnd: false });
    } else if (communicationMode) {
      skipVoiceSendOnEndRef.current = null;
      stopUserRecognition();
    } else {
      skipVoiceSendOnEndRef.current = null;
    }
    if (communicationMode) {
      setIsVoicePaused(true);
    }
    // In workbook mode on any view, if a selection exists, prefer the workbook flow
    if (workbookMode && hasActiveWorkbookSelection) {
      // reset the processing state started above; handleWorkbookSolve manages its own
      setIsProcessing(false);
      setLoadingMessage("");
      await handleWorkbookSolve();
      return;
    }
    startAwaitingResponse();
    // Create a new message object with user's actual text input
    const newUserMessage = {
      type: "user",
      text: combinedInput || "",
      timestamp: Date.now(),
    };
    // Add image to message if present
    if (image) {
      // Create a copy of the image preview URL for the message
      newUserMessage.image = imagePreview;
      newUserMessage.imageName = image.name;
    }
    // Add file information if present
    if (file) {
      newUserMessage.fileName = file.name;
      newUserMessage.fileType = file.type;
    }
    if (voice) {
      newUserMessage.isVoiceInput = true;
    }
    // Immediately clear attachments from input area but keep them in memory for processing
    const tempImage = image;
    const tempImagePreview = imagePreview;
    const tempFile = file;
    const tempVoice = voice;
    // Clear the UI elements immediately
    setImage(null);
    setImagePreview(null);
    setFile(null);
    setVoice(null);
    // Add message to chat and get updated messages for context
    const updatedMessages = [...messages, newUserMessage];
    setMessagesWithCleanup(updatedMessages);
    setShowIntro(false);
    setInputText("");
    if (prefilledForSend) {
      updatePendingPrefilledText("");
    }
    // speakUserTextIfNeeded(trimmedInput, "handleSend-user-text");
    // Reset textarea height after clearing text (small delay to ensure state update)
    setTimeout(() => {
      autoResizeTextarea();
    }, 10);
    // Detect subject for better follow-up questions
    const chatContext = getRecentChatHistory(updatedMessages);
    let responseText = "This is a step-by-step solution based on your input.";
    let rateLimited = false;
    let requestAborted = false;
    let hadError = false;
    try {
      // Enhanced API calls with chat history context
      if (tempImage) {
        const formData = new FormData();
        formData.append("image", tempImage);
        formData.append("query", combinedInput || "");
        formData.append("mode", activeButton);
        // Add chat history context
        formData.append("chatHistory", JSON.stringify(chatContext));
        // Add conversationMode and prompt parameters
        formData.append("conversationMode", communicationMode);
        formData.append("prompt", null);
        formData.append("board", userData?.board || "");
        formData.append("grade", userData?.grade || "");
        const controller = beginNewRequest();
        try {
          const res = await fetch(`${baseUrl}/doubt/solve-image-text`, {
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          const imageRateLimitMessage =
            await extractRateLimitMessageFromResponse(res);
          if (imageRateLimitMessage) {
            toast.warn(imageRateLimitMessage);
            setIsProcessing(false);
            setLoadingMessage("");
            appendBotMessageOnce(imageRateLimitMessage);
            resetVoiceStateAfterModelFailure();
            showRateLimitModal(imageRateLimitMessage);
            rateLimited = true;
            return;
          }
          const data = await parseJsonWithSuccessLog(
            res,
            "/doubt/solve-image-text",
            {
              context: "handleSend-image",
              mode: activeButton,
              conversationMode: Boolean(communicationMode),
              hasImage: true,
            }
          );
          responseText = data?.solution || "Sorry, no explanation found.";
          // Update user message with OCR data if available
          updateUserMessageWithOCR(data);
          // Play audio if conversationMode is true and audio_base64 is present
          if (communicationMode && data?.audio_base64) {
            playBase64Audio(data.audio_base64);
          } else if (communicationMode) {
            stopActiveSystemSpeech();
            setIsSystemSpeaking(true);
            const utterance = new window.SpeechSynthesisUtterance(responseText);
            logSpeechSynthesisUsage("handleSend-image", {
              responseText,
              conversationMode: Boolean(communicationMode),
              activeMode: activeButton,
              hasAudioBase64: Boolean(data?.audio_base64),
              liveAssistance: Boolean(liveAssistance),
              reason: "fallback-to-speech-synthesis",
            });
            utterance.onend = () => {
              setIsSystemSpeaking(false);
              // Start listening for next user prompt
              if (communicationMode) {
                setIsUserSpeaking(true);
                startUserRecognition();
              }
            };
            window.speechSynthesis.speak(utterance);
          }
        } finally {
          clearActiveRequest(controller);
        }
      } else if (tempFile) {
        const formData = new FormData();
        formData.append("file", tempFile);
        formData.append("query", combinedInput || "");
        formData.append("mode", activeButton);
        // Add chat history context
        formData.append("chatHistory", JSON.stringify(chatContext));
        // Add conversationMode and prompt parameters
        formData.append("conversationMode", communicationMode);
        formData.append("prompt", null);
        const controller = beginNewRequest();
        try {
          const res = await fetch(`${baseUrl}/doubt/upload-file`, {
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          if (res.status === 429) {
            let errorMsg =
              "You have reached the maximum number of requests allowed. Please wait before trying again.";
            try {
              const errData = await res.json();
              if (errData && errData.error) {
                errorMsg = errData.error;
                if (errData.detail) {
                  errorMsg += ` (${errData.detail})`;
                }
              } else if (errData && errData.detail) {
                errorMsg += ` (${errData.detail})`;
              }
            } catch {}
            toast.warn(errorMsg);
            setIsProcessing(false);
            setLoadingMessage("");
            setMessagesWithCleanup((prev) => [
              ...prev,
              {
                type: "bot",
                text: errorMsg,
                timestamp: Date.now(),
                contextual: true,
              },
            ]);
            resetVoiceStateAfterModelFailure();
            rateLimited = true;
            // Set voice to resume listening state instead of just returning
            if (communicationMode) {
              setIsUserSpeaking(false);
              setIsVoicePaused(true); // Show Resume Listening button
            }
            return;
          }
          const data = await parseJsonWithSuccessLog(
            res,
            "/doubt/upload-file",
            {
              context: "handleSend-file",
              mode: activeButton,
              conversationMode: Boolean(communicationMode),
              hasFile: true,
            }
          );
          responseText = data?.solution || "Sorry, no explanation found.";
          // Update user message with OCR data if available
          updateUserMessageWithOCR(data);
          // Play audio if conversationMode is true and audio_base64 is present
          if (communicationMode && data?.audio_base64) {
            playBase64Audio(data.audio_base64);
          } else if (communicationMode) {
            stopActiveSystemSpeech();
            setIsSystemSpeaking(true);
            const utterance = new window.SpeechSynthesisUtterance(responseText);
            logSpeechSynthesisUsage("handleSend-file", {
              responseText,
              conversationMode: Boolean(communicationMode),
              activeMode: activeButton,
              hasAudioBase64: Boolean(data?.audio_base64),
              liveAssistance: Boolean(liveAssistance),
              reason: "fallback-to-speech-synthesis",
            });
            utterance.onend = () => {
              setIsSystemSpeaking(false);
              // Start listening for next user prompt
              if (communicationMode) {
                setIsUserSpeaking(true);
                startUserRecognition();
              }
            };
            window.speechSynthesis.speak(utterance);
          }
        } finally {
          clearActiveRequest(controller);
        }
      } else if (tempVoice) {
        const formData = new FormData();
        formData.append("voice", tempVoice);
        formData.append("mode", activeButton);
        // Add chat history context
        formData.append("chatHistory", JSON.stringify(chatContext));
        const controller = beginNewRequest();
        try {
          const res = await fetch(`${baseUrl}/doubt/send-voice`, {
            method: "POST",
            body: formData,
            signal: controller.signal,
          });
          if (res.status === 429) {
            let errorMsg =
              "You have reached the maximum number of requests allowed. Please wait before trying again.";
            try {
              const errData = await res.json();
              if (errData && errData.error) {
                errorMsg = errData.error;
                if (errData.detail) {
                  errorMsg += ` (${errData.detail})`;
                }
              } else if (errData && errData.detail) {
                errorMsg += ` (${errData.detail})`;
              }
            } catch {}
            toast.warn(errorMsg);
            setIsProcessing(false);
            setLoadingMessage("");
            setMessagesWithCleanup((prev) => [
              ...prev,
              {
                type: "bot",
                text: errorMsg,
                timestamp: Date.now(),
                contextual: true,
              },
            ]);
            resetVoiceStateAfterModelFailure();
            rateLimited = true;
            return;
          }
          const data = await parseJsonWithSuccessLog(res, "/doubt/send-voice", {
            context: "handleSend-voice",
            mode: activeButton,
            conversationMode: Boolean(communicationMode),
            hasVoice: true,
          });
          responseText = data?.solution || "Sorry, no explanation found.";
        } finally {
          clearActiveRequest(controller);
        }
      } else {
        const controller = beginNewRequest();
        try {
          const shouldTeachTopic = isTeachMeWorkbookSource;
          const textEndpoint = shouldTeachTopic
            ? "/doubt/teach-topic"
            : "/doubt/solve-text";
          const textPayload = {
            text: combinedInput,
            mode: activeButton,
            chatHistory: chatContext,
            conversationMode: communicationMode,
            prompt: null,
            board: userData?.board,
            grade: userData?.grade,
            ...(shouldTeachTopic && {
              subject: queryWorkbookSubject,
              chapter: queryWorkbookChapter,
            }),
          };
          const res = await fetch(`${baseUrl}${textEndpoint}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(textPayload),
            signal: controller.signal,
          });
          const textRateLimitMessage =
            await extractRateLimitMessageFromResponse(res);
          if (textRateLimitMessage) {
            toast.warn(textRateLimitMessage);
            setIsProcessing(false);
            setLoadingMessage("");
            setMessagesWithCleanup((prev) => [
              ...prev,
              {
                type: "bot",
                text: textRateLimitMessage,
                timestamp: Date.now(),
                contextual: true,
              },
            ]);
            resetVoiceStateAfterModelFailure();
            showRateLimitModal(textRateLimitMessage);
            rateLimited = true;
            return;
          }
          const data = await parseJsonWithSuccessLog(res, textEndpoint, {
            context: "handleSend-text",
            mode: activeButton,
            conversationMode: Boolean(communicationMode),
            textLength: combinedInput?.length ?? 0,
          });
          responseText = data?.solution || "Sorry, no explanation found.";
          // Update user message with OCR data if available
          updateUserMessageWithOCR(data);
          // Play audio only if in voice or live interaction mode and audio_base64 is present
          if ((communicationMode || liveAssistance) && data?.audio_base64) {
            playBase64Audio(data.audio_base64);
          } else if (communicationMode || liveAssistance) {
            stopActiveSystemSpeech();
            setIsSystemSpeaking(true);
            const utterance = new window.SpeechSynthesisUtterance(responseText);
            logSpeechSynthesisUsage("handleSend-text", {
              responseText,
              conversationMode: Boolean(communicationMode),
              activeMode: activeButton,
              hasAudioBase64: Boolean(data?.audio_base64),
              liveAssistance: Boolean(liveAssistance),
              reason: "fallback-to-speech-synthesis",
            });
            utterance.onend = () => {
              setIsSystemSpeaking(false);
              // Start listening for next user prompt
              if (communicationMode) {
                setIsUserSpeaking(true);
                startUserRecognition();
              }
            };
            window.speechSynthesis.speak(utterance);
          }
        } finally {
          clearActiveRequest(controller);
        }
      }
    } catch (err) {
      if (isAbortError(err)) {
        requestAborted = true;
        resetVoiceStateAfterModelFailure();
        return;
      }
      hadError = true;
      responseText = handleModelApiError(err, "handleSend::apiCalls | L3386", {
        hasImage: Boolean(tempImage),
        hasFile: Boolean(tempFile),
        hasVoice: Boolean(tempVoice),
        activeButton,
        inputLength: combinedInput?.length ?? 0,
      });
      resetVoiceStateAfterModelFailure();
    } finally {
      // Cleanup and add response
      if (tempImagePreview) {
        URL.revokeObjectURL(tempImagePreview);
      }
      if (!rateLimited && !requestAborted) {
        const formattedResponse = formatAsSteps(responseText);
        addBotMessageStreaming(formattedResponse);
      } else {
        // If rate limited, set voice to resume listening state
        if (communicationMode) {
          setIsUserSpeaking(false);
          setIsVoicePaused(true); // Show Resume Listening button
        }
      }
      if (hadError && communicationMode && !rateLimited) {
        setIsUserSpeaking(false);
        setIsVoicePaused(true);
      }
      finishAwaitingResponse();
    }
  };

  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      const node = textareaRef.current;
      if (node && typeof node.focus === "function") {
        node.focus();
      }
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing, textareaRef]);

  useEffect(() => {
    handleSendRef.current = handleSend;
  }, [handleSend]);

  useEffect(() => {
    if (pendingAutoSend && image) {
      console.log(
        "[Silence Timer] Image ready, auto-sending message with captured image"
      );
      console.log("[Silence Timer] About to call handleSend()");
      setPendingAutoSend(false);
      handleSendRef.current?.();
      console.log("[Silence Timer] handleSend() invoked from auto-capture");
    }
  }, [pendingAutoSend, image]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith("image/")) {
        handleImageUpload({ target: { files: [file] } });
      } else {
        handleFileUpload({ target: { files: [file] } });
      }
    }
  };
  // Placeholder for Voice button click
  const handleVoiceClick = () => {
    setCommunicationMode(true);
    setIsVoicePaused(false);
  };
  const clearActiveBotResponse = useCallback(() => {
    if (botStreamTimerRef.current) {
      clearTimeout(botStreamTimerRef.current);
      botStreamTimerRef.current = null;
    }
    setMessagesWithCleanup((prev) => {
      const next = [...prev];
      let updated = false;
      for (let i = next.length - 1; i >= 0; i--) {
        const msg = next[i];
        if (!msg || msg.type !== "bot") continue;
        if (msg._streamId) {
          const { _streamId, ...rest } = msg;
          next[i] = rest;
          updated = true;
        }
        break; // keep the latest bot bubble (streaming or finished)
      }
      return updated ? next : prev;
    });
  }, [setMessagesWithCleanup]);
  // Handler to close communication mode
  const handleCloseCommunicationMode = () => {
    setCommunicationMode(false);
    setIsVoicePaused(false); // Ensure Voice button is shown, not Resume Listening
    setShowCameraModal(false); // Also close camera modal
    setLiveAssistance(false); // Also disable interactive mode
    setIsMobileCameraActive(false);
    cancelActiveRequest();
    setIsProcessing(false);
    setLoadingMessage("");
    stopUserRecognition();
    // Use centralized cleanup function for other resources
    cleanupAudioResources();
  };
  const startAwaitingResponse = () => {
    awaitingResponseSinceRef.current = Date.now();
    setIsAwaitingResponse(true);
    // Immediately stop listening while waiting for a response
    stopUserRecognition();
    setIsVoicePaused(true); // Ensure mic shows as muted while awaiting
  };
  const handleEvaluationStart = ({
    question,
    file,
    previewUrl,
    questionImage,
    questionImageUrl,
  }) => {
    if (workbookMode) {
      handleClearChat(true);
    }
    const sessionToken = `eval-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2)}`;
    activeEvaluationSessionRef.current = sessionToken;
    const messageLines = [];
    if (file?.name) {
      messageLines.push(`Answer to evaluate: ${file.name}`);
    }
    if (question) {
      messageLines.push(`Question: ${question}`);
    }
    const text =
      messageLines.length > 0
        ? messageLines.join("\n")
        : "Submitted an answer for evaluation";
    setMessagesWithCleanup((prev) => {
      const baseMessages = workbookMode ? [] : prev;
      return [
        ...baseMessages,
        {
          type: "user",
          text,
          image: previewUrl || null,
          questionImage: questionImageUrl || null,
          fileName: file?.name || null,
          fileType: file?.type || null,
          timestamp: Date.now(),
          isEvaluationPending: true,
        },
      ];
    });
    setShowIntro(false);
    setIsProcessing(true);
    setLoadingMessage(getRandomLoadingMessage());
    startAwaitingResponse();
    if (isMobile) {
      setIsChatModalOpen(true);
    }
    return sessionToken;
  };
  const handleEvaluationFinalize = (token) => {
    if (token && token !== activeEvaluationSessionRef.current) {
      return;
    }
    finishAwaitingResponse();
    if (!token || token === activeEvaluationSessionRef.current) {
      activeEvaluationSessionRef.current = null;
    }
  };
  const handleEvaluationEnd = (token) => {
    handleEvaluationFinalize(token);
  };
  const handleEvaluationError = (err, token) => {
    if (token && token !== activeEvaluationSessionRef.current) {
      return;
    }
    handleEvaluationFinalize(token);
    updateUserMessageWithOCR();
    const errorText =
      err?.message || "Failed to evaluate the answer. Please try again.";
    setMessagesWithCleanup((prev) => [
      ...prev,
      {
        type: "bot",
        text: errorText,
        timestamp: Date.now(),
        contextual: true,
      },
    ]);
  };
  // Handler for speaker button (placeholder)
  const handleSpeakerClick = () => {
    toast.info("Play system voice (to be implemented)");
  };
  const handleUserInterrupt = () => {
    if (!communicationMode) return;

    if (isSystemSpeaking) {
      stopActiveSystemSpeech();
      clearActiveBotResponse();
      handleResumeListening();
      return;
    }

    if (isVoicePaused) {
      handleResumeListening();
    } else {
      pauseUserListening();
    }
  };
  const handleToggleSystemAudio = useCallback(async () => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      return;
    }
    try {
      if (audioElement.paused) {
        const playAttempt = audioElement.play();
        if (playAttempt?.then) {
          await playAttempt;
        }
        setIsSystemAudioPaused(false);
        setIsSystemSpeaking(true);
      } else {
        audioElement.pause();
        setIsSystemAudioPaused(true);
        setIsSystemSpeaking(false);
      }
    } catch (error) {
      console.warn("Failed to toggle system audio:", error);
    }
  }, []);
  const handleReplaySystemAudio = useCallback(async () => {
    const audioElement = audioRef.current;
    if (!audioElement) {
      return;
    }
    try {
      if (window?.speechSynthesis?.speaking) {
        try {
          window.speechSynthesis.cancel();
        } catch (speechError) {
          console.warn(
            "Failed to cancel speech synthesis before replay:",
            speechError
          );
        }
      }
      currentUtteranceRef.current = null;
      audioElement.pause();
      audioElement.currentTime = 0;
      const playAttempt = audioElement.play();
      if (playAttempt?.then) {
        await playAttempt;
      }
      setIsSystemAudioPaused(false);
      setIsSystemSpeaking(true);
    } catch (error) {
      console.warn("Failed to replay system audio:", error);
    }
  }, []);
  const renderCommunicationPanel = (marginClass = "") => {
    return (
      <div className={`w-full flex justify-center flex-shrink-0`}>
        <div className="w-full bg-white rounded-2xl flex items-center justify-between relative text-gray-900">
          <div className="flex-1 flex flex-row items-center justify-center">
            <div className="mr-2 text-xs text-center text-gray-500 h-full justify-center">
              System
            </div>
            <div className="flex gap-2 items-end">
              <div
                className="bg-indigo-400 rounded-xl"
                style={{
                  width: "24px",
                  height: "18px",
                  opacity: systemModulationOpacities[0],
                  transition: "opacity 0.1s",
                }}
              ></div>
              <div
                className="bg-indigo-500 rounded-xl"
                style={{
                  width: "24px",
                  height: "22px",
                  opacity: systemModulationOpacities[1],
                  transition: "opacity 0.1s",
                }}
              ></div>
              <div
                className="bg-indigo-400 rounded-xl"
                style={{
                  width: "24px",
                  height: "18px",
                  opacity: systemModulationOpacities[2],
                  transition: "opacity 0.1s",
                }}
              ></div>
            </div>
            {audioUrl ? (
              <div className="ml-5 flex gap-2">
                <button
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-2xl shadow hover:bg-gray-200"
                  onClick={handleToggleSystemAudio}
                  title={isSystemAudioPaused ? "Play" : "Pause"}
                >
                  {isSystemAudioPaused ? (
                    <FaPlay size={20} />
                  ) : (
                    <FaPause size={20} />
                  )}
                </button>
                <button
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-2xl shadow hover:bg-gray-200"
                  onClick={handleReplaySystemAudio}
                  title="Replay"
                >
                  <FaRedo size={20} />
                </button>
              </div>
            ) : (
              <div className="mt-3 h-10" />
            )}
          </div>
          <div className="flex-1 flex flex-row items-center justify-center">
            <div className="mr-2 text-xs text-center text-gray-500">You</div>
            <div className="flex gap-2 items-end">
              <div
                className="bg-green-400 rounded-xl"
                style={{
                  width: "24px",
                  height: "18px",
                  opacity: userModulationOpacities[0],
                  transition: "opacity 0.1s",
                }}
              ></div>
              <div
                className="bg-green-500 rounded-xl"
                style={{
                  width: "24px",
                  height: "22px",
                  opacity: userModulationOpacities[1],
                  transition: "opacity 0.1s",
                }}
              ></div>
              <div
                className="bg-green-400 rounded-xl"
                style={{
                  width: "24px",
                  height: "18px",
                  opacity: userModulationOpacities[2],
                  transition: "opacity 0.1s",
                }}
              ></div>
            </div>
            <div className="ml-5">
              <button
                disabled={showProcessingIndicator}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-2xl shadow transition-colors ${
                  isSystemSpeaking || isVoicePaused
                    ? "bg-yellow-200 hover:bg-yellow-300 text-yellow-900"
                    : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                } ${
                  showProcessingIndicator ? "opacity-60 cursor-not-allowed" : ""
                }`}
                onClick={
                  !showProcessingIndicator ? handleUserInterrupt : undefined
                }
                title={
                  showProcessingIndicator
                    ? "Processing..."
                    : isSystemSpeaking
                    ? "Interrupt response"
                    : isVoicePaused
                    ? "Resume listening"
                    : "Listening"
                }
                aria-label={
                  showProcessingIndicator
                    ? "Processing..."
                    : isSystemSpeaking
                    ? "Interrupt response"
                    : isVoicePaused
                    ? "Resume listening"
                    : "Listening"
                }
              >
                {isSystemSpeaking || isVoicePaused ? (
                  <FaMicrophoneSlash size={20} />
                ) : (
                  <FaMicrophone size={20} />
                )}
              </button>
            </div>
          </div>
          <div className="">
            <button
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={handleCloseCommunicationMode}
              title="Close"
              aria-label="Close voice mode"
            >
              <FaTimes size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  };
  // Listen/Speak mode management
  useEffect(() => {
    if (
      !communicationMode ||
      isSystemSpeaking ||
      isAwaitingResponse ||
      isVoicePaused
    ) {
      stopUserRecognition();
      return;
    }

    if (!currentRecognitionRef.current) {
      startUserRecognition();
    }

    return () => {
      stopUserRecognition();
    };
  }, [communicationMode, isSystemSpeaking, isAwaitingResponse, isVoicePaused]);
  // When system finishes speaking, return to listen mode
  useEffect(() => {
    if (!communicationMode) return;
    if (!isSystemSpeaking && !isAwaitingResponse) {
      // Restart listening when system finished and we're not awaiting a response
      setIsUserSpeaking(false); // triggers listen cycle
    }
  }, [isSystemSpeaking, isAwaitingResponse, communicationMode]);
  // Animate user modulation bars while user is speaking or in communication mode
  useEffect(() => {
    let interval;
    if (
      isUserSpeaking ||
      (communicationMode && !isSystemSpeaking && !isVoicePaused)
    ) {
      interval = setInterval(() => {
        setUserModulationOpacities([
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
        ]);
      }, 150);
    } else {
      setUserModulationOpacities([0.4, 0.6, 0.4]);
    }
    return () => clearInterval(interval);
  }, [isUserSpeaking, communicationMode, isSystemSpeaking, isVoicePaused]);
  // Animate system modulation bars while system is speaking by changing opacity
  useEffect(() => {
    let interval;
    if (isSystemSpeaking) {
      interval = setInterval(() => {
        // Generate three random opacities for a flickering effect
        setSystemModulationOpacities([
          Math.random() * 0.7 + 0.3, // Opacity between 0.3 and 1.0
          Math.random() * 0.7 + 0.3,
          Math.random() * 0.7 + 0.3,
        ]);
      }, 150);
    } else {
      setSystemModulationOpacities([0.4, 0.6, 0.4]); // Reset to a default, non-animated state
    }
    return () => clearInterval(interval);
  }, [isSystemSpeaking]);
  function pauseUserListening() {
    setIsVoicePaused(true);
    setSilenceTimerActive(false);
    setSilenceTimerCount(5);
    stopUserRecognition();
  }
  // Add handler for resume button
  function handleResumeListening() {
    if (isAwaitingResponse) {
      return;
    }
    setIsVoicePaused(false);
    startUserRecognition();
  }
  function startUserRecognition() {
    stopUserRecognition({ skipOnEnd: true });
    if (!("webkitSpeechRecognition" in window)) {
      toast.warn("Speech Recognition not supported.");
      return;
    }
    let finalSpeechText = "";
    let silenceTimer = null;
    const INITIAL_SILENCE_TIMEOUT_MS = 5000;
    const POST_SPEECH_SILENCE_TIMEOUT_MS = 2000;
    let hasAnySpeech = false;
    const recognition = new window.webkitSpeechRecognition();
    currentRecognitionRef.current = recognition; // Track the current instance
    skipRecognitionOnEndRef.current = false;
    recognition.continuous = true; // Enable continuous listening
    recognition.interimResults = true;
    recognition.lang = "en-IN";
    // Function to handle silence timeout
    const handleSilenceTimeout = () => {
      console.log(
        "[Voice] startUserRecognition silence timeout reached, stopping recognition"
      );
      try {
        recognition.stop();
      } catch (_) {}
    };
    // Function to reset silence timer
    const resetSilenceTimer = () => {
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      const timeout = hasAnySpeech
        ? POST_SPEECH_SILENCE_TIMEOUT_MS
        : INITIAL_SILENCE_TIMEOUT_MS;
      silenceTimer = setTimeout(handleSilenceTimeout, timeout);
    };
    // Clear input at start
    setInputText("");
    inputTextRef.current = "";
    try {
      recognition.start();
      setIsVoicePaused(false);
      setIsUserSpeaking(true);
      // Start the silence timer
      resetSilenceTimer();
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | startUserRecognition::start | L4232`,
        error,
        {
          communicationMode,
          liveAssistance,
          hadExistingRecognition: Boolean(currentRecognitionRef.current),
        }
      );
      console.error("Recognition start error:", error);
      currentRecognitionRef.current = null;
      setIsUserSpeaking(false);
      return;
    }
    recognition.onresult = (event) => {
      // Reset silence timer when speech is detected
      resetSilenceTimer();
      let finalTranscript = "";
      let interimTranscript = "";
      // Process ALL results from the beginning to accumulate complete text
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      // Combine final and interim results
      const completeTranscript = finalTranscript + interimTranscript;
      // Update final speech text only when we have new final results
      if (finalTranscript) {
        finalSpeechText = finalTranscript;
      }
      if (
        !hasAnySpeech &&
        (finalTranscript.trim() || interimTranscript.trim())
      ) {
        hasAnySpeech = true;
      }
      setInputText(completeTranscript);
      inputTextRef.current = completeTranscript;
      setTimeout(autoResizeTextarea, 0);
      setIsVoicePaused(false); // Hide Resume Listening when user is speaking

      // Reset silence timer when user starts speaking
      if (silenceTimerActive) {
        setSilenceTimerActive(false);
        setSilenceTimerCount(5);
      }
    };
    recognition.onerror = (event) => {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | startUserRecognition::onerror | L4276`,
        new Error(`Speech recognition error: ${event?.error ?? "unknown"}`),
        {
          eventError: event?.error ?? null,
          eventMessage: event?.message ?? null,
          communicationMode,
          isSystemSpeaking,
        }
      );
      console.error("Speech recognition error:", event.error);
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      setIsUserSpeaking(false);
      skipRecognitionOnEndRef.current = false;
      currentRecognitionRef.current = null; // Clear reference on error
      if (event.error === "no-speech" && communicationMode) {
        setIsVoicePaused(true);
      }
    };
    recognition.onend = async () => {
      // Clean up recognition instance and timer
      if (silenceTimer) {
        clearTimeout(silenceTimer);
      }
      currentRecognitionRef.current = null;
      if (skipRecognitionOnEndRef.current === recognition) {
        skipRecognitionOnEndRef.current = false;
        setIsUserSpeaking(false);
        return;
      }
      const shouldSkipVoiceSend = skipVoiceSendOnEndRef.current === recognition;
      if (shouldSkipVoiceSend) {
        skipVoiceSendOnEndRef.current = null;
      }
      // Use the final speech text if available, otherwise use current input
      const speechText = finalSpeechText || inputTextRef.current;
      const trimmedSpeechText = (speechText || "").trim();
      const prefilledSpeech = getPrefilledTextForSend();
      const combinedSpeechText = prefilledSpeech
        ? trimmedSpeechText
          ? `${prefilledSpeech}\n\n${trimmedSpeechText}`
          : prefilledSpeech
        : trimmedSpeechText;
      const hasVoiceOrPrefillInput = Boolean(combinedSpeechText);
      if (communicationMode && hasVoiceOrPrefillInput) {
        if (shouldSkipVoiceSend) {
          setIsUserSpeaking(false);
          return;
        }
        if (prefilledSpeech) {
          updatePendingPrefilledText("");
        }
        const tempFile = fileRef.current;
        const chatContext = getRecentChatHistory(messages);
        // Use a frame captured ~3s earlier if available
        const cached = pickFrameForSend(5000);
        if (cached) {
          const file = cached;
          const imageUrl = URL.createObjectURL(file);
          setImage(file);
          setImagePreview(imageUrl);
          // Wait for state to update before sending message
          setTimeout(async () => {
            const newUserMessage = {
              type: "user",
              text: combinedSpeechText || `Sent an image: ${file.name}`,
              timestamp: Date.now(),
              image: imageUrl,
              imageName: file.name,
            };
            if (tempFile) {
              newUserMessage.fileName = tempFile.name;
              newUserMessage.fileType = tempFile.type;
            }
            setFile(null);
            setVoice(null);
            setMessagesWithCleanup((prev) => [...prev, newUserMessage]);
            setShowIntro(false);
            setInputText("");
            inputTextRef.current = "";
            setIsProcessing(true);
            startAwaitingResponse();
            setImage(null);
            setImagePreview(null);
            let responseText =
              "This is a step-by-step solution based on your input.";
            let rateLimited = false;
            try {
              const formData = new FormData();
              formData.append("image", file);
              formData.append("query", newUserMessage.text.trim() || "");
              formData.append("mode", activeButton);
              formData.append("chatHistory", JSON.stringify(chatContext));
              formData.append("conversationMode", communicationMode);
              formData.append("prompt", null);
              formData.append("board", userData?.board || "");
              formData.append("grade", userData?.grade || "");
              const controller = beginNewRequest();
              let res;
              try {
                res = await fetch(`${baseUrl}/doubt/solve-image-text`, {
                  method: "POST",
                  body: formData,
                  signal: controller.signal,
                });
              } finally {
                clearActiveRequest(controller);
              }
              const previewRateLimitMessage =
                await extractRateLimitMessageFromResponse(res);
              if (previewRateLimitMessage) {
                toast.warn(previewRateLimitMessage);
                setIsProcessing(false);
                setLoadingMessage("");
                setMessagesWithCleanup((prev) => [
                  ...prev,
                  {
                    type: "bot",
                    text: previewRateLimitMessage,
                    timestamp: Date.now(),
                    contextual: true,
                  },
                ]);
                resetVoiceStateAfterModelFailure();
                showRateLimitModal(previewRateLimitMessage);
                rateLimited = true;
                return;
              }
              const data = await parseJsonWithSuccessLog(
                res,
                "/doubt/solve-image-text",
                {
                  context: "image-preview-flow",
                  mode: activeButton,
                  conversationMode: Boolean(communicationMode),
                  hasImage: true,
                }
              );
              responseText = data?.solution || "Sorry, no explanation found.";
              if (communicationMode && data?.audio_base64) {
                playBase64Audio(data.audio_base64);
              } else if (communicationMode) {
                setIsSystemSpeaking(true);
                const utterance = new window.SpeechSynthesisUtterance(
                  responseText
                );
                logSpeechSynthesisUsage("image-preview-flow", {
                  responseText,
                  conversationMode: Boolean(communicationMode),
                  activeMode: activeButton,
                  hasAudioBase64: Boolean(data?.audio_base64),
                  liveAssistance: Boolean(liveAssistance),
                  reason: "fallback-to-speech-synthesis",
                });
                utterance.onend = () => {
                  setIsSystemSpeaking(false);
                  if (communicationMode) {
                    setIsUserSpeaking(true);
                    startUserRecognition();
                  }
                };
                window.speechSynthesis.speak(utterance);
              }
              // Add system (bot) message to chat in live assistance mode
              if (!rateLimited) {
                const formattedResponse = formatAsSteps(responseText);
                addBotMessageStreaming(formattedResponse);
              }
            } catch (err) {
              if (isAbortError(err)) {
                return;
              }
              const errorMessage = handleModelApiError(
                err,
                "image-preview-flow::solveRequest | L4524",
                {
                  hasImage: true,
                  activeMode: activeButton,
                  liveAssistance: Boolean(liveAssistance),
                  speechCharacters: combinedSpeechText?.length ?? 0,
                }
              );
              responseText = errorMessage;
              appendBotMessageOnce(errorMessage);
              resetVoiceStateAfterModelFailure();
            } finally {
              setIsProcessing(false);
              setLoadingMessage("");
              finishAwaitingResponse();
            }
          }, 0);
          // Rolling cache prunes; no explicit clear
          return;
        }
        // Non-liveAssistance fallback (original logic)
        const tempImage = imageRef.current;
        const tempImagePreview = imagePreviewRef.current;
        const newUserMessage = {
          type: "user",
          text:
            combinedSpeechText ||
            (tempImage
              ? `Sent an image: ${tempImage.name}`
              : tempFile
              ? `Sent a document: ${tempFile.name}`
              : "Sent media input"),
          timestamp: Date.now(),
        };
        if (tempImage) {
          newUserMessage.image = tempImagePreview;
          newUserMessage.imageName = tempImage.name;
        }
        if (tempFile) {
          newUserMessage.fileName = tempFile.name;
          newUserMessage.fileType = tempFile.type;
        }
        setImage(null);
        setImagePreview(null);
        setFile(null);
        setVoice(null);
        setMessagesWithCleanup((prev) => [...prev, newUserMessage]);
        setShowIntro(false);
        setInputText("");
        inputTextRef.current = "";
        setIsProcessing(true);
        startAwaitingResponse();
        let responseText =
          "This is a step-by-step solution based on your input.";
        let rateLimited = false;
        let requestAborted = false;
        let botAdded = false; // Track whether we've appended a bot message in this flow
        try {
          if (tempImage) {
            const formData = new FormData();
            formData.append("image", tempImage);
            formData.append("query", newUserMessage.text.trim() || "");
            formData.append("mode", activeButton);
            formData.append("chatHistory", JSON.stringify(chatContext));
            formData.append("conversationMode", communicationMode);
            formData.append("prompt", null);
            formData.append("board", userData?.board || "");
            formData.append("grade", userData?.grade || "");
            const controller = beginNewRequest();
            let res;
            try {
              res = await fetch(`${baseUrl}/doubt/solve-image-text`, {
                method: "POST",
                body: formData,
                signal: controller.signal,
              });
            } finally {
              clearActiveRequest(controller);
            }
            const conversationImageRateLimitMessage =
              await extractRateLimitMessageFromResponse(res);
            if (conversationImageRateLimitMessage) {
              toast.warn(conversationImageRateLimitMessage);
              setIsProcessing(false);
              setLoadingMessage("");
              appendBotMessageOnce(conversationImageRateLimitMessage);
              resetVoiceStateAfterModelFailure();
              showRateLimitModal(conversationImageRateLimitMessage);
              rateLimited = true;
              return;
            }
            const data = await parseJsonWithSuccessLog(
              res,
              "/doubt/solve-image-text",
              {
                context: "conversation-image",
                mode: activeButton,
                conversationMode: Boolean(communicationMode),
                hasImage: true,
              }
            );
            responseText = data?.solution || "Sorry, no explanation found.";
            if (communicationMode && data?.audio_base64) {
              playBase64Audio(data.audio_base64);
            } else if (communicationMode) {
              setIsSystemSpeaking(true);
              const utterance = new window.SpeechSynthesisUtterance(
                responseText
              );
              logSpeechSynthesisUsage("conversation-image", {
                responseText,
                conversationMode: Boolean(communicationMode),
                activeMode: activeButton,
                hasAudioBase64: Boolean(data?.audio_base64),
                liveAssistance: Boolean(liveAssistance),
                reason: "fallback-to-speech-synthesis",
              });
              utterance.onend = () => {
                setIsSystemSpeaking(false);
                if (communicationMode) {
                  setIsUserSpeaking(true);
                  startUserRecognition();
                }
              };
              window.speechSynthesis.speak(utterance);
            }
            // Add system (bot) message to chat in live assistance mode
            if (!rateLimited) {
              const formattedResponse = formatAsSteps(responseText);
              addBotMessageStreaming(formattedResponse);
              botAdded = true;
            }
          } else if (tempFile) {
            const formData = new FormData();
            formData.append("file", tempFile);
            formData.append("query", newUserMessage.text.trim() || "");
            formData.append("mode", activeButton);
            formData.append("chatHistory", JSON.stringify(chatContext));
            formData.append("conversationMode", communicationMode);
            formData.append("prompt", null);
            const controller = beginNewRequest();
            let res;
            try {
              res = await fetch(`${baseUrl}/doubt/upload-file`, {
                method: "POST",
                body: formData,
                signal: controller.signal,
              });
            } finally {
              clearActiveRequest(controller);
            }
            if (res.status === 429) {
              let errorMsg =
                "You have reached the maximum number of requests allowed. Please wait before trying again.";
              try {
                const errData = await res.json();
                if (errData && errData.error) {
                  errorMsg = errData.error;
                  if (errData.detail) {
                    errorMsg += ` (${errData.detail})`;
                  }
                } else if (errData && errData.detail) {
                  errorMsg += ` (${errData.detail})`;
                }
              } catch {}
              toast.warn(errorMsg);
              setIsProcessing(false);
              setLoadingMessage("");
              appendBotMessageOnce(errorMsg);
              resetVoiceStateAfterModelFailure();
              rateLimited = true;
              return;
            }
            const data = await parseJsonWithSuccessLog(
              res,
              "/doubt/upload-file",
              {
                context: "conversation-file",
                mode: activeButton,
                conversationMode: Boolean(communicationMode),
                hasFile: Boolean(tempFile),
              }
            );
            responseText = data?.solution || "Sorry, no explanation found.";
            if (communicationMode && data?.audio_base64) {
              playBase64Audio(data.audio_base64);
            } else if (communicationMode) {
              setIsSystemSpeaking(true);
              const utterance = new window.SpeechSynthesisUtterance(
                responseText
              );
              logSpeechSynthesisUsage("conversation-file", {
                responseText,
                conversationMode: Boolean(communicationMode),
                activeMode: activeButton,
                hasAudioBase64: Boolean(data?.audio_base64),
                liveAssistance: Boolean(liveAssistance),
                reason: "fallback-to-speech-synthesis",
              });
              utterance.onend = () => {
                setIsSystemSpeaking(false);
                if (communicationMode) {
                  setIsUserSpeaking(true);
                  startUserRecognition();
                }
              };
              window.speechSynthesis.speak(utterance);
            }
            // Add system (bot) message to chat in live assistance mode
            if (!rateLimited) {
              const formattedResponse = formatAsSteps(responseText);
              appendBotMessageOnce(formattedResponse);
              botAdded = true;
            }
          } else {
            const controller = beginNewRequest();
            let res;
            const shouldTeachTopic = isTeachMeWorkbookSource;
            const textEndpoint = shouldTeachTopic
              ? "/doubt/teach-topic"
              : "/doubt/solve-text";
            const textPayload = {
              text: newUserMessage.text,
              mode: activeButton,
              chatHistory: chatContext,
              conversationMode: communicationMode,
              prompt: null,
              board: userData?.board,
              grade: userData?.grade,
              ...(shouldTeachTopic && {
                subject: queryWorkbookSubject,
                chapter: queryWorkbookChapter,
              }),
            };
            try {
              res = await fetch(`${baseUrl}${textEndpoint}`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(textPayload),
                signal: controller.signal,
              });
            } finally {
              clearActiveRequest(controller);
            }
            const textRateLimitMessage =
              await extractRateLimitMessageFromResponse(res);
            if (textRateLimitMessage) {
              toast.warn(textRateLimitMessage);
              setIsProcessing(false);
              setLoadingMessage("");
              appendBotMessageOnce(textRateLimitMessage);
              resetVoiceStateAfterModelFailure();
              showRateLimitModal(textRateLimitMessage);
              rateLimited = true;
              return;
            }
            const data = await parseJsonWithSuccessLog(res, textEndpoint, {
              context: "conversation-text",
              mode: activeButton,
              conversationMode: Boolean(communicationMode),
              textLength: newUserMessage.text?.length ?? 0,
            });
            responseText = data?.solution || "Sorry, no explanation found.";
            if ((communicationMode || liveAssistance) && data?.audio_base64) {
              playBase64Audio(data.audio_base64);
            } else if (communicationMode || liveAssistance) {
              setIsSystemSpeaking(true);
              const utterance = new window.SpeechSynthesisUtterance(
                responseText
              );
              logSpeechSynthesisUsage("conversation-text", {
                responseText,
                conversationMode: Boolean(communicationMode),
                activeMode: activeButton,
                hasAudioBase64: Boolean(data?.audio_base64),
                liveAssistance: Boolean(liveAssistance),
                reason: "fallback-to-speech-synthesis",
              });
              utterance.onend = () => {
                setIsSystemSpeaking(false);
                if (communicationMode) {
                  setIsUserSpeaking(true);
                  startUserRecognition();
                }
              };
              window.speechSynthesis.speak(utterance);
            }
            // Add system (bot) message to chat in live assistance mode
            if (!rateLimited) {
              const formattedResponse = formatAsSteps(responseText);
              addBotMessageStreaming(formattedResponse);
              botAdded = true;
            }
          }
        } catch (err) {
          if (isAbortError(err)) {
            requestAborted = true;
            resetVoiceStateAfterModelFailure();
            return;
          }
          responseText = handleModelApiError(
            err,
            "conversation-fallback::apiCalls | L4799",
            {
              hasImage: Boolean(tempImage),
              hasFile: Boolean(tempFile),
              activeMode: activeButton,
              liveAssistance: Boolean(liveAssistance),
              textLength: newUserMessage.text?.length ?? 0,
            }
          );
          resetVoiceStateAfterModelFailure();
        } finally {
          if (tempImagePreview) {
            URL.revokeObjectURL(tempImagePreview);
          }
          if (!rateLimited && !botAdded && !requestAborted) {
            const formattedResponse = formatAsSteps(responseText);
            addBotMessageStreaming(formattedResponse);
            botAdded = true;
          } else if (rateLimited) {
            // If rate limited, set voice to resume listening state
            if (communicationMode) {
              setIsUserSpeaking(false);
              setIsVoicePaused(true); // Show Resume Listening button
            }
          }
          setIsProcessing(false);
          setLoadingMessage("");
          finishAwaitingResponse();
        }
      } else if (communicationMode && !trimmedSpeechText) {
        // No speech detected, browser stopped listening (inactivity) and system is not speaking
        setIsUserSpeaking(false);
        setIsVoicePaused(true); // Show Resume Listening button

        // If camera modal is open, start 5-second timer for automatic screenshot
        if (showCameraModal && liveAssistance) {
          console.log(
            "[Silence Timer] Starting 5-second timer for auto-capture"
          );
          setSilenceTimerActive(true);
          setSilenceTimerCount(5);
        }
      } else {
        setIsUserSpeaking(false);
        setIsVoicePaused(false);
      }
    };
    return () => {
      recognition.stop();
      setIsUserSpeaking(false);
    };
  }
  useEffect(() => {
    if (!communicationMode && isVoicePaused) {
      setIsVoicePaused(false);
    }
  }, [communicationMode, isVoicePaused]);
  useEffect(() => {
    return () => {
      if (lastAudioUrlRef.current) {
        URL.revokeObjectURL(lastAudioUrlRef.current);
        lastAudioUrlRef.current = null;
      }
    };
  }, []);
  // At the top, ensure you have these refs
  const timeoutRef = useRef();
  const recognitionRef = useRef();
  // Add this cleanup useEffect near the top-level of the component
  useEffect(() => {
    return () => {
      // Audio cleanup
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      // Speech synthesis cleanup
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      // Timeout cleanup
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Speech recognition cleanup
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
        recognitionRef.current = null;
      }
      // Image preview cleanup
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      // Message image blob cleanup
      messages.forEach((msg) => {
        if (
          msg.image &&
          typeof msg.image === "string" &&
          msg.image.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(msg.image);
          } catch (e) {}
        }
        if (
          msg.questionImage &&
          typeof msg.questionImage === "string" &&
          msg.questionImage.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(msg.questionImage);
          } catch (e) {}
        }
      });
      // Workbook cleanup
      if (workbookFabricCanvas) {
        try {
          workbookFabricCanvas.clear();
          workbookFabricCanvas.dispose();
        } catch (e) {}
      }
      if (workbookFabricCanvasRef.current) {
        try {
          if (workbookFabricCanvasRef.current.dispose) {
            workbookFabricCanvasRef.current.dispose();
          }
        } catch (e) {}
      }
      if (workbookRenderTaskRef.current) {
        try {
          workbookRenderTaskRef.current.cancel();
        } catch (e) {}
      }
      if (workbookPdfUrl && workbookPdfUrl.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(workbookPdfUrl);
        } catch (e) {}
      }
    };
  }, []);
  useEffect(() => {
    // Whenever audioUrl changes, always revoke the previous one
    return () => {
      if (lastAudioUrlRef.current) {
        URL.revokeObjectURL(lastAudioUrlRef.current);
        lastAudioUrlRef.current = null;
      }
    };
  }, [audioUrl]);
  // After messages and chatEndRef are defined:
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);
  // Clear chat when switching between workbook mode and chat modes (homework/stepbystep)
  useEffect(() => {
    const modeKey = `${workbookMode ? "workbook" : "chat"}|${activeButton}`;
    if (!modeInitRef.current) {
      modeInitRef.current = true;
      lastChatModeRef.current = modeKey;
      return;
    }
    if (lastChatModeRef.current !== modeKey) {
      lastChatModeRef.current = modeKey;
      cancelActiveRequest();
      setIsProcessing(false);
      setLoadingMessage("");
      if (messages && messages.length > 0) {
        handleClearChat(true);
      }
    }
  }, [workbookMode, activeButton, messages.length, cancelActiveRequest]);
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);
  // Initialize fabric.js canvas for workbook
  useEffect(() => {
    if (
      !workbookCanvasRef.current ||
      !workbookPdfDims.width ||
      !workbookPdfDims.height ||
      !workbookIsCanvasReady
    ) {
      return;
    }
    console.log(
      "Initializing workbook fabric canvas with dimensions:",
      workbookPdfDims
    );
    // Remove any previous fabric canvas
    if (
      workbookFabricCanvasRef.current &&
      workbookFabricCanvasRef.current.__canvas
    ) {
      workbookFabricCanvasRef.current.__canvas.parentNode.removeChild(
        workbookFabricCanvasRef.current.__canvas
      );
    }
    // Create a new fabric canvas overlay
    const fabricCanvasEl = document.createElement("canvas");
    // Use display dimensions for fabric canvas (logical pixels)
    fabricCanvasEl.width = workbookPdfDims.width;
    fabricCanvasEl.height = workbookPdfDims.height;
    fabricCanvasEl.style.position = "absolute";
    fabricCanvasEl.style.left = "0";
    fabricCanvasEl.style.top = "0";
    fabricCanvasEl.style.pointerEvents = workbookMarkerActive ? "auto" : "none";
    fabricCanvasEl.style.zIndex = "2";
    fabricCanvasEl.style.width = `${workbookPdfDims.width}px`;
    fabricCanvasEl.style.height = `${workbookPdfDims.height}px`;
    // Ensure the parent container exists
    if (!workbookCanvasRef.current.parentNode) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | initWorkbookFabricCanvas::parentMissing | L4838`,
        new Error("Workbook PDF canvas parent node not found"),
        {
          workbookPdfDims,
          workbookIsCanvasReady,
        }
      );
      console.error("Workbook PDF canvas parent node not found");
      return;
    }
    workbookCanvasRef.current.parentNode.appendChild(fabricCanvasEl);
    try {
      const fCanvas = new fabric.Canvas(fabricCanvasEl, {
        selection: false,
        preserveObjectStacking: true,
      });
      console.log("Workbook fabric canvas created successfully");
      setWorkbookFabricCanvas(fCanvas);
      workbookFabricCanvasRef.current = fCanvas;
    } catch (error) {
      captureSentryError(
        `${DOUBT_SOLVER_FILE} | initWorkbookFabricCanvas | L4854`,
        error,
        {
          workbookPdfDims,
          workbookMarkerActive,
        }
      );
      console.error("Error creating workbook fabric canvas:", error);
      // Remove the canvas element if fabric creation failed
      if (fabricCanvasEl.parentNode) {
        fabricCanvasEl.parentNode.removeChild(fabricCanvasEl);
      }
      return;
    }
    // Clean up on unmount
    return () => {
      console.log("Cleaning up workbook fabric canvas");
      if (
        workbookFabricCanvasRef.current &&
        workbookFabricCanvasRef.current.dispose
      ) {
        try {
          workbookFabricCanvasRef.current.dispose();
        } catch (error) {
          console.log("Error disposing fabric canvas:", error);
        }
      }
      if (fabricCanvasEl && fabricCanvasEl.parentNode) {
        try {
          fabricCanvasEl.parentNode.removeChild(fabricCanvasEl);
        } catch (error) {
          console.log("Error removing canvas element:", error);
        }
      }
    };
  }, [
    workbookPdfDims.width,
    workbookPdfDims.height,
    workbookMarkerActive,
    workbookIsCanvasReady,
    workbookZoomLevel,
  ]);
  // Workbook marker tool logic
  useEffect(() => {
    if (!workbookFabricCanvas) {
      return;
    }
    let rect, isDown, origX, origY;
    const onMouseDown = (o) => {
      if (!workbookMarkerActive) {
        return;
      }
      // Disable selection while pinch gesture is active on mobile
      if (isMobile && isPinchingRef.current) {
        return;
      }
      console.log("mousedown", o);
      isDown = true;
      const pointer = workbookFabricCanvas.getPointer(o.e);
      origX = pointer.x;
      origY = pointer.y;
      // Remove all existing rectangles before creating a new one
      const allRects = workbookFabricCanvas.getObjects("rect");
      allRects.forEach((obj) => workbookFabricCanvas.remove(obj));
      rect = new fabric.Rect({
        left: origX,
        top: origY,
        originX: "left",
        originY: "top",
        width: 0,
        height: 0,
        angle: 0,
        fill: "rgba(0,0,255,0.1)",
        stroke: "blue",
        strokeWidth: 2,
        transparentCorners: false,
        hasRotatingPoint: false,
      });
      workbookFabricCanvas.add(rect);
      workbookFabricCanvas.setActiveObject(rect);
    };
    const onMouseMove = (o) => {
      console.log("mousemove", o);
      if (!isDown || !rect) return;
      const pointer = workbookFabricCanvas.getPointer(o.e);
      if (origX > pointer.x) {
        rect.set({ left: pointer.x });
      }
      if (origY > pointer.y) {
        rect.set({ top: pointer.y });
      }
      rect.set({ width: Math.abs(origX - pointer.x) });
      rect.set({ height: Math.abs(origY - pointer.y) });
      workbookFabricCanvas.renderAll();
    };
    const onMouseUp = () => {
      if (isDown && rect) {
        // Clear chat when a new area (problem) is selected
        // Don't clear if teachMe flow is active
        if (
          messagesRef.current &&
          messagesRef.current.length > 0 &&
          !teachMePromptSentRef.current
        ) {
          handleClearChat(true);
        }
        resetWorkbookEvaluationState();
        setWorkbookSelectedRect(rect);
        // Generate question image from the selected area
        createQuestionImageFromSelection(rect).then((questionImage) => {
          if (questionImage) {
            setWorkbookQuestionImage(questionImage);
            console.log("Question image created:", questionImage.name);
          }
        });
      }
      isDown = false;
    };
    const onObjectSelected = (e) => {
      // Clear chat when a new area (problem) is selected
      // Don't clear if teachMe flow is active
      if (
        messagesRef.current &&
        messagesRef.current.length > 0 &&
        !teachMePromptSentRef.current
      ) {
        handleClearChat(true);
      }
      resetWorkbookEvaluationState();
      setWorkbookSelectedRect(e.target);
      // Generate question image from the selected area
      createQuestionImageFromSelection(e.target).then((questionImage) => {
        if (questionImage) {
          setWorkbookQuestionImage(questionImage);
          console.log(
            "Question image created from object selection:",
            questionImage.name
          );
        }
      });
    };
    workbookFabricCanvas.on("mouse:down", onMouseDown);
    workbookFabricCanvas.on("mouse:move", onMouseMove);
    workbookFabricCanvas.on("mouse:up", onMouseUp);
    workbookFabricCanvas.on("object:selected", onObjectSelected);
    return () => {
      workbookFabricCanvas.off("mouse:down", onMouseDown);
      workbookFabricCanvas.off("mouse:move", onMouseMove);
      workbookFabricCanvas.off("mouse:up", onMouseUp);
      workbookFabricCanvas.off("object:selected", onObjectSelected);
    };
  }, [
    workbookFabricCanvas,
    workbookMarkerActive,
    resetWorkbookEvaluationState,
  ]);
  // Trigger workbook PDF rendering when document, page, or zoom changes
  useEffect(() => {
    let mounted = true;
    const renderPage = async () => {
      if (!workbookPdfDocument || !workbookCanvasRef.current) {
        console.log("PDF rendering skipped:", {
          hasPdfDocument: !!workbookPdfDocument,
          hasCanvas: !!workbookCanvasRef.current,
        });
        return;
      }
      console.log(
        "Starting workbook PDF render for page:",
        workbookCurrentPage
      );
      setIsWorkbookLoading(true);
      setWorkbookIsCanvasReady(false);
      // Cancel previous render task if it exists
      if (workbookRenderTaskRef.current) {
        workbookRenderTaskRef.current.cancel();
        console.log("Previous workbook render task cancelled");
      }
      try {
        const page = await workbookPdfDocument.getPage(workbookCurrentPage);
        // Get device pixel ratio for high-DPI displays
        const dpr = window.devicePixelRatio || 1;
        // Calculate the actual scale needed for high quality rendering
        // Use the zoom level directly for proper scaling
        const baseScale = workbookZoomLevel;
        const renderScale = baseScale * dpr;
        const viewport = page.getViewport({ scale: renderScale });
        const canvas = workbookCanvasRef.current;
        if (!canvas) {
          console.log("Canvas became null during rendering");
          return;
        }
        console.log("Rendering workbook page with viewport:", {
          width: viewport.width,
          height: viewport.height,
          zoomLevel: workbookZoomLevel,
          renderScale: renderScale,
          dpr: dpr,
        });
        // Set canvas size in device pixels (high resolution)
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        // Set CSS size in logical pixels (display size)
        canvas.style.width = `${viewport.width / dpr}px`;
        canvas.style.height = `${viewport.height / dpr}px`;
        if (mounted) {
          // Store both actual and display dimensions
          setWorkbookPdfDims({
            width: viewport.width / dpr,
            height: viewport.height / dpr,
            actualWidth: viewport.width,
            actualHeight: viewport.height,
            dpr: dpr,
            renderScale: renderScale,
          });
        }
        const context = canvas.getContext("2d");
        // Enable high-quality rendering settings
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = "high";
        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          enableWebGL: false, // Use 2D context for better quality
          renderInteractiveForms: false, // Disable for better performance
        };
        workbookRenderTaskRef.current = page.render(renderContext);
        await workbookRenderTaskRef.current.promise;
        if (mounted) {
          console.log("Workbook PDF page rendered successfully");
          setWorkbookIsCanvasReady(true);
        }
      } catch (error) {
        if (error.name === "RenderingCancelledException") {
          console.log("Workbook render was cancelled");
        } else {
          captureSentryError(
            `${DOUBT_SOLVER_FILE} | renderWorkbookPage | L5088`,
            error,
            {
              page: workbookCurrentPage,
              zoomLevel: workbookZoomLevel,
              renderScale,
              dpr,
            }
          );
          console.error("Error rendering workbook PDF page:", error);
        }
        if (mounted) {
          setWorkbookIsCanvasReady(false);
        }
      } finally {
        if (mounted) {
          setIsWorkbookLoading(false);
          workbookRenderTaskRef.current = null;
        }
      }
    };
    renderPage();
    return () => {
      mounted = false;
      if (workbookRenderTaskRef.current) {
        workbookRenderTaskRef.current.cancel();
        workbookRenderTaskRef.current = null;
        console.log("Workbook render task cancelled on dependency change");
      }
    };
  }, [workbookPdfDocument, workbookCurrentPage, workbookZoomLevel]);
  // Reset workbook selects/options when entering workbook mode or on refresh while in workbook
  useEffect(() => {
    console.log("reset workmode");
    if (workbookMode) {
      setWorkbookBoard("");
      setWorkbookSubject("");
      setWorkbookChapter("");
      setWorkbookSubjects([]);
      setWorkbookChapters([]);
      setWorkbookPdfUrl("");
      setWorkbookPdfDocument(null);
      setWorkbookCurrentPage(1);
      setWorkbookNumPages(0);
      setWorkbookResetKey((k) => k + 1);
    }
  }, [workbookMode]);

  // Reset all workbook-related local state when workbook mode is turned off from context
  useEffect(() => {
    if (!workbookMode) {
      // Reset all workbook-related state to default values when workbook mode is disabled
      setWorkbookBoard("");
      setWorkbookSubject("");
      setWorkbookChapter("");
      setWorkbookPdfUrl("");
      setWorkbookSyllabusData([]);
      setWorkbookSubjects([]);
      setWorkbookChapters([]);
      setIsWorkbookLoading(false);
      setWorkbookPdfDocument(null);
      setWorkbookCurrentPage(1);
      setWorkbookNumPages(0);
      setWorkbookZoomLevel(1.0);
      setWorkbookResetKey(0);
      setWorkbookEvaluateResetKey(0);
      setShowWorkbookControls(true);
      setWorkbookFabricCanvas(null);
      setWorkbookSelectedRect(null);
      setWorkbookInputText("");
      setWorkbookMarkerActive(true);
      setWorkbookPdfDims({ width: 800, height: 600 });
      setWorkbookIsCanvasReady(false);
      setWorkbookQuestionImage(null);
      setShowWorkbookGuideModal(false);
    }
  }, [workbookMode]);
  const workbookBoardBubbleOptions = [
    {
      value: "CBSE",
      label: "NCERT",
    },
  ];
  const workbookSubjectBubbleOptions = workbookSubjects.map((subject) => ({
    value: subject,
    label: subject,
  }));
  const workbookChapterBubbleOptions = workbookChapters
    .filter((chapter) => chapter.docId)
    .map((chapter) => ({
      value: chapter.name,
      label: chapter.name,
    }));
  const workbookSelectionSteps = [
    {
      id: "workbook-book",
      title: "Select Book",
      subtitle: "Select one",
      icon: <FaBookOpen className="text-lg" />,
      options: workbookBoardBubbleOptions,
      selectedValue: workbookBoard,
      onSelect: handleWorkbookBoardChange,
      emptyText: "No books available yet",
    },
    {
      id: "workbook-subject",
      title: "Select Subject",
      subtitle: "Select one",
      icon: <FaFolderOpen className="text-lg" />,
      options: workbookSubjectBubbleOptions,
      selectedValue: workbookSubject,
      onSelect: handleWorkbookSubjectChange,
      disabled: workbookSubjects.length === 0,
      emptyText: workbookBoard
        ? "No subjects available for this book"
        : "Select a book to load subjects",
    },
    {
      id: "workbook-chapter",
      title: "Select Chapter",
      subtitle: "Select one",
      icon: <FaListUl className="text-lg" />,
      options: workbookChapterBubbleOptions,
      selectedValue: workbookChapter,
      onSelect: handleWorkbookChapterChange,
      disabled: workbookChapters.length === 0,
      emptyText: workbookSubject
        ? "No chapters available for this subject"
        : "Select a subject to load chapters",
    },
  ];
  const renderWorkbookSelectionPanel = (
    className = "grid grid-cols-1 gap-4 mt-3"
  ) => (
    <div className={className}>
      <WorkbookSelectionTimeline steps={workbookSelectionSteps} />
    </div>
  );
  const showProcessingIndicator = isProcessing && isAwaitingResponse;
  return (
    <MathJaxContext version={3} config={mathJaxConfig}>
      <div className="h-full flex flex-col bg-red">
        {/* Workbook Guide Modal */}
        {showWorkbookGuideModal && (
          <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-xl w-[92%] max-w-md p-4 relative">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-indigo-600">
                  Workbook Guide
                </h2>
                <button
                  aria-label="Close workbook guide"
                  className="text-indigo-600 hover:text-indigo-700"
                  onClick={() => {
                    setShowWorkbookGuideModal(false);
                    setWorkbookGuideShown(true); // Mark as shown when closed
                  }}
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="flex justify-center">
                <img
                  src="/NotebookGuide.gif"
                  alt="Workbook Guide"
                  className="max-h-[60vh] object-contain rounded-md"
                />
              </div>
            </div>
          </div>
        )}
        <RateLimitModal
          isOpen={isRateLimitModalOpen}
          serverMessage={rateLimitServerMessage}
          supportMessage={RATE_LIMIT_MODAL_SUPPORT_MESSAGE}
          onClose={handleRateLimitModalClose}
        />
        {/* Workbook Mode */}
        {workbookMode ? (
          <div className="flex flex-col h-screen min-h-0">
            {/* Top Bar */}

            {/* Split Screen Content (desktop); Mobile shows only the PDF viewer */}
            <div
              className="flex-col flex-1 overflow-hidden h-full min-h-0"
              ref={resizeRef}
            >
              {/* Main Content Area */}
              <div
                className={`flex flex-1 overflow-hidden min-h-0 ${
                  isMobile ? "flex-col" : ""
                } h-full`}
              >
                {/* Left Panel - PDF Viewer */}
                <div
                  className={`flex flex-col bg-gray-50 ${
                    isMobile ? "" : "border-r border-gray-200"
                  } h-full min-h-0 min-w-0 ${isMobile ? "" : "flex-shrink-0"}`}
                  style={{
                    width:
                      isMobile || !showLeftPanel
                        ? "100%"
                        : `${leftPanelWidth}%`,
                  }}
                >
                  <div className="flex-shrink-0 w-full">
                    <div
                      className={`bg-white border-b border-gray-200 ${
                        isMobile ? "px-0" : "px-3"
                      } py-2 flex flex-row`}
                    >
                      <div className="flex flex-wrap items-start gap-2 sm:items-center sm:gap-3 w-1/10">
                        {/* Close button */}
                        {!workbookPdfDocument &&
                          !workbookParamAppliedRef.current && (
                            <button
                              onClick={handleCloseWorkbook}
                              className="text-gray-600 hover:text-gray-800 p-2 -ml-2 self-start flex-shrink-0"
                              aria-label="Close workbook"
                            >
                              <FaTimes size={20} />
                            </button>
                          )}
                        {!workbookPdfDocument &&
                          workbookParamAppliedRef.current && (
                            <button
                              onClick={() => {
                                handleCloseWorkbook();
                                handleWorkbookCloseNavigation();
                              }}
                              className="text-gray-600 hover:text-gray-800 p-2 -ml-2 self-start flex-shrink-0"
                              aria-label="Close workbook"
                            >
                              <FaTimes size={20} />
                            </button>
                          )}
                        {/* Back Button */}
                        {workbookPdfDocument && (
                          <button
                            onClick={handleWorkbookBackClick}
                            className="text-gray-600 hover:text-gray-800 p-2 -ml-2 self-start flex-shrink-0"
                            aria-label="Go Back"
                          >
                            <FaChevronLeft size={20} />
                          </button>
                        )}
                        {/* Controls: wrap on small screens, aligned with consistent sizing */}
                        {/* {workbookPdfDocument && (
                      <div key={`hdr-${workbookResetKey}`} className="flex-1">
                        {isMobile ? (
                          <>
                            <button
                              onClick={() =>
                                setShowWorkbookControls((prev) => !prev)
                              }
                              className="flex items-center justify-between w-full h-10 px-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-full transition-colors text-sm"
                            >
                              <span>Workbook Controls</span>
                              <span className="text-xs">
                                {showWorkbookControls ? "▲" : "▼"}
                              </span>
                            </button>
                            {showWorkbookControls && (
                              <div className="mt-2 grid grid-cols-1 gap-3 sm:gap-4">
                                <select
                                  value={workbookBoard}
                                  onChange={(e) =>
                                    handleWorkbookBoardChange(e.target.value)
                                  }
                                  className="w-full h-10 min-w-0 px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                >
                                  <option value="">Select Book</option>
                                  <option value="CBSE">NCERT</option>
                                  <option value="VMC">VMC</option>
                                </select>
                                <select
                                  value={workbookSubject}
                                  onChange={(e) =>
                                    handleWorkbookSubjectChange(e.target.value)
                                  }
                                  className="w-full h-10 min-w-0 px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                  disabled={
                                    workbookSubjects.length === 0 ||
                                    isSyllabusLoading
                                  }
                                >
                                  <option value="">Select Subject</option>
                                  {workbookSubjects.map((subject) => (
                                    <option key={subject} value={subject}>
                                      {subject}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={workbookChapter}
                                  onChange={(e) =>
                                    handleWorkbookChapterChange(e.target.value)
                                  }
                                  className="w-full h-10 min-w-0 px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                  disabled={
                                    workbookChapters.length === 0 ||
                                    isSyllabusLoading
                                  }
                                >
                                  <option value="">Select Chapter</option>
                                  {workbookChapters
                                    .filter((chapter) => chapter.workBookUrl)
                                    .map((chapter) => (
                                      <option
                                        key={chapter.name}
                                        value={chapter.name}
                                      >
                                        {chapter.name}
                                      </option>
                                    ))}
                                </select>
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={handleSolveWorkbookClick}
                                    disabled={
                                      isSyllabusLoading ||
                                      !workbookBoard ||
                                      !workbookSubject ||
                                      !workbookChapter
                                    }
                                    className="w-full h-10 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full transition-colors text-sm whitespace-nowrap"
                                  >
                                    Get Workbook
                                  </button>
                                </div>
                                <button
                                  onClick={handleUploadWorksheetClick}
                                  className="w-full md:w-auto h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full transition-colors text-sm whitespace-nowrap md:border-l md:border-gray-200 md:pl-5"
                                >
                                  Upload Worksheet
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2 w-full">
                            <select
                              value={workbookBoard}
                              onChange={(e) =>
                                handleWorkbookBoardChange(e.target.value)
                              }
                              className="flex-1 h-10 min-w-[160px] px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                              <option value="">Select Book</option>
                              <option value="CBSE">NCERT</option>
                              <option value="VMC">VMC</option>
                            </select>
                            <select
                              value={workbookSubject}
                              onChange={(e) =>
                                handleWorkbookSubjectChange(e.target.value)
                              }
                              className="flex-1 h-10 min-w-[160px] px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={
                                workbookSubjects.length === 0 ||
                                isSyllabusLoading
                              }
                            >
                              <option value="">Select Subject</option>
                              {workbookSubjects.map((subject) => (
                                <option key={subject} value={subject}>
                                  {subject}
                                </option>
                              ))}
                            </select>
                            <select
                              value={workbookChapter}
                              onChange={(e) =>
                                handleWorkbookChapterChange(e.target.value)
                              }
                              className="flex-1 h-10 min-w-[200px] px-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={
                                workbookChapters.length === 0 ||
                                isSyllabusLoading
                              }
                            >
                              <option value="">Select Chapter</option>
                              {workbookChapters
                                .filter((chapter) => chapter.workBookUrl)
                                .map((chapter) => (
                                  <option
                                    key={chapter.name}
                                    value={chapter.name}
                                  >
                                    {chapter.name}
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={handleSolveWorkbookClick}
                              disabled={
                                isSyllabusLoading ||
                                !workbookBoard ||
                                !workbookSubject ||
                                !workbookChapter
                              }
                              className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full transition-colors text-sm whitespace-nowrap flex-shrink-0"
                            >
                              Get Workbook
                            </button>
                            <button
                              onClick={handleUploadWorksheetClick}
                              className="h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full transition-colors text-sm whitespace-nowrap flex-shrink-0"
                            >
                              Upload Worksheet
                            </button>
                          </div>
                        )}
                      </div>
                    )} */}

                        {/* New Chat button - desktop only, show only when chat has begun */}
                        {/* {!isMobile && hasActiveChat && (
                  <button
                    onClick={handleClearChat}
                    className="ml-auto bg-[#0D99FF] text-white py-2 px-4 rounded-full text-sm flex items-center gap-2 hover:bg-[#6B56E8] transition-colors flex-shrink-0"
                    aria-label="New Chat"
                  >
                    <FaEdit className="text-sm" />
                    New Chat
                  </button>
                )} */}
                      </div>
                      <div className="flex w-9/10 justify-center">
                        {!workbookPdfDocument &&
                          currentWorkbookTab !== "loadingPDF" &&
                          !isTeachMeWorkbookSource && (
                            <div className="flex flex-wrap justify-center gap-3 px-4 py-3 bg-white rounded-2xl">
                              {/* <button
                              onClick={() => {
                                setCurrentWorkbookTab("schoolWorkbook");
                              }}
                              className={`px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                                currentWorkbookTab === "schoolWorkbook"
                                  ? "bg-gradient-to-r from-[#0D99FF] to-[#9F7AEA] text-white shadow-lg shadow-[#0D99FF]/25"
                                  : "bg-white text-gray-600 hover:text-[#0D99FF] border border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
                              }`}
                            >
                              School Books
                            </button> */}
                              <button
                                onClick={() => {
                                  setCurrentWorkbookTab("myUploads");
                                }}
                                className={`px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                                  currentWorkbookTab === "myUploads"
                                    ? "text-white shadow-lg shadow-[#0D99FF]/25"
                                    : "bg-white text-gray-600 hover:text-[#0D99FF] border border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
                                }`}
                                style={
                                  currentWorkbookTab === "myUploads"
                                    ? { backgroundColor: "#0D99FF" }
                                    : {}
                                }
                              >
                                My Uploads
                              </button>
                              <button
                                onClick={() => {
                                  setCurrentWorkbookTab("pyq");
                                }}
                                className={`px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                                  currentWorkbookTab === "pyq"
                                    ? "text-white shadow-lg shadow-[#0D99FF]/25"
                                    : "bg-white text-gray-600 hover:text-[#0D99FF] border border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
                                }`}
                                style={
                                  currentWorkbookTab === "pyq"
                                    ? { backgroundColor: "#0D99FF" }
                                    : {}
                                }
                              >
                                P.Y.Q.
                              </button>
                              <button
                                onClick={() => {
                                  setCurrentWorkbookTab("samplePapers");
                                }}
                                className={`px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
                                  currentWorkbookTab === "samplePapers"
                                    ? "text-white shadow-lg shadow-[#0D99FF]/25"
                                    : "bg-white text-gray-600 hover:text-[#0D99FF] border border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
                                }`}
                                style={
                                  currentWorkbookTab === "samplePapers"
                                    ? { backgroundColor: "#0D99FF" }
                                    : {}
                                }
                              >
                                Sample Papers
                              </button>
                            </div>
                          )}

                        {/* PDF Controls */}

                        {workbookPdfDocument && (
                          <div
                            className={`flex items-center justify-between bg-white  border-gray-200 ${
                              isMobile ? "px-0 py-3" : ""
                            } w-full`}
                          >
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() =>
                                  setWorkbookZoomLevel((prev) =>
                                    Math.max(0.5, prev - 0.1)
                                  )
                                }
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                -
                              </button>
                              <span className="text-sm font-medium">
                                {Math.round(workbookZoomLevel * 100)}%
                              </span>
                              <button
                                onClick={() =>
                                  setWorkbookZoomLevel((prev) =>
                                    Math.min(3, prev + 0.1)
                                  )
                                }
                                className="p-1 text-gray-500 hover:text-gray-700"
                              >
                                +
                              </button>
                              {isMobile && (
                                <button
                                  onClick={() =>
                                    setHandToolEnabled((prev) => {
                                      const next = !prev;
                                      setWorkbookMarkerActive(!next);
                                      setWorkbookSelectedRect(null);
                                      return next;
                                    })
                                  }
                                  className={`p-1 rounded ${
                                    handToolEnabled
                                      ? "bg-indigo-100 text-indigo-600"
                                      : "text-gray-500 hover:text-gray-700"
                                  }`}
                                  title={
                                    handToolEnabled
                                      ? "Disable Pan/Zoom"
                                      : "Enable Pan/Zoom"
                                  }
                                  aria-label="Toggle Hand Tool"
                                >
                                  <FaHandPaper />
                                </button>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setShowWorkbookGuideModal(true)}
                                className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                              >
                                <FaInfoCircle size={20} />
                              </button>
                              <button
                                onClick={() =>
                                  setWorkbookCurrentPage((prev) =>
                                    Math.max(1, prev - 1)
                                  )
                                }
                                disabled={workbookCurrentPage === 1}
                                className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                              >
                                &lt;
                              </button>
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  min={1}
                                  max={Math.max(workbookNumPages || 0, 1)}
                                  step={1}
                                  value={workbookPageInput}
                                  onChange={handleWorkbookPageInputChange}
                                  onBlur={handleWorkbookPageInputCommit}
                                  onKeyDown={handleWorkbookPageInputKeyDown}
                                  disabled={!workbookNumPages}
                                  inputMode="numeric"
                                  className="w-10 h-10 border border-gray-300 rounded text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:text-gray-400 disabled:border-gray-200"
                                  aria-label="Go to workbook page"
                                  title="Enter a page number and press Enter"
                                />
                                <span className="text-sm font-medium whitespace-nowrap">
                                  / {workbookNumPages}
                                </span>
                              </div>
                              <button
                                onClick={() =>
                                  setWorkbookCurrentPage((prev) =>
                                    Math.min(workbookNumPages, prev + 1)
                                  )
                                }
                                disabled={
                                  workbookCurrentPage === workbookNumPages
                                }
                                className="p-1 text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                              >
                                &gt;
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {workbookPdfDocument ? (
                    <div className="flex flex-col flex-1 min-h-0">
                      {/* PDF Canvas */}
                      <div
                        className={`flex-1 overflow-auto flex justify-center ${
                          isMobile ? "p-0" : "p-4"
                        }`}
                        ref={pdfScrollRef}
                      >
                        {workbookPdfDocument ? (
                          <div
                            style={{
                              position: "relative",
                              width: isMobile
                                ? "100%"
                                : workbookPdfDims.width || 400,
                              height: workbookPdfDims.height || 300,
                              maxWidth: "100%",
                              // Touch gestures: enabled only when Hand tool is active on mobile
                              touchAction:
                                isMobile && handToolEnabled ? "none" : "auto",
                            }}
                            ref={pdfWrapperRef}
                          >
                            {/* Loading Overlay */}
                            {isWorkbookLoading && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                              </div>
                            )}
                            {/* PDF Canvas - Always rendered */}
                            <canvas
                              ref={workbookCanvasRef}
                              className="border border-gray-300 shadow-lg"
                              style={{
                                display: "block",
                                position: "absolute",
                                left: 0,
                                top: 0,
                                zIndex: 1,
                                imageRendering: "crisp-edges",
                              }}
                            />
                            {/* Text Input Modal for Selected Rectangle (desktop only) */}
                            {!isMobile && hasActiveWorkbookSelection && (
                              <div
                                style={{
                                  position: "absolute",
                                  left: workbookSelectedRect.left,
                                  top:
                                    workbookSelectedRect.top +
                                    workbookSelectedRect.height +
                                    10,
                                  background: "#fff",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: "0.5rem",
                                  padding: "1rem",
                                  boxShadow:
                                    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                  zIndex: 50,
                                  minWidth: "300px",
                                }}
                              >
                                <div className="relative">
                                  <button
                                    onClick={closeWorkbookMarkerSelection}
                                    className="absolute -top-5 -right-5 text-gray-500 hover:text-gray-700 transition-colors duration-200 bg-white rounded-full p-1 shadow-sm"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-4 w-4"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                  <div
                                    className={`flex flex-col ${
                                      teachMePromptSentRef.current
                                        ? "gap-1"
                                        : "gap-3"
                                    }`}
                                  >
                                    <div
                                      className={`flex gap-2 items-center ${
                                        teachMePromptSentRef.current
                                          ? "justify-center"
                                          : ""
                                      }`}
                                    >
                                      {(!hasEvaluateAttachment ||
                                        isProcessing) && (
                                        <button
                                          onClick={handleWorkbookSolve}
                                          disabled={isProcessing}
                                          className={`${
                                            isProcessing ? "w-full" : ""
                                          } px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${
                                            isProcessing
                                              ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                                              : "bg-indigo-600 text-white hover:bg-indigo-700"
                                          }`}
                                        >
                                          {isProcessing
                                            ? "Processing..."
                                            : teachMePromptSentRef.current
                                            ? "Explain"
                                            : "Solve Doubt"}
                                        </button>
                                      )}
                                      {!isProcessing &&
                                        !teachMePromptSentRef.current && (
                                          <WorkbookEvaluate
                                            key={`evaluate-${workbookEvaluateResetKey}`}
                                            questionText={inputText}
                                            conversationMode={communicationMode}
                                            playAudio={playBase64Audio}
                                            chatHistory={getRecentChatHistory(
                                              messages
                                            )}
                                            currentSubject={workbookSubject}
                                            questionImage={
                                              workbookQuestionImage
                                            }
                                            onFileChange={
                                              setHasEvaluateAttachment
                                            }
                                            onEvaluationStart={
                                              handleEvaluationStart
                                            }
                                            onEvaluationComplete={({
                                              sessionToken,
                                              evaluation,
                                              ocrExtractedText,
                                              ocrSuccess,
                                              ocrConfidence,
                                            }) => {
                                              if (
                                                sessionToken &&
                                                sessionToken !==
                                                  activeEvaluationSessionRef.current
                                              ) {
                                                return;
                                              }
                                              handleWorkbookEvaluationComplete({
                                                sessionToken,
                                                evaluation,
                                                ocrExtractedText,
                                                ocrSuccess,
                                                ocrConfidence,
                                              });
                                            }}
                                            onEvaluationEnd={
                                              handleEvaluationEnd
                                            }
                                            onEvaluationError={
                                              handleEvaluationError
                                            }
                                            isMobile={isMobile}
                                            onRateLimit={showRateLimitModal}
                                          />
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {/* Mobile: Show the same options as desktop under selection */}
                            {isMobile &&
                              hasActiveWorkbookSelection &&
                              !isChatModalOpen && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: workbookSelectedRect.left,
                                    top:
                                      workbookSelectedRect.top +
                                      workbookSelectedRect.height +
                                      10,
                                    background: "#fff",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "0.5rem",
                                    padding: "0.75rem",
                                    boxShadow:
                                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                                    zIndex: 60,
                                    minWidth: "260px",
                                  }}
                                >
                                  <div className="relative">
                                    <button
                                      onClick={closeWorkbookMarkerSelection}
                                      className="absolute -top-5 -right-5 text-gray-500 hover:text-gray-700 transition-colors duration-200 bg-white rounded-full p-1 shadow-sm"
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M6 18L18 6M6 6l12 12"
                                        />
                                      </svg>
                                    </button>
                                    <div className="flex flex-col gap-3">
                                      <div className="flex gap-2 items-center">
                                        {(!hasEvaluateAttachment ||
                                          isProcessing) && (
                                          <button
                                            onClick={handleWorkbookSolve}
                                            disabled={isProcessing}
                                            className={`${
                                              isProcessing ? "w-full" : ""
                                            } px-4 py-2 rounded-full text-sm font-bold transition-colors duration-200 ${
                                              isProcessing
                                                ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                                                : "bg-indigo-600 text-white hover:bg-indigo-700"
                                            }`}
                                          >
                                            {isProcessing
                                              ? "Processing..."
                                              : "Solve Doubt"}
                                          </button>
                                        )}
                                        {!isProcessing && (
                                          <WorkbookEvaluate
                                            key={`evaluate-mobile-${workbookEvaluateResetKey}`}
                                            questionText={inputText}
                                            conversationMode={communicationMode}
                                            playAudio={playBase64Audio}
                                            chatHistory={getRecentChatHistory(
                                              messages
                                            )}
                                            currentSubject={workbookSubject}
                                            questionImage={
                                              workbookQuestionImage
                                            }
                                            onFileChange={
                                              setHasEvaluateAttachment
                                            }
                                            onEvaluationStart={
                                              handleEvaluationStart
                                            }
                                            onEvaluationComplete={({
                                              sessionToken,
                                              evaluation,
                                              ocrExtractedText,
                                              ocrSuccess,
                                              ocrConfidence,
                                            }) => {
                                              if (
                                                sessionToken &&
                                                sessionToken !==
                                                  activeEvaluationSessionRef.current
                                              ) {
                                                return;
                                              }
                                              if (isMobile) {
                                                setIsChatModalOpen(true);
                                              }
                                              handleWorkbookEvaluationComplete({
                                                sessionToken,
                                                evaluation,
                                                ocrExtractedText,
                                                ocrSuccess,
                                                ocrConfidence,
                                              });
                                            }}
                                            onEvaluationEnd={
                                              handleEvaluationEnd
                                            }
                                            onEvaluationError={
                                              handleEvaluationError
                                            }
                                            isMobile={isMobile}
                                            onRateLimit={showRateLimitModal}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center h-full text-gray-500">
                            Click "Solve Workbook" to load the PDF
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`flex flex-1 items-start justify-start p-4 overflow-auto ${
                        currentWorkbookTab === "schoolWorkboo"
                          ? "pt-[20vh]"
                          : ""
                      }`}
                    >
                      {currentWorkbookTab === "schoolWorkbook" ? (
                        <div className="w-full max-w-4xl">
                          {/* Helper text */}
                          {/* <div className="text-center text-gray-500 mb-4">
                            Select a chapter to view the workbook or upload a
                            worksheet
                          </div> */}
                          {/* Row 1: Selects (stacked vertically; centered) */}
                          <div
                            key={workbookResetKey}
                            className="w-full max-w-3xl mx-auto"
                          >
                            {isMobile ? (
                              <>
                                <button
                                  onClick={() =>
                                    setShowWorkbookControls((prev) => !prev)
                                  }
                                  className="flex items-center justify-between w-full px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold rounded-full transition-colors text-sm"
                                >
                                  <span>Workbook Controls</span>
                                  <span className="text-xs">
                                    {showWorkbookControls ? "▲" : "▼"}
                                  </span>
                                </button>
                                {showWorkbookControls &&
                                  renderWorkbookSelectionPanel()}
                              </>
                            ) : (
                              renderWorkbookSelectionPanel()
                            )}
                            {/* Row 2: Action Buttons (stacked vertically; centered) */}
                            {(isMobile ? showWorkbookControls : true) && (
                              <div
                                key={`btns-${workbookResetKey}`}
                                className="grid grid-cols-1 gap-3 max-w-md mx-auto mt-4"
                              >
                                {/* <button
                                  onClick={handleSolveWorkbookClick}
                                  className="w-full h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full transition-colors text-sm"
                                >
                                  Get Workbook
                                </button> */}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                      {currentWorkbookTab === "myUploads" ? (
                        <div className="w-full max-w-4xl min-h-[600px] h-full flex flex-col">
                          {/* My Uploads Tab Content */}
                          {!fetchingPDF && (
                            <>
                              <div className="flex flex-col h-full min-h-0 overflow-hidden max-h-[90vh] md:max-h-[calc(100vh -250px)] gap-4">
                                {/* Header with Filters and Upload Button */}
                                <div className="flex flex-row items-center gap-3 flex-none flex-wrap">
                                  <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="px-6 py-3 text-white rounded-2xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-[#0D99FF]/25 whitespace-normal text-center leading-snug flex-shrink min-w-0"
                                    style={{ backgroundColor: "#0D99FF" }}
                                  >
                                    + Upload New File
                                  </button>
                                  <span className="text-gray-500 text-sm">
                                    Or
                                  </span>
                                  <span className="text-gray-500 text-sm">
                                    Select from below list.
                                  </span>
                                </div>

                                {/* File List */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-y-auto min-h-0 pb-6 mb-6">
                                  <div className="overflow-x-auto flex-1 min-h-0">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            File Details
                                          </th>
                                          <th className="px-4 py-3 text-right">
                                            <div className="flex flex-row flex-nowrap items-center justify-end gap-3">
                                              <div className="flex items-center gap-2">
                                                <svg
                                                  className="w-4 h-4 text-gray-500 flex-shrink-0"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                                  />
                                                </svg>
                                                <select
                                                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white"
                                                  value={uploadFilter.subject}
                                                  onChange={(e) => {
                                                    setUploadFilter((prev) => ({
                                                      ...prev,
                                                      subject: e.target.value,
                                                    }));
                                                  }}
                                                >
                                                  <option value="">
                                                    All Subjects
                                                  </option>
                                                  {subjects &&
                                                  Array.isArray(subjects) &&
                                                  subjects.length > 0 ? (
                                                    subjects.map((subject) => (
                                                      <option
                                                        key={subject}
                                                        value={subject}
                                                      >
                                                        {subject}
                                                      </option>
                                                    ))
                                                  ) : (
                                                    <>
                                                      <option value="Mathematics">
                                                        Mathematics
                                                      </option>
                                                      <option value="Physics">
                                                        Physics
                                                      </option>
                                                      <option value="Chemistry">
                                                        Chemistry
                                                      </option>
                                                      <option value="Biology">
                                                        Biology
                                                      </option>
                                                    </>
                                                  )}
                                                </select>
                                              </div>

                                              <select
                                                value={uploadFilter.chapter}
                                                onChange={(e) =>
                                                  setUploadFilter((prev) => ({
                                                    ...prev,
                                                    chapter: e.target.value,
                                                  }))
                                                }
                                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white"
                                              >
                                                <option value="">
                                                  All Chapters
                                                </option>
                                                {getFilteredChapters().map(
                                                  (chapter) => (
                                                    <option
                                                      key={chapter}
                                                      value={chapter}
                                                    >
                                                      {chapter}
                                                    </option>
                                                  )
                                                )}
                                              </select>
                                            </div>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredUploads.length === 0 ? (
                                          <tr>
                                            <td
                                              colSpan={2}
                                              className="px-4 py-12"
                                            >
                                              <div className="flex flex-col items-center justify-center text-center">
                                                <div className="text-gray-400 text-6xl mb-4">
                                                  📁
                                                </div>
                                                <h4 className="text-lg font-medium text-gray-600 mb-2">
                                                  No files found
                                                </h4>
                                                <p className="text-gray-500">
                                                  Upload your first worksheet to
                                                  get started
                                                </p>
                                              </div>
                                            </td>
                                          </tr>
                                        ) : (
                                          <>
                                            {isLoadingUploads ? (
                                              <tr>
                                                <td
                                                  colSpan={2}
                                                  className="px-4 py-12"
                                                >
                                                  <div className="flex flex-1 items-center justify-center w-full h-full min-h-[400px]">
                                                    <SimpleThreeDotsLoader message="Loading your uploads..." />
                                                  </div>
                                                </td>
                                              </tr>
                                            ) : (
                                              uploadedFiles.map(
                                                (file, index) => {
                                                  const formattedDate =
                                                    formatDateTime(
                                                      file.createdAt
                                                    );
                                                  console.log(
                                                    "Formatting date for file:",
                                                    file,
                                                    "Result:",
                                                    formattedDate
                                                  );
                                                  return (
                                                    <tr
                                                      key={file.id}
                                                      className="hover:bg-gray-50"
                                                    >
                                                      <td
                                                        className="px-4 py-3"
                                                        colSpan={2}
                                                      >
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            openUploadedSheet(
                                                              file
                                                            )
                                                          }
                                                          className="flex items-start justify-between w-full"
                                                        >
                                                          <div>
                                                            <div className="flex items-start flex-1">
                                                              <div className="text-2xl mr-3 flex-shrink-0">
                                                                📄
                                                              </div>
                                                              <div className="min-w-0 flex-1 justify-start">
                                                                <div className="text-sm text-left font-medium text-gray-900 truncate">
                                                                  {truncateFileName(
                                                                    file.filename ??
                                                                      file.name ??
                                                                      ""
                                                                  )}
                                                                </div>
                                                                <div className="text-xs text-left text-gray-500 mt-1">
                                                                  {formatFileSize(
                                                                    file.size
                                                                  )}
                                                                </div>
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                                                    {
                                                                      file.subject
                                                                    }
                                                                  </span>
                                                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                                    {
                                                                      file.chapter
                                                                    }
                                                                  </span>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                          <div className="text-right ml-4 flex-shrink-0">
                                                            <div className="text-xs text-gray-500">
                                                              {formattedDate}
                                                            </div>
                                                            {/* <div className="text-xs text-gray-400">
                                                              {formattedDate}
                                                            </div> */}
                                                          </div>
                                                        </button>
                                                      </td>
                                                    </tr>
                                                  );
                                                }
                                              )
                                            )}
                                          </>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Pagination */}
                                  {filteredUploads.length > 0 &&
                                    totalPages > 1 && (
                                      <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="text-xs text-gray-600">
                                          {getUploadsPaginationSummary()}
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <button
                                            onClick={() =>
                                              setCurrentPage((prev) =>
                                                Math.max(prev - 1, 1)
                                              )
                                            }
                                            disabled={currentPage === 1}
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ‹
                                          </button>
                                          {Array.from(
                                            {
                                              length: Math.min(totalPages, 5),
                                            },
                                            (_, i) => {
                                              let page;
                                              if (totalPages <= 5) {
                                                page = i + 1;
                                              } else if (currentPage <= 3) {
                                                page = i + 1;
                                              } else if (
                                                currentPage >=
                                                totalPages - 2
                                              ) {
                                                page = totalPages - 4 + i;
                                              } else {
                                                page = currentPage - 2 + i;
                                              }
                                              return (
                                                <button
                                                  key={page}
                                                  onClick={() =>
                                                    setCurrentPage(page)
                                                  }
                                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                                    currentPage === page
                                                      ? "bg-[#0D99FF] text-white"
                                                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                                  }`}
                                                >
                                                  {page}
                                                </button>
                                              );
                                            }
                                          )}
                                          <button
                                            onClick={() =>
                                              setCurrentPage((prev) =>
                                                Math.min(prev + 1, totalPages)
                                              )
                                            }
                                            disabled={
                                              currentPage === totalPages
                                            }
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ›
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </>
                          )}
                          {fetchingPDF && (
                            <div className="flex flex-1 items-center justify-center w-full h-full min-h-[600px]">
                              <SimpleThreeDotsLoader message="Loading your Worksheet" />
                            </div>
                          )}

                          {/* Upload Modal */}
                          {showUploadModal && (
                            <div className="fixed inset-0 bg-gradient-to-br from-[#0D99FF]/20 to-[#9F7AEA]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                              <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                                <div className="p-6">
                                  <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      Upload New File
                                    </h3>
                                    <button
                                      onClick={() => setShowUploadModal(false)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      ✕
                                    </button>
                                  </div>

                                  <div className="space-y-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        File{" "}
                                        <span className="text-red-500">*</span>
                                      </label>
                                      <div
                                        onClick={() =>
                                          fileInputRef.current?.click()
                                        }
                                        className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:border-[#0D99FF] transition-colors"
                                      >
                                        <div className="text-4xl text-gray-400 mb-2">
                                          📄
                                        </div>
                                        <p className="text-sm text-gray-600">
                                          {newFile.file
                                            ? newFile.file.name
                                            : "Click to select PDF file"}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Only PDF files are supported
                                        </p>
                                      </div>
                                      <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept=".pdf"
                                        onChange={(e) => {
                                          console.log(e.target.files);
                                          setNewFile((prev) => ({
                                            ...prev,
                                            file: e.target.files[0],
                                          }));
                                        }}
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subject{" "}
                                        {/* <span className="text-gray-400">
                                          (Optional)
                                        </span> */}
                                      </label>
                                      <select
                                        value={newFile.subject}
                                        onChange={(e) =>
                                          setNewFile((prev) => ({
                                            ...prev,
                                            subject: e.target.value,
                                          }))
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent"
                                      >
                                        <option value="">Select Subject</option>
                                        {subjects &&
                                        Array.isArray(subjects) &&
                                        subjects.length > 0 ? (
                                          subjects.map((subject) => (
                                            <option
                                              key={subject}
                                              value={subject}
                                            >
                                              {subject}
                                            </option>
                                          ))
                                        ) : (
                                          <>
                                            <option value="Mathematics">
                                              Mathematics
                                            </option>
                                            <option value="Physics">
                                              Physics
                                            </option>
                                            <option value="Chemistry">
                                              Chemistry
                                            </option>
                                            <option value="Biology">
                                              Biology
                                            </option>
                                          </>
                                        )}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Chapter{" "}
                                        {/* <span className="text-gray-400">
                                          (Optional)
                                        </span> */}
                                      </label>
                                      <select
                                        value={newFile.chapter}
                                        onChange={(e) =>
                                          setNewFile((prev) => ({
                                            ...prev,
                                            chapter: e.target.value,
                                          }))
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent"
                                        disabled={!newFile.subject}
                                      >
                                        <option value="">
                                          Select Chapter (Optional)
                                        </option>
                                        {getChaptersFromContext &&
                                        getChaptersFromContext(newFile.subject)
                                          ? getChaptersFromContext(
                                              newFile.subject
                                            ).map((chapter) => (
                                              <option
                                                key={chapter}
                                                value={chapter}
                                              >
                                                {chapter}
                                              </option>
                                            ))
                                          : null}
                                      </select>
                                    </div>

                                    <div
                                      className="relative"
                                      ref={goalsDropdownRef}
                                    >
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Goals{" "}
                                        <span className="text-gray-400">
                                          (Optional)
                                        </span>
                                      </label>
                                      <div
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white cursor-pointer flex items-center justify-between"
                                        onClick={() =>
                                          setGoalsDropdownOpen(
                                            !goalsDropdownOpen
                                          )
                                        }
                                      >
                                        <span
                                          className={`flex-1 truncate ${
                                            newFile.goals &&
                                            newFile.goals.length > 0
                                              ? "text-gray-900"
                                              : "text-gray-400"
                                          }`}
                                        >
                                          {newFile.goals &&
                                          newFile.goals.length > 0
                                            ? newFile.goals
                                                .map((goalValue) => {
                                                  const option =
                                                    goalOptions.find(
                                                      (opt) =>
                                                        opt.value === goalValue
                                                    );
                                                  return option
                                                    ? option.label
                                                    : goalValue;
                                                })
                                                .join(", ")
                                            : "Select Goals (Optional)"}
                                        </span>
                                        <ChevronDownIcon
                                          size={20}
                                          className={`text-gray-400 transition-transform ${
                                            goalsDropdownOpen
                                              ? "rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </div>
                                      {goalsDropdownOpen && (
                                        <div className="absolute z-10 w-full bottom-full mb-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-hidden">
                                          <div className="p-2 border-b border-gray-200">
                                            <input
                                              type="text"
                                              placeholder="Search goals..."
                                              value={goalsSearchTerm}
                                              onChange={(e) =>
                                                setGoalsSearchTerm(
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent outline-none"
                                              autoFocus
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            />
                                          </div>
                                          <div className="max-h-48 overflow-y-auto">
                                            {goalOptions
                                              .filter((option) =>
                                                option.label
                                                  .toLowerCase()
                                                  .includes(
                                                    goalsSearchTerm.toLowerCase()
                                                  )
                                              )
                                              .map((option) => {
                                                const isSelected =
                                                  newFile.goals &&
                                                  newFile.goals.includes(
                                                    option.value
                                                  );
                                                return (
                                                  <div
                                                    key={option.value}
                                                    className={`px-4 py-2 flex items-center gap-3 text-sm cursor-pointer transition-colors ${
                                                      isSelected
                                                        ? "bg-purple-50 text-primary"
                                                        : "hover:bg-purple-50 text-gray-700"
                                                    }`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setNewFile((prev) => {
                                                        const currentGoals =
                                                          Array.isArray(
                                                            prev.goals
                                                          )
                                                            ? prev.goals
                                                            : [];
                                                        const isSelected =
                                                          currentGoals.includes(
                                                            option.value
                                                          );
                                                        const updatedGoals =
                                                          isSelected
                                                            ? currentGoals.filter(
                                                                (g) =>
                                                                  g !==
                                                                  option.value
                                                              )
                                                            : [
                                                                ...currentGoals,
                                                                option.value,
                                                              ];
                                                        return {
                                                          ...prev,
                                                          goals: updatedGoals,
                                                        };
                                                      });
                                                    }}
                                                  >
                                                    <span
                                                      className={`w-4 h-4 flex items-center justify-center border rounded ${
                                                        isSelected
                                                          ? "bg-[#0D99FF] border-[#0D99FF] text-white"
                                                          : "border-gray-300 bg-white"
                                                      }`}
                                                    >
                                                      {isSelected && (
                                                        <CheckIcon size={12} />
                                                      )}
                                                    </span>
                                                    <span>{option.label}</span>
                                                  </div>
                                                );
                                              })}
                                            {goalOptions.filter((option) =>
                                              option.label
                                                .toLowerCase()
                                                .includes(
                                                  goalsSearchTerm.toLowerCase()
                                                )
                                            ).length === 0 && (
                                              <div className="px-4 py-3 text-sm text-gray-500">
                                                No results found.
                                              </div>
                                            )}
                                          </div>
                                          {newFile.goals &&
                                            newFile.goals.length > 0 && (
                                              <div className="p-2 border-t border-gray-200">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setNewFile((prev) => ({
                                                      ...prev,
                                                      goals: [],
                                                    }));
                                                  }}
                                                  className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                                                >
                                                  Clear All
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {!uploadingUserWorksheet && (
                                    <div className="flex space-x-3 mt-6">
                                      <button
                                        onClick={() =>
                                          setShowUploadModal(false)
                                        }
                                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleWorkBookUploadNew}
                                        disabled={!newFile.file}
                                        className="flex-1 px-4 py-2 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: "#0D99FF" }}
                                      >
                                        Upload
                                      </button>
                                    </div>
                                  )}
                                  {uploadingUserWorksheet && (
                                    <div className="flex items-center justify-center">
                                      <SimpleThreeDotsLoader message="Loading your Worksheet" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                      {currentWorkbookTab === "pyq" ? (
                        <div className="w-full max-w-4xl min-h-[600px] h-full flex flex-col">
                          {/* PYQ Tab Content */}
                          {!fetchingPDF && (
                            <>
                              <div className="flex flex-col h-full min-h-0 overflow-hidden max-h-[90vh] md:max-h-[calc(100vh -250px)] gap-4">
                                {/* File List */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-y-auto min-h-0 pb-6 mb-6">
                                  <div className="overflow-x-auto flex-1 min-h-0">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            File Details
                                          </th>
                                          <th className="px-4 py-3 text-right">
                                            <div className="flex flex-row flex-nowrap items-center justify-end gap-3">
                                              <div className="flex items-center gap-2">
                                                <svg
                                                  className="w-4 h-4 text-gray-500 flex-shrink-0"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                                  />
                                                </svg>
                                                <select
                                                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white"
                                                  value={pyqFilter.subject}
                                                  onChange={(e) => {
                                                    setPyqFilter((prev) => ({
                                                      ...prev,
                                                      subject: e.target.value,
                                                    }));
                                                  }}
                                                >
                                                  <option value="">
                                                    All Subjects
                                                  </option>
                                                  {subjects &&
                                                  Array.isArray(subjects) &&
                                                  subjects.length > 0 ? (
                                                    subjects.map((subject) => (
                                                      <option
                                                        key={subject}
                                                        value={subject}
                                                      >
                                                        {subject}
                                                      </option>
                                                    ))
                                                  ) : (
                                                    <>
                                                      <option value="Mathematics">
                                                        Mathematics
                                                      </option>
                                                      <option value="Physics">
                                                        Physics
                                                      </option>
                                                      <option value="Chemistry">
                                                        Chemistry
                                                      </option>
                                                      <option value="Biology">
                                                        Biology
                                                      </option>
                                                    </>
                                                  )}
                                                </select>
                                              </div>

                                              <select
                                                value={pyqFilter.year}
                                                onChange={(e) =>
                                                  setPyqFilter((prev) => ({
                                                    ...prev,
                                                    year: e.target.value,
                                                  }))
                                                }
                                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white"
                                              >
                                                <option value="">
                                                  All Years
                                                </option>
                                                <option value="2025">
                                                  2025
                                                </option>
                                                <option value="2024">
                                                  2024
                                                </option>
                                                <option value="2023">
                                                  2023
                                                </option>
                                                <option value="2022">
                                                  2022
                                                </option>
                                              </select>
                                            </div>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredPyqFiles.length === 0 ? (
                                          <tr>
                                            <td
                                              colSpan={2}
                                              className="px-4 py-12"
                                            >
                                              <div className="flex flex-col items-center justify-center text-center">
                                                <div className="text-gray-400 text-6xl mb-4">
                                                  📁
                                                </div>
                                                <h4 className="text-lg font-medium text-gray-600 mb-2">
                                                  No PYQ found
                                                </h4>
                                                <p className="text-gray-500">
                                                  No previous year questions
                                                  available
                                                </p>
                                              </div>
                                            </td>
                                          </tr>
                                        ) : (
                                          <>
                                            {isLoadingPyq ? (
                                              <tr>
                                                <td
                                                  colSpan={2}
                                                  className="px-4 py-12"
                                                >
                                                  <div className="flex flex-1 items-center justify-center w-full h-full min-h-[400px]">
                                                    <SimpleThreeDotsLoader message="Loading PYQ..." />
                                                  </div>
                                                </td>
                                              </tr>
                                            ) : (
                                              pyqFiles.map((file, index) => {
                                                const formattedDate =
                                                  formatDateTime(
                                                    file.createdAt
                                                  );
                                                return (
                                                  <tr
                                                    key={file.id}
                                                    className="hover:bg-gray-50"
                                                  >
                                                    <td
                                                      className="px-4 py-3"
                                                      colSpan={2}
                                                    >
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          openUploadedSheet(
                                                            file
                                                          )
                                                        }
                                                        className="flex items-start justify-between w-full"
                                                      >
                                                        <div>
                                                          <div className="flex items-start flex-1">
                                                            <div className="text-2xl mr-3 flex-shrink-0">
                                                              📄
                                                            </div>
                                                            <div className="min-w-0 flex-1 justify-start">
                                                              <div className="text-sm text-left font-medium text-gray-900 truncate">
                                                                {truncateFileName(
                                                                  file.filename ??
                                                                    file.name ??
                                                                    ""
                                                                )}
                                                              </div>
                                                              <div className="text-xs text-left text-gray-500 mt-1">
                                                                {formatFileSize(
                                                                  file.size
                                                                )}
                                                              </div>
                                                              <div className="flex flex-wrap gap-1 mt-2">
                                                                {file.subject && (
                                                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                                                    {
                                                                      file.subject
                                                                    }
                                                                  </span>
                                                                )}
                                                                {file.year && (
                                                                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                                    Year:{" "}
                                                                    {file.year}
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </div>
                                                        <div className="text-right ml-4 flex-shrink-0">
                                                          <div className="text-xs text-gray-500">
                                                            {formattedDate}
                                                          </div>
                                                        </div>
                                                      </button>
                                                    </td>
                                                  </tr>
                                                );
                                              })
                                            )}
                                          </>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Pagination */}
                                  {filteredPyqFiles.length > 0 &&
                                    pyqTotalPages > 1 && (
                                      <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="text-xs text-gray-600">
                                          {getPyqPaginationSummary()}
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <button
                                            onClick={() =>
                                              setPyqPage((prev) =>
                                                Math.max(prev - 1, 1)
                                              )
                                            }
                                            disabled={pyqPage === 1}
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ‹
                                          </button>
                                          {Array.from(
                                            {
                                              length: Math.min(
                                                pyqTotalPages,
                                                5
                                              ),
                                            },
                                            (_, i) => {
                                              let page;
                                              if (pyqTotalPages <= 5) {
                                                page = i + 1;
                                              } else if (pyqPage <= 3) {
                                                page = i + 1;
                                              } else if (
                                                pyqPage >=
                                                pyqTotalPages - 2
                                              ) {
                                                page = pyqTotalPages - 4 + i;
                                              } else {
                                                page = pyqPage - 2 + i;
                                              }
                                              return (
                                                <button
                                                  key={page}
                                                  onClick={() =>
                                                    setPyqPage(page)
                                                  }
                                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                                    pyqPage === page
                                                      ? "bg-[#0D99FF] text-white"
                                                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                                  }`}
                                                >
                                                  {page}
                                                </button>
                                              );
                                            }
                                          )}
                                          <button
                                            onClick={() =>
                                              setPyqPage((prev) =>
                                                Math.min(
                                                  prev + 1,
                                                  pyqTotalPages
                                                )
                                              )
                                            }
                                            disabled={pyqPage === pyqTotalPages}
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ›
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </>
                          )}
                          {fetchingPDF && (
                            <div className="flex flex-1 items-center justify-center w-full h-full min-h-[600px]">
                              <SimpleThreeDotsLoader message="Loading PDF..." />
                            </div>
                          )}

                          {/* Upload Modal */}
                          {showUploadModal && (
                            <div className="fixed inset-0 bg-gradient-to-br from-[#0D99FF]/20 to-[#9F7AEA]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                              <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                                <div className="p-6">
                                  <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      Upload New File
                                    </h3>
                                    <button
                                      onClick={() => setShowUploadModal(false)}
                                      className="text-gray-400 hover:text-gray-600"
                                    >
                                      ✕
                                    </button>
                                  </div>

                                  <div className="space-y-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        File{" "}
                                        <span className="text-red-500">*</span>
                                      </label>
                                      <div
                                        onClick={() =>
                                          fileInputRef.current?.click()
                                        }
                                        className="w-full p-6 border-2 border-dashed border-gray-300 rounded-xl text-center cursor-pointer hover:border-[#0D99FF] transition-colors"
                                      >
                                        <div className="text-4xl text-gray-400 mb-2">
                                          📄
                                        </div>
                                        <p className="text-sm text-gray-600">
                                          {newFile.file
                                            ? newFile.file.name
                                            : "Click to select PDF file"}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                          Only PDF files are supported
                                        </p>
                                      </div>
                                      <input
                                        ref={fileInputRef}
                                        type="file"
                                        className="hidden"
                                        accept=".pdf"
                                        onChange={(e) => {
                                          console.log(e.target.files);
                                          setNewFile((prev) => ({
                                            ...prev,
                                            file: e.target.files[0],
                                          }));
                                        }}
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subject{" "}
                                        {/* <span className="text-gray-400">
                                          (Optional)
                                        </span> */}
                                      </label>
                                      <select
                                        value={newFile.subject}
                                        onChange={(e) =>
                                          setNewFile((prev) => ({
                                            ...prev,
                                            subject: e.target.value,
                                          }))
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent"
                                      >
                                        <option value="">Select Subject</option>
                                        {subjects &&
                                        Array.isArray(subjects) &&
                                        subjects.length > 0 ? (
                                          subjects.map((subject) => (
                                            <option
                                              key={subject}
                                              value={subject}
                                            >
                                              {subject}
                                            </option>
                                          ))
                                        ) : (
                                          <>
                                            <option value="Mathematics">
                                              Mathematics
                                            </option>
                                            <option value="Physics">
                                              Physics
                                            </option>
                                            <option value="Chemistry">
                                              Chemistry
                                            </option>
                                            <option value="Biology">
                                              Biology
                                            </option>
                                          </>
                                        )}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Chapter{" "}
                                        {/* <span className="text-gray-400">
                                          (Optional)
                                        </span> */}
                                      </label>
                                      <select
                                        value={newFile.chapter}
                                        onChange={(e) =>
                                          setNewFile((prev) => ({
                                            ...prev,
                                            chapter: e.target.value,
                                          }))
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent"
                                        disabled={!newFile.subject}
                                      >
                                        <option value="">
                                          Select Chapter (Optional)
                                        </option>
                                        {getChaptersFromContext &&
                                        getChaptersFromContext(newFile.subject)
                                          ? getChaptersFromContext(
                                              newFile.subject
                                            ).map((chapter) => (
                                              <option
                                                key={chapter}
                                                value={chapter}
                                              >
                                                {chapter}
                                              </option>
                                            ))
                                          : null}
                                      </select>
                                    </div>

                                    <div
                                      className="relative"
                                      ref={goalsDropdownRef}
                                    >
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Goals{" "}
                                        <span className="text-gray-400">
                                          (Optional)
                                        </span>
                                      </label>
                                      <div
                                        className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white cursor-pointer flex items-center justify-between"
                                        onClick={() =>
                                          setGoalsDropdownOpen(
                                            !goalsDropdownOpen
                                          )
                                        }
                                      >
                                        <span
                                          className={`flex-1 truncate ${
                                            newFile.goals &&
                                            newFile.goals.length > 0
                                              ? "text-gray-900"
                                              : "text-gray-400"
                                          }`}
                                        >
                                          {newFile.goals &&
                                          newFile.goals.length > 0
                                            ? newFile.goals
                                                .map((goalValue) => {
                                                  const option =
                                                    goalOptions.find(
                                                      (opt) =>
                                                        opt.value === goalValue
                                                    );
                                                  return option
                                                    ? option.label
                                                    : goalValue;
                                                })
                                                .join(", ")
                                            : "Select Goals (Optional)"}
                                        </span>
                                        <ChevronDownIcon
                                          size={20}
                                          className={`text-gray-400 transition-transform ${
                                            goalsDropdownOpen
                                              ? "rotate-180"
                                              : ""
                                          }`}
                                        />
                                      </div>
                                      {goalsDropdownOpen && (
                                        <div className="absolute z-10 w-full bottom-full mb-1 bg-white border border-gray-300 rounded-xl shadow-lg max-h-60 overflow-hidden">
                                          <div className="p-2 border-b border-gray-200">
                                            <input
                                              type="text"
                                              placeholder="Search goals..."
                                              value={goalsSearchTerm}
                                              onChange={(e) =>
                                                setGoalsSearchTerm(
                                                  e.target.value
                                                )
                                              }
                                              className="w-full px-3 py-2 border border-gray-200 rounded focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent outline-none"
                                              autoFocus
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            />
                                          </div>
                                          <div className="max-h-48 overflow-y-auto">
                                            {goalOptions
                                              .filter((option) =>
                                                option.label
                                                  .toLowerCase()
                                                  .includes(
                                                    goalsSearchTerm.toLowerCase()
                                                  )
                                              )
                                              .map((option) => {
                                                const isSelected =
                                                  newFile.goals &&
                                                  newFile.goals.includes(
                                                    option.value
                                                  );
                                                return (
                                                  <div
                                                    key={option.value}
                                                    className={`px-4 py-2 flex items-center gap-3 text-sm cursor-pointer transition-colors ${
                                                      isSelected
                                                        ? "bg-purple-50 text-purple-700"
                                                        : "hover:bg-purple-50 text-gray-700"
                                                    }`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setNewFile((prev) => {
                                                        const currentGoals =
                                                          Array.isArray(
                                                            prev.goals
                                                          )
                                                            ? prev.goals
                                                            : [];
                                                        const isSelected =
                                                          currentGoals.includes(
                                                            option.value
                                                          );
                                                        const updatedGoals =
                                                          isSelected
                                                            ? currentGoals.filter(
                                                                (g) =>
                                                                  g !==
                                                                  option.value
                                                              )
                                                            : [
                                                                ...currentGoals,
                                                                option.value,
                                                              ];
                                                        return {
                                                          ...prev,
                                                          goals: updatedGoals,
                                                        };
                                                      });
                                                    }}
                                                  >
                                                    <span
                                                      className={`w-4 h-4 flex items-center justify-center border rounded ${
                                                        isSelected
                                                          ? "bg-[#0D99FF] border-[#0D99FF] text-white"
                                                          : "border-gray-300 bg-white"
                                                      }`}
                                                    >
                                                      {isSelected && (
                                                        <CheckIcon size={12} />
                                                      )}
                                                    </span>
                                                    <span>{option.label}</span>
                                                  </div>
                                                );
                                              })}
                                            {goalOptions.filter((option) =>
                                              option.label
                                                .toLowerCase()
                                                .includes(
                                                  goalsSearchTerm.toLowerCase()
                                                )
                                            ).length === 0 && (
                                              <div className="px-4 py-3 text-sm text-gray-500">
                                                No results found.
                                              </div>
                                            )}
                                          </div>
                                          {newFile.goals &&
                                            newFile.goals.length > 0 && (
                                              <div className="p-2 border-t border-gray-200">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setNewFile((prev) => ({
                                                      ...prev,
                                                      goals: [],
                                                    }));
                                                  }}
                                                  className="w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                                                >
                                                  Clear All
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {!uploadingUserWorksheet && (
                                    <div className="flex space-x-3 mt-6">
                                      <button
                                        onClick={() =>
                                          setShowUploadModal(false)
                                        }
                                        className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={handleWorkBookUploadNew}
                                        disabled={!newFile.file}
                                        className="flex-1 px-4 py-2 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: "#0D99FF" }}
                                      >
                                        Upload
                                      </button>
                                    </div>
                                  )}
                                  {uploadingUserWorksheet && (
                                    <div className="flex items-center justify-center">
                                      <SimpleThreeDotsLoader message="Loading your Worksheet" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                      {currentWorkbookTab === "samplePapers" ? (
                        <div className="w-full max-w-4xl min-h-[600px] h-full flex flex-col">
                          {!fetchingPDF && (
                            <>
                              <div className="flex flex-col h-full min-h-0 overflow-hidden max-h-[90vh] md:max-h-[calc(100vh -250px)] gap-4">
                                {/* File List */}
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex-1 flex flex-col overflow-y-auto min-h-0 pb-6 mb-6">
                                  <div className="overflow-x-auto flex-1 min-h-0">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            File Details
                                          </th>
                                          <th className="px-4 py-3 text-right">
                                            <div className="flex flex-row flex-nowrap items-center justify-end gap-3">
                                              <div className="flex items-center gap-2">
                                                <svg
                                                  className="w-4 h-4 text-gray-500 flex-shrink-0"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                                  />
                                                </svg>
                                                <select
                                                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D99FF] focus:border-transparent bg-white"
                                                  value={
                                                    samplePapersFilter.subject
                                                  }
                                                  onChange={(e) => {
                                                    setSamplePapersFilter(
                                                      (prev) => ({
                                                        ...prev,
                                                        subject: e.target.value,
                                                      })
                                                    );
                                                  }}
                                                >
                                                  <option value="">
                                                    All Subjects
                                                  </option>
                                                  {subjects &&
                                                  Array.isArray(subjects) &&
                                                  subjects.length > 0 ? (
                                                    subjects.map((subject) => (
                                                      <option
                                                        key={subject}
                                                        value={subject}
                                                      >
                                                        {subject}
                                                      </option>
                                                    ))
                                                  ) : (
                                                    <>
                                                      <option value="Mathematics">
                                                        Mathematics
                                                      </option>
                                                      <option value="Physics">
                                                        Physics
                                                      </option>
                                                      <option value="Chemistry">
                                                        Chemistry
                                                      </option>
                                                      <option value="Biology">
                                                        Biology
                                                      </option>
                                                    </>
                                                  )}
                                                </select>
                                              </div>
                                            </div>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredSamplePapers.length === 0 ? (
                                          <tr>
                                            <td
                                              colSpan={2}
                                              className="px-4 py-12"
                                            >
                                              <div className="flex flex-col items-center justify-center text-center">
                                                <div className="text-gray-400 text-6xl mb-4">
                                                  📁
                                                </div>
                                                <h4 className="text-lg font-medium text-gray-600 mb-2">
                                                  No files found
                                                </h4>
                                                <p className="text-gray-500">
                                                  No sample papers available
                                                </p>
                                              </div>
                                            </td>
                                          </tr>
                                        ) : (
                                          <>
                                            {isLoadingSamplePapers ? (
                                              <tr>
                                                <td
                                                  colSpan={2}
                                                  className="px-4 py-12"
                                                >
                                                  <div className="flex flex-1 items-center justify-center w-full h-full min-h-[400px]">
                                                    <SimpleThreeDotsLoader message="Loading sample papers..." />
                                                  </div>
                                                </td>
                                              </tr>
                                            ) : (
                                              samplePapersFiles.map(
                                                (file, index) => {
                                                  const formattedDate =
                                                    formatDateTime(
                                                      file.createdAt
                                                    );
                                                  return (
                                                    <tr
                                                      key={file.id}
                                                      className="hover:bg-gray-50"
                                                    >
                                                      <td
                                                        className="px-4 py-3"
                                                        colSpan={2}
                                                      >
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            openUploadedSheet(
                                                              file
                                                            )
                                                          }
                                                          className="flex items-start justify-between w-full"
                                                        >
                                                          <div>
                                                            <div className="flex items-start flex-1">
                                                              <div className="text-2xl mr-3 flex-shrink-0">
                                                                📄
                                                              </div>
                                                              <div className="min-w-0 flex-1 justify-start">
                                                                <div className="text-sm text-left font-medium text-gray-900 truncate">
                                                                  {truncateFileName(
                                                                    file.filename ??
                                                                      file.name ??
                                                                      ""
                                                                  )}
                                                                </div>
                                                                <div className="text-xs text-left text-gray-500 mt-1">
                                                                  {formatFileSize(
                                                                    file.size
                                                                  )}
                                                                </div>
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                  {file.subject && (
                                                                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                                                      {
                                                                        file.subject
                                                                      }
                                                                    </span>
                                                                  )}
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>
                                                          <div className="text-right ml-4 flex-shrink-0">
                                                            <div className="text-xs text-gray-500">
                                                              {formattedDate}
                                                            </div>
                                                          </div>
                                                        </button>
                                                      </td>
                                                    </tr>
                                                  );
                                                }
                                              )
                                            )}
                                          </>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>

                                  {/* Pagination */}
                                  {filteredSamplePapers.length > 0 &&
                                    samplePapersTotalPages > 1 && (
                                      <div className="px-4 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                                        <div className="text-xs text-gray-600">
                                          {getSamplePapersPaginationSummary()}
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <button
                                            onClick={() =>
                                              setSamplePapersPage((prev) =>
                                                Math.max(prev - 1, 1)
                                              )
                                            }
                                            disabled={samplePapersPage === 1}
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ‹
                                          </button>
                                          {Array.from(
                                            {
                                              length: Math.min(
                                                samplePapersTotalPages,
                                                5
                                              ),
                                            },
                                            (_, i) => {
                                              let page;
                                              if (samplePapersTotalPages <= 5) {
                                                page = i + 1;
                                              } else if (
                                                samplePapersPage <= 3
                                              ) {
                                                page = i + 1;
                                              } else if (
                                                samplePapersPage >=
                                                samplePapersTotalPages - 2
                                              ) {
                                                page =
                                                  samplePapersTotalPages -
                                                  4 +
                                                  i;
                                              } else {
                                                page = samplePapersPage - 2 + i;
                                              }
                                              return (
                                                <button
                                                  key={page}
                                                  onClick={() =>
                                                    setSamplePapersPage(page)
                                                  }
                                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                                    samplePapersPage === page
                                                      ? "bg-[#0D99FF] text-white"
                                                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                                                  }`}
                                                >
                                                  {page}
                                                </button>
                                              );
                                            }
                                          )}
                                          <button
                                            onClick={() =>
                                              setSamplePapersPage((prev) =>
                                                Math.min(
                                                  prev + 1,
                                                  samplePapersTotalPages
                                                )
                                              )
                                            }
                                            disabled={
                                              samplePapersPage ===
                                              samplePapersTotalPages
                                            }
                                            className="px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            ›
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                </div>
                              </div>
                            </>
                          )}
                          {fetchingPDF && (
                            <div className="flex flex-1 items-center justify-center w-full h-full min-h-[600px]">
                              <SimpleThreeDotsLoader message="Loading your Worksheet" />
                            </div>
                          )}
                        </div>
                      ) : null}
                      {currentWorkbookTab === "loadingPDF" ? (
                        <div className="flex items-center justify-center w-full h-full">
                          <SimpleThreeDotsLoader message={"Loading PDF..."} />
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                {/* Resizer - desktop only */}
                {!isMobile && showLeftPanel && (
                  <div
                    className="w-4 h-auto flex flex-col justify-center items-center cursor-col-resize flex-shrink-0"
                    onMouseDown={handleMouseDown}
                  >
                    {/*
                  The line itself (now two thinner lines separated by the icon),
                  this is optional but can make the line appear thicker overall.
                  If you want a single solid line, you can adjust the heights.
                */}
                    {/* Top line segment, uses flex-grow to fill space */}
                    <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />

                    {/* 2. The Enhanced Draggable Handle Icon (more noticeable) */}
                    <div className="p-1 rounded-md bg-white border border-gray-400 shadow-md transition-all duration-200 hover:bg-gray-100">
                      {/* Dot sizes and gaps are reduced for a more compact look */}
                      <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                        <div className="w-1 h-1 bg-gray-700 rounded-full" />
                      </div>
                    </div>

                    {/* Bottom line segment, uses flex-grow to fill space */}
                    <div className="flex-grow w-1 bg-gray-300 transition-colors duration-200" />
                  </div>
                )}
                {/* Right Panel - Chat Interface (desktop only) */}
                {!isMobile && showLeftPanel && (
                  <div
                    className="flex flex-col bg-white flex-shrink-0 min-w-0"
                    style={{ width: `${100 - leftPanelWidth}%` }}
                  >
                    {/* Chat Interface - Similar to regular DoubtSolver */}
                    <div className="h-full flex flex-col pb-0 sm:pt-4 sm:pl-4 sm:pr-4">
                      {showIntro && !fromQuestions && introHero}
                      {/* Messages Container - Scrollable with fixed height */}
                      {!showIntro && (
                        <div
                          ref={messagesContainerRef}
                          className="relative flex-1 overflow-y-auto pr-2 flex flex-col gap-3 min-h-0 custom-scrollbar"
                          style={{
                            maxHeight: "95%", // Better space utilization for workbook mode
                            scrollbarWidth: "thin",
                            scrollbarColor: "#cbd5e0 #f7fafc",
                          }}
                        >
                          {isHistoryLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                              <SimpleThreeDotsLoader message="Loading chat history..." />
                            </div>
                          )}
                          {messages.map((msg, index) =>
                            msg.type === "bot" ? (
                              <div
                                key={`bot-${index}-${
                                  msg.timestamp || Date.now()
                                }`}
                                className="flex flex-col"
                              >
                                <div className="flex justify-start">
                                  <div className="p-3 whitespace-pre-line rounded-xl max-w-[90%] sm:max-w-[80%] bg-gray-100 text-gray-800 self-start">
                                    <style>{`
                                  .ai-table-bordered table {
                                    border-collapse: collapse;
                                    width: 100%;
                                    margin-bottom: 1em;
                                  }
                                  .ai-table-bordered th, .ai-table-bordered td {
                                    border: 1px solid #888;
                                    padding: 6px 12px;
                                    text-align: left;
                                  }
                                  .ai-table-bordered th {
                                    background: #f3f4f6;
                                  }
                                `}</style>
                                    <div className="ai-table-bordered">
                                      {(msg.text || "").includes("<table>") ? (
                                        // Use HTML rendering for messages with tables
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: applyBasicMarkdown(
                                              msg.text || ""
                                            ).replace(
                                              /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*|<table>)/g,
                                              "<br>$2"
                                            ),
                                          }}
                                        />
                                      ) : (
                                        // Use custom MathJax renderer for messages without tables
                                        <MathJaxRenderer className="whitespace-pre-line">
                                          {applyBasicMarkdown(msg.text || "")
                                            .replace(
                                              /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*)/g,
                                              "\n$2"
                                            )
                                            .replace(/<br\s*\/?>/gi, "\n")}
                                        </MathJaxRenderer>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {/* Action icons below the bot message */}
                                <div className="mt-1 flex items-center gap-3 justify-start pl-1 text-gray-500 self-start py-2">
                                  <button
                                    type="button"
                                    title="Copy"
                                    className="hover:text-gray-700"
                                    onClick={async () => {
                                      try {
                                        await navigator.clipboard.writeText(
                                          msg.text || ""
                                        );
                                        toast?.success?.("Copied");
                                      } catch (e) {
                                        console.warn("Copy failed", e);
                                      }
                                    }}
                                  >
                                    <FaClone size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    title="Like"
                                    className={`hover:text-green-600 ${
                                      messageReactions[msg.id || msg.timestamp]
                                        ?.liked
                                        ? "text-green-600"
                                        : "text-gray-500"
                                    }`}
                                    onClick={() =>
                                      toggleMessageReaction(
                                        msg.id || msg.timestamp,
                                        "like"
                                      )
                                    }
                                  >
                                    <FaThumbsUp size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    title="Dislike"
                                    className={`hover:text-rose-600 ${
                                      messageReactions[msg.id || msg.timestamp]
                                        ?.disliked
                                        ? "text-rose-600"
                                        : "text-gray-500"
                                    }`}
                                    onClick={() =>
                                      toggleMessageReaction(
                                        msg.id || msg.timestamp,
                                        "dislike"
                                      )
                                    }
                                  >
                                    <FaThumbsDown size={14} />
                                  </button>

                                  <button
                                    type="button"
                                    title="Share"
                                    className="hover:text-indigo-600"
                                    onClick={async () => {
                                      const text = msg.text || "";
                                      try {
                                        if (navigator.share) {
                                          await navigator.share({ text });
                                        } else if (navigator.clipboard) {
                                          await navigator.clipboard.writeText(
                                            text
                                          );
                                          toast?.info?.("Copied to clipboard");
                                        }
                                      } catch (e) {
                                        console.warn("Share failed", e);
                                      }
                                    }}
                                  >
                                    <FaShareAlt size={14} />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                key={`user-${index}-${
                                  msg.timestamp || Date.now()
                                }`}
                                className="flex justify-end"
                              >
                                <div className="p-3 rounded-xl max-w-[90%] bg-indigo-50 text-gray-900 self-end">
                                  {/* Display question image if present */}
                                  {msg.questionImage && (
                                    <div className="mb-2">
                                      <div className="text-xs text-gray-600 mb-1">
                                        Question:
                                      </div>
                                      <img
                                        src={msg.questionImage}
                                        alt="Question"
                                        className="max-h-24 mb-2 rounded shadow border"
                                      />
                                    </div>
                                  )}
                                  {/* Display answer image if present */}
                                  {msg.image && (
                                    <div className="mb-2">
                                      <img
                                        src={msg.image}
                                        alt="Answer"
                                        className="max-h-24 mb-2 rounded shadow border"
                                      />
                                    </div>
                                  )}
                                  <div>
                                    <MathJaxRenderer className="whitespace-pre-line">
                                      {msg.text}
                                    </MathJaxRenderer>
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                          {showProcessingIndicator && (
                            <SimpleThreeDotsLoader message={loadingMessage} />
                          )}
                          <div ref={chatEndRef}></div>
                        </div>
                      )}
                      {/* Input Area - Fixed at bottom */}
                      <div
                        className={` ${
                          showIntro
                            ? "h-3/10 flex flex-col align-bottom  justify-end"
                            : ""
                        }`}
                      >
                        <div className={`relative mt-2 flex-shrink-0 w-full `}>
                          {/* Image preview area above the text input - smaller size preview */}
                          {imagePreview && (
                            <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="max-w-full rounded-lg"
                                style={{ maxHeight: "100px" }}
                              />
                              <button
                                onClick={handleRemoveImage}
                                className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                              >
                                <FaTimes size={14} />
                              </button>
                              <div className="text-xs text-gray-500 mt-1">
                                {image?.name}
                              </div>
                            </div>
                          )}
                          {/* File preview */}
                          {file && !imagePreview && (
                            <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                              <div className="flex items-center">
                                <FaPaperclip className="mr-2 text-indigo-500" />
                                <span className="text-sm">{file.name}</span>
                              </div>
                              <button
                                onClick={() => setFile(null)}
                                className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                              >
                                <FaTimes size={14} />
                              </button>
                            </div>
                          )}
                          {/* Drag and drop area */}
                          <div
                            className={`relative ${
                              isDragging
                                ? "border-indigo-600 border-dashed border-2 rounded-lg"
                                : ""
                            }`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            {isDragging && (
                              <div className="absolute inset-0 bg-indigo-100 bg-opacity-50 flex items-center justify-center pointer-events-none rounded-lg z-10">
                                <p className="text-indigo-600 font-semibold">
                                  Drop file to attach
                                </p>
                              </div>
                            )}
                            {/* Quick actions above input */}
                            {/* <div className="mb-2 flex flex-wrap gap-2 justify-center">
                          <button
                            type="button"
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
                          >
                            Take Quiz
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
                          >
                            Review Topic
                          </button>
                        </div> */}
                            <textarea
                              ref={textareaRef}
                              placeholder={
                                fromQuestions
                                  ? "Your question is already loaded above. Enter Your Doubt!"
                                  : "Enter Your Doubt"
                              }
                              value={inputText}
                              onChange={(e) => setInputText(e.target.value)}
                              onKeyDown={handleKeyDown}
                              onPaste={handlePaste}
                              className={`w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                                fromQuestions && inputText.length > 0
                                  ? "min-h-[100px] sm:min-h-[200px]"
                                  : "min-h-[60px] sm:min-h-[80px]"
                              }`}
                              disabled={isProcessing}
                            />
                            <div className="absolute bottom-3 left-4 flex gap-4 text-gray-500">
                              {/* Image/Camera section with dropdown */}
                              <div className="relative flex items-center gap-2">
                                <FaPlusCircle
                                  size={20}
                                  className={`cursor-pointer ${
                                    isProcessing ? "opacity-50" : ""
                                  }`}
                                  onClick={
                                    !isProcessing ? handleImageClick : undefined
                                  }
                                />
                                {/* New: Direct camera button beside plus */}
                                {/* Camera options dropdown */}
                                {showCameraOptions && (
                                  <div className="absolute bottom-8 left-0 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex flex-col gap-2 z-10">
                                    <button
                                      onClick={handleFileClick}
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    >
                                      <FaPaperclip size={16} /> Upload file{" "}
                                    </button>
                                    <input
                                      type="file"
                                      accept=".pdf,.doc,.docx,.txt"
                                      ref={fileInputRef}
                                      onChange={handleFileUpload}
                                      style={{ display: "none" }}
                                      disabled={isProcessing}
                                    />
                                    <button
                                      onClick={handleGalleryClick}
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    >
                                      <FaImage size={16} />
                                      Gallery
                                    </button>
                                    <button
                                      onClick={handleCameraClick}
                                      className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    >
                                      <FaCamera size={16} />
                                      Camera
                                    </button>
                                    {/*<button
                                  onClick={handleWorkbookClick}
                                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                >
                                  <FaBook size={16} />
                                  Workbook
                                </button>*/}
                                  </div>
                                )}

                                {/* New: Direct Worbook button beside plus */}
                                {/* {!disableWorkbookMode &&
                                  !teachMePromptSentRef.current && (
                                    <button
                                      onClick={handleWorkbookClick}
                                      className="flex items-center gap-2 py-2 text-sm hover:bg-gray-100 rounded"
                                    >
                                      <FaBook size={16} />
                                      Workbook
                                    </button>
                                  )} */}
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                ref={imageInputRef}
                                onChange={handleImageUpload}
                                style={{ display: "none" }}
                                disabled={isProcessing}
                              />
                            </div>
                            {/* Modified mic section with two options */}
                            <div
                              className={`absolute bottom-3 right-3 ${
                                isMobile
                                  ? "flex flex-col items-end gap-1"
                                  : "flex gap-2 items-center"
                              }`}
                            >
                              {isMobile ? (
                                <>
                                  <div className="flex gap-2 items-center">
                                    <span title="Live Assistance">
                                      <img
                                        src="/assets/video-camera-outline.jpg"
                                        alt="Live Assistance"
                                        width={24}
                                        height={24}
                                        style={{
                                          marginRight: 0,
                                          verticalAlign: "middle",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => {
                                          if (isProcessing) return;
                                          // Close any open camera/gallery menu to avoid overlay conflicts
                                          setShowCameraOptions(false);
                                          if (!liveAssistance) {
                                            setLiveAssistance(true);
                                            if (!communicationMode)
                                              setCommunicationMode(true);
                                            setShowCameraModal(true);
                                          } else {
                                            setLiveAssistance(false);
                                            setShowCameraModal(false);
                                          }
                                        }}
                                      />
                                    </span>
                                    {!isVoicePaused && (
                                      <button
                                        type="button"
                                        className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                                        onClick={
                                          !isProcessing
                                            ? isVoicePaused
                                              ? handleResumeListening
                                              : handleVoiceClick
                                            : undefined
                                        }
                                        disabled={isProcessing}
                                        title={
                                          isVoicePaused ? "Voice" : "Voice"
                                        }
                                        style={{ minWidth: 32 }}
                                      >
                                        <MdGraphicEq
                                          size={18}
                                          style={{ display: "block" }}
                                        />{" "}
                                      </button>
                                    )}
                                  </div>
                                  {((!showIntro && messages.length > 0) ||
                                    (isVoicePaused &&
                                      !showProcessingIndicator)) && (
                                    <div className="flex gap-2 items-center">
                                      {!showIntro &&
                                        messages.length > 0 &&
                                        !teachMePromptSentRef.current && (
                                          <button
                                            onClick={() =>
                                              handleClearChat(false)
                                            }
                                            className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs sm:text-sm whitespace-nowrap transition-colors hover:bg-primary-dark active:bg-primary-dark/90"
                                            title="Clear all messages and start fresh"
                                          >
                                            <FaEdit className="w-4 h-4" />
                                          </button>
                                        )}
                                      {isVoicePaused &&
                                        !showProcessingIndicator && (
                                          <button
                                            type="button"
                                            className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                                            onClick={
                                              !isProcessing &&
                                              !showProcessingIndicator
                                                ? isVoicePaused
                                                  ? handleResumeListening
                                                  : handleVoiceClick
                                                : undefined
                                            }
                                            disabled={
                                              isProcessing ||
                                              showProcessingIndicator
                                            }
                                            title={
                                              isVoicePaused ? "Voice" : "Voice"
                                            }
                                            style={{ minWidth: 32 }}
                                          >
                                            <MdGraphicEq
                                              size={18}
                                              style={{ display: "block" }}
                                            />{" "}
                                            {/* {isVoicePaused
                                          ? "Voice"
                                          : null} */}
                                          </button>
                                        )}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {!showIntro &&
                                    messages.length > 0 &&
                                    !teachMePromptSentRef.current && (
                                      <button
                                        onClick={() => handleClearChat(false)}
                                        className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs sm:text-sm whitespace-nowrap transition-colors hover:bg-primary-dark active:bg-primary-dark/90"
                                        title="Clear all messages and start fresh"
                                      >
                                        <FaEdit className="w-4 h-4" />
                                      </button>
                                    )}
                                  {/* {!teachMePromptSentRef.current && (
                                    <span title="Live Assistance">
                                      <img
                                        src="/assets/video-camera-outline.jpg"
                                        alt="Live Assistance"
                                        width={24}
                                        height={24}
                                        style={{
                                          marginRight: 0,
                                          verticalAlign: "middle",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => {
                                          if (isProcessing) return;
                                          setShowCameraOptions(false);
                                          if (!liveAssistance) {
                                            setLiveAssistance(true);
                                            if (!communicationMode)
                                              setCommunicationMode(true);
                                            setShowCameraModal(true);
                                          } else {
                                            setLiveAssistance(false);
                                            setShowCameraModal(false);
                                          }
                                        }}
                                      />
                                    </span>
                                  )} */}
                                  <button
                                    type="button"
                                    className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 ${"bg-gray-200 hover:bg-indigo-200 text-gray-700"}`}
                                    onClick={
                                      !isProcessing && !showProcessingIndicator
                                        ? isVoicePaused
                                          ? handleResumeListening
                                          : handleVoiceClick
                                        : undefined
                                    }
                                    disabled={
                                      isProcessing || showProcessingIndicator
                                    }
                                    title={isVoicePaused ? "Voice" : "Voice"}
                                    style={{ minWidth: 32 }}
                                  >
                                    <MdGraphicEq
                                      size={18}
                                      style={{ display: "block" }}
                                    />{" "}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Communication Mode UI - appears between messages and homework buttons */}
                      {communicationMode && renderCommunicationPanel("mb-2")}
                      {/* Show loading state in communication mode */}
                      {showProcessingIndicator && !workbookMode && (
                        <SimpleThreeDotsLoader message={loadingMessage} />
                      )}
                      {/* Button section - always show */}
                      <div
                        className={`${
                          isMobile && workbookMode
                            ? "w-full flex flex-col items-start gap-3 mt-2 flex-shrink-0"
                            : `flex flex-col sm:flex-row items-center ${
                                showSolutionModeButtons
                                  ? "justify-between"
                                  : "justify-end"
                              } gap-4 flex-shrink-0`
                        } ${fromQuestions ? " bg-red-600 mb-10" : ""}`}
                      >
                        {showSolutionModeButtons && (
                          <div
                            className={`${
                              isMobile && workbookMode
                                ? "flex w-full gap-2"
                                : "flex flex-wrap sm:flex-nowrap justify-center gap-2 sm:gap-4"
                            }`}
                          >
                            <div className="relative flex-1 min-w-0">
                              <button
                                onClick={() => {
                                  setActiveButton("homework");
                                  textareaRef.current?.focus();
                                }}
                                onMouseEnter={() =>
                                  setShowHomeworkTooltipNormal(true)
                                }
                                onMouseLeave={() =>
                                  setShowHomeworkTooltipNormal(false)
                                }
                                className={`flex w-full items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all whitespace-normal break-words text-center leading-tight
                            ${
                              activeButton === "homework"
                                ? "border border-gray-400 bg-indigo-50 text-primary"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                              >
                                <FaBook />
                                Full Solution
                              </button>
                              {showHomeworkTooltipNormal && (
                                <div
                                  className={`absolute ${
                                    workbookMode
                                      ? "bottom-full mb-2"
                                      : "top-full mt-2"
                                  } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}
                                >
                                  Get Full Solution
                                </div>
                              )}
                            </div>
                            <div className="relative flex-initial min-w-max">
                              <button
                                onClick={() => {
                                  setActiveButton("stepbystep");
                                  textareaRef.current?.focus();
                                }}
                                onMouseEnter={() =>
                                  setShowStepTooltipNormal(true)
                                }
                                onMouseLeave={() =>
                                  setShowStepTooltipNormal(false)
                                }
                                className={`inline-flex w-auto items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all whitespace-normal break-words text-center leading-tight
                            ${
                              activeButton === "stepbystep"
                                ? "border border-gray-400 bg-indigo-50 text-indigo-700"
                                : "text-gray-700 hover:bg-gray-50"
                            }`}
                              >
                                <FaPencil className="flex-shrink-0" />
                                <span className="min-w-0 text-center whitespace-nowrap leading-tight">
                                  Step by Step Doubt Clearing
                                </span>
                              </button>
                              {showStepTooltipNormal && (
                                <div
                                  className={`absolute ${
                                    workbookMode
                                      ? "bottom-full mb-2"
                                      : "top-full mt-2"
                                  } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}
                                >
                                  Get Step-by-Step Help
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        <div
                          className={`${
                            isMobile && workbookMode
                              ? "w-full flex justify-end gap-2 mt-2"
                              : `flex ${
                                  showSolutionModeButtons
                                    ? ""
                                    : "w-full justify-end"
                                } gap-2 sm:gap-4 mt-4 sm:mt-0`
                          }`}
                        >
                          {fromQuestions && !disableWorkbookMode && (
                            <button
                              onClick={() => window.history.back()}
                              className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                            >
                              Back to Quiz
                            </button>
                          )}
                          <button
                            className="px-4 py-2 sm:px-6 sm:py-3 text-white font-bold rounded-full transition-colors text-sm"
                            style={{
                              backgroundColor: isProcessing
                                ? "rgba(13, 153, 255, 0.6)"
                                : "#0D99FF",
                            }}
                            onMouseEnter={(e) => {
                              if (!isProcessing) {
                                e.currentTarget.style.backgroundColor =
                                  "#0B7ACC";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isProcessing) {
                                e.currentTarget.style.backgroundColor =
                                  "#0D99FF";
                              }
                            }}
                            onClick={
                              hasActiveWorkbookSelection
                                ? handleWorkbookSolve
                                : handleSend
                            }
                            disabled={isProcessing}
                          >
                            {isProcessing
                              ? "Processing..."
                              : fromQuestions
                              ? "Full Solution"
                              : "Solve My Doubt"}
                          </button>
                        </div>
                      </div>
                      {/* Click outside to close camera options */}
                      {showCameraOptions && (
                        <div
                          className="fixed inset-0 z-5"
                          onClick={() => setShowCameraOptions(false)}
                        />
                      )}
                      {/* Camera Modal */}
                      {!isMobile && (
                        <CameraModal
                          isOpen={showCameraModal}
                          onClose={() => {
                            setShowCameraModal(false);
                            if (liveAssistance) {
                              setLiveAssistance(false);
                            }
                            // Reset timer when closing modal
                            setSilenceTimerActive(false);
                            setSilenceTimerCount(5);
                            setTriggerCameraCapture(false);
                          }}
                          onCapture={handleCameraCapture}
                          hideCapture={liveAssistance}
                          silenceTimerActive={silenceTimerActive}
                          silenceTimerCount={silenceTimerCount}
                          triggerCapture={triggerCameraCapture}
                          onAutoCapture={handleAutoCameraCapture}
                          handleSend={handleSend}
                        />
                      )}
                      {/* Legacy duplicate audio block removed; playback handled by root-level audio element */}
                      {false && audioUrl && (
                        <audio
                          key={audioUrl} // Add key to force proper recreation
                          ref={audioRef}
                          src={audioUrl}
                          preload="auto"
                          controls={false}
                          autoPlay
                          style={{ display: "none" }}
                          onLoadStart={() =>
                            console.log("🎵 Audio loading started")
                          }
                          onCanPlay={() => console.log("🎵 Audio can play")}
                          onPlay={() => {
                            console.log("🎵 Audio started playing");
                            setIsSystemAudioPaused(false);
                            setIsSystemSpeaking(true);
                          }}
                          onPause={(event) => {
                            setIsSystemAudioPaused(true);
                            if (!event?.target?.ended) {
                              setIsSystemSpeaking(false);
                            }
                          }}
                          onEnded={() => {
                            if (lastAudioUrlRef.current) {
                              URL.revokeObjectURL(lastAudioUrlRef.current);
                              lastAudioUrlRef.current = null;
                            }
                            if (audioBlobRef.current) {
                              audioBlobRef.current = null;
                            }
                            setAudioUrl(null);
                            setIsSystemSpeaking(false); // Stop system animation for audio
                            setIsSystemAudioPaused(true);
                            // Start listening for next user prompt after audio ends
                            if (communicationMode) {
                              setIsUserSpeaking(true);
                              startUserRecognition();
                            }
                          }}
                          onError={(e) => {
                            captureSentryError(
                              `${DOUBT_SOLVER_FILE} | audioElement::onError | L6100`,
                              new Error(
                                `Audio element error: ${
                                  e?.target?.error?.code ?? "unknown"
                                }`
                              ),
                              {
                                audioErrorCode: e?.target?.error?.code ?? null,
                                audioErrorMessage:
                                  e?.target?.error?.message ?? null,
                                audioUrl,
                                communicationMode,
                              }
                            );
                            console.error("❌ Audio element error:", e);
                            console.error(
                              "Audio error details:",
                              e.target.error
                            );
                            // Clean up on error
                            if (lastAudioUrlRef.current) {
                              URL.revokeObjectURL(lastAudioUrlRef.current);
                              lastAudioUrlRef.current = null;
                            }
                            if (audioBlobRef.current) {
                              audioBlobRef.current = null;
                            }
                            setAudioUrl(null);
                            setIsSystemSpeaking(false);
                            setIsSystemAudioPaused(true);
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Mobile: full-screen chat modal overlay */}
              {isMobile && isChatModalOpen && (
                <MobileChatUIStandalone
                  asModal
                  title="Chat"
                  onClose={() => setIsChatModalOpen(false)}
                  showIntro={showIntro && !fromQuestions}
                  introContent={introHero}
                  messages={messages}
                  isHistoryLoading={isHistoryLoading}
                  messagesContainerRef={messagesContainerRef}
                  chatEndRef={chatEndRef}
                  messageContainerMaxHeight="calc(100vh - 200px)"
                  applyBasicMarkdown={applyBasicMarkdown}
                  workbookMode={workbookMode}
                  showProcessingIndicator={showProcessingIndicator}
                  loadingMessage={loadingMessage}
                  renderCommunicationPanel={(cls) =>
                    communicationMode ? renderCommunicationPanel(cls) : null
                  }
                  handleWorkbookClick={handleWorkbookClick}
                  imagePreview={imagePreview}
                  imageName={image?.name}
                  fileName={file?.name}
                  onRemoveImage={handleRemoveImage}
                  onClearFile={() => setFile(null)}
                  textareaRef={textareaRef}
                  inputText={inputText}
                  onInputTextChange={setInputText}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  isProcessing={isProcessing}
                  isVoicePaused={isVoicePaused}
                  onResumeListening={handleResumeListening}
                  onVoiceClick={handleVoiceClick}
                  onClearChat={handleClearChat}
                  onLiveAssistClick={handleLiveAssistToggle}
                  fromQuestions={fromQuestions}
                  onSend={handleSend}
                  communicationMode={Boolean(communicationMode)}
                  onStopSystemSpeech={stopActiveSystemSpeech}
                  showModeDropdownOnMobile={true}
                  activeMode={activeButton}
                  onModeChange={(m) => setActiveButton(m)}
                  onImageChange={handleImageUpload}
                  onFileChange={handleFileUpload}
                  onCameraClick={handleCameraClick}
                  onCameraCapture={handleCameraCapture}
                  cameraModalOpen={showCameraModal}
                  onCameraModalOpenChange={setShowCameraModal}
                  cameraHideCapture={liveAssistance}
                  onCameraVisibilityChange={setIsMobileCameraActive}
                  silenceTimerActive={silenceTimerActive}
                  silenceTimerCount={silenceTimerCount}
                  triggerCameraCapture={triggerCameraCapture}
                  onAutoCapture={handleAutoCameraCapture}
                  onWorkbookClick={handleWorkbookClick}
                  resetKey={activeButton}
                  handleSend={handleSend}
                />
              )}
              {/* Mobile floating chat launcher */}
              {isMobile && !isChatModalOpen && (
                <button
                  type="button"
                  onClick={() => setIsChatModalOpen(true)}
                  className="fixed bottom-5 right-4 z-[9999] flex items-center justify-center w-14 h-14 rounded-full bg-indigo-600 text-white shadow-xl shadow-indigo-500/50 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  aria-label="Open chat"
                >
                  <FaComments size={22} />
                </button>
              )}
            </div>
          </div>
        ) : /* Normal DoubtSolver Mode */
        isMobile ? (
          <MobileChatUIStandalone
            title="Chat"
            showIntro={showIntro && !fromQuestions}
            introContent={introHero}
            messages={messages}
            isHistoryLoading={isHistoryLoading}
            messagesContainerRef={messagesContainerRef}
            chatEndRef={chatEndRef}
            messageContainerMaxHeight="calc(100vh - 300px)"
            applyBasicMarkdown={applyBasicMarkdown}
            workbookMode={workbookMode}
            showProcessingIndicator={showProcessingIndicator}
            loadingMessage={loadingMessage}
            renderCommunicationPanel={(cls) =>
              communicationMode ? renderCommunicationPanel(cls) : null
            }
            imagePreview={imagePreview}
            imageName={image?.name}
            fileName={file?.name}
            onRemoveImage={handleRemoveImage}
            onClearFile={() => setFile(null)}
            textareaRef={textareaRef}
            inputText={inputText}
            onInputTextChange={setInputText}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            isProcessing={isProcessing}
            isVoicePaused={isVoicePaused}
            onResumeListening={handleResumeListening}
            onVoiceClick={handleVoiceClick}
            onClearChat={handleClearChat}
            onLiveAssistClick={handleLiveAssistToggle}
            fromQuestions={fromQuestions}
            onSend={handleSend}
            communicationMode={Boolean(communicationMode)}
            onStopSystemSpeech={stopActiveSystemSpeech}
            showModeDropdownOnMobile={true}
            activeMode={activeButton}
            onModeChange={(m) => setActiveButton(m)}
            onImageChange={handleImageUpload}
            onFileChange={handleFileUpload}
            onCameraClick={handleCameraClick}
            onCameraCapture={handleCameraCapture}
            cameraModalOpen={showCameraModal}
            onCameraModalOpenChange={setShowCameraModal}
            cameraHideCapture={liveAssistance}
            onCameraVisibilityChange={setIsMobileCameraActive}
            silenceTimerActive={silenceTimerActive}
            silenceTimerCount={silenceTimerCount}
            triggerCameraCapture={triggerCameraCapture}
            onAutoCapture={handleAutoCameraCapture}
            onWorkbookClick={handleWorkbookClick}
            resetKey={activeButton}
            handleSend={handleSend}
          />
        ) : (
          <div className="h-full flex flex-col pt-4 pr-4 pl-4 pb-1 sm:pt-6 sm:pl-6 sm:pr-6">
            {/* Modified intro section to handle pre-filled questions */}
            {showIntro && !fromQuestions && (
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-12 mb-6 flex-shrink-0">
                <img
                  src="/images/tutor.webp"
                  alt="Aanya"
                  className="w-24 h-28 sm:w-30 sm:h-34 object-cover object-top rounded-2xl"
                />
                <div className="pt-0 sm:pt-12 text-center sm:text-left">
                  <h1 className="text-2xl sm:text-4xl font-semibold text-gray-950 leading-tight">
                    Hello,{" "}
                    {userData?.name?.trim()?.split(/\s+/)[0] || "Student"}!
                    <br />
                    How can I help you ?
                  </h1>
                </div>
              </div>
            )}
            {/* Messages Container - Scrollable with fixed height */}
            {!showIntro && (
              <div
                ref={messagesContainerRef}
                className={`relative flex-1 overflow-y-auto pr-2 flex flex-col gap-3 ${
                  showPrefilledTextPreview ? "min-h-[55vh]" : "min-h-[70vh]"
                } ${
                  showPrefilledTextPreview ? "max-h-[55vh]" : "max-h-[80vh] "
                }custom-scrollbar`}
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#cbd5e0 #f7fafc",
                }}
              >
                {isHistoryLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                    <SimpleThreeDotsLoader message="Loading chat history..." />
                  </div>
                )}
                {messages.map((msg, index) =>
                  msg.type === "bot" ? (
                    <div className="flex flex-col justify-start">
                      <div
                        key={`bot-${index}-${msg.timestamp || Date.now()}`}
                        className="flex justify-start"
                      >
                        <div className="p-3 whitespace-pre-line rounded-xl max-w-[90%] sm:max-w-[80%] bg-blue-50 text-gray-800 self-start">
                          <style>{`
                      .ai-table-bordered table {
                        border-collapse: collapse;
                        width: 100%;
                        margin-bottom: 1em;
                      }
                      .ai-table-bordered th, .ai-table-bordered td {
                        border: 1px solid #888;
                        padding: 6px 12px;
                        text-align: left;
                      }
                      .ai-table-bordered th {
                        background: #f3f4f6;
                      }
                    `}</style>
                          <div className="ai-table-bordered">
                            {(msg.text || "").includes("<table>") ? (
                              // Use HTML rendering for messages with tables
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: applyBasicMarkdown(msg.text || "")
                                    // Add extra space before section headers (emoji or **)
                                    .replace(
                                      /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*|<table>)/g,
                                      "<br>$2"
                                    ),
                                  // Add <br> for all other newlines
                                  //  .replace(/\n/g, '<br>')
                                }}
                              />
                            ) : (
                              // Use custom MathJax renderer for messages without tables
                              <MathJaxRenderer className="whitespace-pre-line">
                                {applyBasicMarkdown(msg.text || "")
                                  // Add extra space before section headers (emoji or **)
                                  .replace(
                                    /(\n)([\u2705\u26a0\u274c\uD83D\uDCCA\uD83D\uDFE2]|\*\*)/g,
                                    "\n$2"
                                  )
                                  // Replace <br> tags with newlines for MathJax processing
                                  .replace(/<br\s*\/?>/gi, "\n")}
                              </MathJaxRenderer>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Action icons below the bot message */}
                      <div className="mt-1 flex items-center gap-3 justify-start pl-1 text-gray-500 self-start py-2">
                        <button
                          type="button"
                          title="Copy"
                          className="hover:text-gray-700"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(
                                msg.text || ""
                              );
                              toast?.success?.("Copied");
                            } catch (e) {
                              console.warn("Copy failed", e);
                            }
                          }}
                        >
                          <FaClone size={14} />
                        </button>
                        <button
                          type="button"
                          title="Like"
                          className={`hover:text-green-600 ${
                            messageReactions[msg.id || msg.timestamp]?.liked
                              ? "text-green-600"
                              : "text-gray-500"
                          }`}
                          onClick={() =>
                            toggleMessageReaction(
                              msg.id || msg.timestamp,
                              "like"
                            )
                          }
                        >
                          <FaThumbsUp size={14} />
                        </button>
                        <button
                          type="button"
                          title="Dislike"
                          className={`hover:text-rose-600 ${
                            messageReactions[msg.id || msg.timestamp]?.disliked
                              ? "text-rose-600"
                              : "text-gray-500"
                          }`}
                          onClick={() =>
                            toggleMessageReaction(
                              msg.id || msg.timestamp,
                              "dislike"
                            )
                          }
                        >
                          <FaThumbsDown size={14} />
                        </button>
                        <button
                          type="button"
                          title="Share"
                          className="hover:text-indigo-600"
                          onClick={async () => {
                            const text = msg.text || "";
                            try {
                              if (navigator.share) {
                                await navigator.share({ text });
                              } else if (navigator.clipboard) {
                                await navigator.clipboard.writeText(text);
                                toast?.info?.("Copied to clipboard");
                              }
                            } catch (e) {
                              console.warn("Share failed", e);
                            }
                          }}
                        >
                          <FaShareAlt size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={`user-${index}-${msg.timestamp || Date.now()}`}
                      className="flex flex-col items-end"
                    >
                      <div className="p-3 rounded-xl max-w-[90%] bg-indigo-50 text-gray-900 self-end">
                        {/* Display question image if present */}
                        {msg.questionImage && (
                          <div className="mb-2">
                            <div className="text-xs text-gray-600 mb-1">
                              Question:
                            </div>
                            <img
                              src={msg.questionImage}
                              alt="Question"
                              className="max-h-24 mb-2 rounded shadow border"
                            />
                          </div>
                        )}
                        {/* Display answer image if present */}
                        {msg.image && (
                          <div className="mb-2">
                            <img
                              src={msg.image}
                              alt="Answer"
                              className="max-h-24 mb-2 rounded shadow border"
                            />
                          </div>
                        )}
                        <div>
                          <MathJaxRenderer className="whitespace-pre-line">
                            {msg.text}
                          </MathJaxRenderer>
                        </div>
                      </div>
                    </div>
                  )
                )}
                {showProcessingIndicator && !workbookMode && (
                  <SimpleThreeDotsLoader message={loadingMessage} />
                )}
                <div ref={chatEndRef}></div>
              </div>
            )}
            {/* Input Area - Fixed at bottom */}
            <div className="relative mt-4 flex-shrink-0">
              {/* Prefilled question preview */}
              {showPrefilledTextPreview && (
                <div className="mb-2 p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Prefilled Doubt
                  </div>
                  <MathJaxRenderer className="text-sm text-gray-900 whitespace-pre-line">
                    {pendingPrefilledText}
                  </MathJaxRenderer>
                </div>
              )}
              {/* Image preview area above the text input - smaller size preview */}
              {imagePreview && (
                <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-w-full rounded-lg"
                    style={{ maxHeight: "100px" }}
                  />
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                  >
                    <FaTimes size={14} />
                  </button>
                  <div className="text-xs text-gray-500 mt-1">
                    {image?.name}
                  </div>
                </div>
              )}
              {/* File preview */}
              {file && !imagePreview && (
                <div className="mb-2 p-2 border border-gray-200 rounded-lg bg-gray-50 relative">
                  <div className="flex items-center">
                    <FaPaperclip className="mr-2 text-indigo-500" />
                    <span className="text-sm">{file.name}</span>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="absolute top-2 right-2 bg-gray-800 bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70"
                  >
                    <FaTimes size={14} />
                  </button>
                </div>
              )}
              {/* Drag and drop area */}
              <div
                className={`relative ${
                  isDragging
                    ? "border-indigo-600 border-dashed border-2 rounded-lg"
                    : ""
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {isDragging && (
                  <div className="absolute inset-0 bg-indigo-100 bg-opacity-50 flex items-center justify-center pointer-events-none rounded-lg z-10">
                    <p className="text-indigo-600 font-semibold">
                      Drop file to attach
                    </p>
                  </div>
                )}
                {/* Quick actions above input */}
                {/* <div className="mb-2 flex flex-wrap gap-2 justify-center">
                          <button
                            type="button"
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200"
                          >
                            Take Quiz
                          </button>
                          <button
                            type="button"
                            className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200"
                          >
                            Review Topic
                          </button>
                        </div> */}
                <textarea
                  ref={textareaRef}
                  placeholder={
                    fromQuestions
                      ? "Your question is already loaded above. Enter Your Doubt!"
                      : "Enter Your Doubt"
                  }
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  className={`w-full rounded-2xl border border-gray-300 px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    fromQuestions && inputText.length > 0
                      ? "min-h-[100px] sm:min-h-[200px]"
                      : "min-h-[60px] sm:min-h-[80px]"
                  }`}
                  disabled={isProcessing}
                />
                <div className="absolute bottom-3 left-4 flex gap-4 text-gray-500">
                  {/* Image/Camera section with dropdown */}
                  <div className="relative flex items-center gap-2">
                    <FaPlusCircle
                      size={20}
                      className={`cursor-pointer ${
                        isProcessing ? "opacity-50" : ""
                      }`}
                      onClick={!isProcessing ? handleImageClick : undefined}
                    />
                    {/* New: Direct camera button beside plus */}
                    {/* Camera options dropdown */}
                    {showCameraOptions && (
                      <div className="absolute bottom-8 left-0 bg-white border border-gray-300 rounded-lg shadow-lg p-2 flex flex-col gap-2 z-10">
                        <button
                          onClick={handleFileClick}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                        >
                          <FaPaperclip size={16} /> Upload file{" "}
                        </button>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          style={{ display: "none" }}
                          disabled={isProcessing}
                        />
                        <button
                          onClick={handleGalleryClick}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                        >
                          <FaImage size={16} />
                          Gallery
                        </button>
                        <button
                          onClick={handleCameraClick}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                        >
                          <FaCamera size={16} />
                          Camera
                        </button>
                        {/* <button
                          onClick={handleWorkbookClick}
                          className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 rounded"
                        >
                          <FaBook size={16} />
                          Workbook
                        </button> */}
                      </div>
                    )}
                  </div>
                  {/* New: Direct Worbook button beside plus */}
                  {!disableWorkbookMode && (
                    <button
                      onClick={handleWorkbookClick}
                      className="flex items-center gap-2 py-2 text-sm hover:bg-gray-100 rounded"
                    >
                      <FaBook size={16} />
                      Workbook
                    </button>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                    disabled={isProcessing}
                  />
                </div>
                {/* Modified mic section with two options */}
                <div
                  className={`absolute bottom-3 right-3 ${
                    isMobile
                      ? "flex flex-col items-end gap-1"
                      : "flex gap-2 items-center"
                  }`}
                >
                  {isMobile ? (
                    <>
                      <div className="flex gap-2 items-center">
                        <span title="Live Assistance">
                          <img
                            src="/assets/video-camera-outline.jpg"
                            alt="Live Assistance"
                            width={24}
                            height={24}
                            style={{
                              marginRight: 0,
                              verticalAlign: "middle",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              if (isProcessing) return;
                              // Close any open camera/gallery menu to avoid overlay conflicts
                              setShowCameraOptions(false);
                              if (!liveAssistance) {
                                setLiveAssistance(true);
                                if (!communicationMode)
                                  setCommunicationMode(true);
                                setShowCameraModal(true);
                              } else {
                                setLiveAssistance(false);
                                setShowCameraModal(false);
                              }
                            }}
                          />
                        </span>
                        {!isVoicePaused && (
                          <button
                            type="button"
                            className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                            onClick={
                              !isProcessing
                                ? isVoicePaused
                                  ? handleResumeListening
                                  : handleVoiceClick
                                : undefined
                            }
                            disabled={isProcessing}
                            title={isVoicePaused ? "Voice" : "Voice"}
                            style={{ minWidth: 32 }}
                          >
                            <MdGraphicEq
                              size={18}
                              style={{ display: "block" }}
                            />{" "}
                          </button>
                        )}
                      </div>
                      {((!showIntro && messages.length > 0) ||
                        isVoicePaused) && (
                        <div className="flex gap-2 items-center">
                          {!showIntro &&
                            messages.length > 0 &&
                            !teachMePromptSentRef.current && (
                              <button
                                onClick={() => handleClearChat(false)}
                                className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs sm:text-sm whitespace-nowrap transition-colors hover:bg-primary-dark active:bg-primary-dark/90"
                                title="Clear all messages and start fresh"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                            )}
                          {isVoicePaused && !showProcessingIndicator && (
                            <button
                              type="button"
                              className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 bg-gray-200 hover:bg-indigo-200 text-gray-700`}
                              onClick={
                                !isProcessing && !showProcessingIndicator
                                  ? isVoicePaused
                                    ? handleResumeListening
                                    : handleVoiceClick
                                  : undefined
                              }
                              disabled={isProcessing || showProcessingIndicator}
                              title={isVoicePaused ? "Voice" : "Voice"}
                              style={{ minWidth: 32 }}
                            >
                              <MdGraphicEq
                                size={18}
                                style={{ display: "block" }}
                              />{" "}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {!showIntro &&
                        messages.length > 0 &&
                        !teachMePromptSentRef.current && (
                          <button
                            onClick={() => handleClearChat(false)}
                            className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary text-white text-xs sm:text-sm whitespace-nowrap transition-colors hover:bg-primary-dark active:bg-primary-dark/90"
                            title="Clear all messages and start fresh"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                        )}
                      {/* <span title="Live Assistance">
                        <img
                          src="/assets/video-camera-outline.jpg"
                          alt="Live Assistance"
                          width={24}
                          height={24}
                          style={{
                            marginRight: 0,
                            verticalAlign: "middle",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (isProcessing) return;
                            setShowCameraOptions(false);
                            if (!liveAssistance) {
                              setLiveAssistance(true);
                              if (!communicationMode)
                                setCommunicationMode(true);
                              setShowCameraModal(true);
                            } else {
                              setLiveAssistance(false);
                              setShowCameraModal(false);
                            }
                          }}
                        />
                      </span> */}
                      <button
                        type="button"
                        className={`flex items-center justify-center gap-1 px-4 py-1 rounded-full shadow transition-colors text-xs font-medium h-8 ${"bg-gray-200 hover:bg-indigo-200 text-gray-700"}`}
                        onClick={!isProcessing ? handleVoiceClick : undefined}
                        disabled={isProcessing}
                        title={isVoicePaused ? "Voice" : "Voice"}
                        style={{ minWidth: 32 }}
                      >
                        <MdGraphicEq size={18} style={{ display: "block" }} />{" "}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Communication Mode UI - appears between messages and homework buttons */}
            {communicationMode && renderCommunicationPanel("mb-4")}
            {/* Button section - always show */}
            <div
              className={`${
                isMobile && workbookMode
                  ? "w-full flex flex-col items-start gap-3 mt-4 flex-shrink-0"
                  : `flex flex-col sm:flex-row items-center ${
                      showSolutionModeButtons
                        ? "justify-between"
                        : "justify-end"
                    } flex-shrink-0`
              }${fromQuestions ? " pb-5" : ""}`}
            >
              {showSolutionModeButtons && (
                <div
                  className={`${
                    isMobile && workbookMode
                      ? "flex w-full gap-2"
                      : "flex flex-wrap justify-center gap-2 sm:gap-4"
                  }`}
                >
                  <div className="relative">
                    <button
                      onClick={() => {
                        setActiveButton("homework");
                        textareaRef.current?.focus();
                      }}
                      onMouseEnter={() => setShowHomeworkTooltipNormal(true)}
                      onMouseLeave={() => setShowHomeworkTooltipNormal(false)}
                      className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all
                ${
                  activeButton === "homework"
                    ? "border border-gray-400 bg-indigo-50 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                    >
                      <FaBook />
                      Full Solution
                    </button>
                    {showHomeworkTooltipNormal && (
                      <div
                        className={`absolute ${
                          workbookMode ? "bottom-full mb-2" : "top-full mt-2"
                        } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}
                      >
                        Get Full Solution
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => {
                        setActiveButton("stepbystep");
                        textareaRef.current?.focus();
                      }}
                      onMouseEnter={() => setShowStepTooltipNormal(true)}
                      onMouseLeave={() => setShowStepTooltipNormal(false)}
                      className={`flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2 rounded-full text-sm font-semibold transition-all
                ${
                  activeButton === "stepbystep"
                    ? "border border-gray-400 bg-indigo-50 text-primary"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                    >
                      <FaPencil /> Step by Step Doubt Clearing
                    </button>
                    {showStepTooltipNormal && (
                      <div
                        className={`absolute ${
                          workbookMode ? "bottom-full mb-2" : "top-full mt-2"
                        } left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50`}
                      >
                        Get Step-by-Step Help
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div
                className={`${
                  isMobile && workbookMode
                    ? "w-full flex justify-end gap-2 mt-2"
                    : `flex ${
                        showSolutionModeButtons ? "" : "w-full justify-end"
                      } gap-2 sm:gap-4 mt-4 sm:mt-0`
                }`}
              >
                {fromQuestions && !disableWorkbookMode && (
                  <button
                    onClick={() => window.history.back()}
                    className="px-4 py-2 sm:px-6 sm:py-3 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-full transition-colors text-sm"
                  >
                    Back to Quiz
                  </button>
                )}
                <button
                  className={`px-4 py-2 sm:px-6 sm:py-3 ${
                    isProcessing
                      ? "bg-indigo-400"
                      : "bg-primary hover:bg-indigo-700"
                  } text-white font-bold rounded-full transition-colors text-sm`}
                  onClick={
                    hasActiveWorkbookSelection
                      ? handleWorkbookSolve
                      : handleSend
                  }
                  disabled={
                    isProcessing ||
                    (!inputText.trim() && !file && !image && !voice)
                  }
                >
                  {isProcessing
                    ? "Processing..."
                    : fromQuestions
                    ? "Full Solution"
                    : "Solve My Doubt"}
                </button>
              </div>
            </div>
            {/* Click outside to close camera options */}
            {showCameraOptions && (
              <div
                className="fixed inset-0 z-5"
                onClick={() => setShowCameraOptions(false)}
              />
            )}
            {/* Camera Modal */}
            {!isMobile && (
              <CameraModal
                isOpen={showCameraModal}
                onClose={() => {
                  setShowCameraModal(false);
                  setIsMobileCameraActive(false);
                  if (liveAssistance) {
                    setLiveAssistance(false);
                  }
                  // Reset timer when closing modal
                  setSilenceTimerActive(false);
                  setSilenceTimerCount(5);
                  setTriggerCameraCapture(false);
                }}
                onCapture={handleCameraCapture}
                hideCapture={liveAssistance}
                silenceTimerActive={silenceTimerActive}
                silenceTimerCount={silenceTimerCount}
                triggerCapture={triggerCameraCapture}
                onAutoCapture={handleAutoCameraCapture}
                handleSend={handleSend}
              />
            )}
            {/* Remove/disable LiveInteraction modal */}
            {/* <LiveInteraction
        isOpen={showLiveCameraModal}
        onClose={() => setShowLiveCameraModal(false)}
        onResponse={handleLiveCameraResponse}
        activeMode={activeButton}
        chatHistory={messages}
      /> */}
          </div>
        )}
        {audioUrl && (
          <audio
            key={audioUrl} // Add key to force proper recreation
            ref={audioRef}
            src={audioUrl}
            preload="auto"
            controls={false}
            autoPlay
            style={{ display: "none" }}
            onLoadStart={() => console.log("🎵 Audio loading started")}
            onCanPlay={() => console.log("🎵 Audio can play")}
            onPlay={() => {
              console.log("🎵 Audio started playing");
              setIsSystemAudioPaused(false);
              setIsSystemSpeaking(true);
            }}
            onPause={(event) => {
              setIsSystemAudioPaused(true);
              if (!event?.target?.ended) {
                setIsSystemSpeaking(false);
              }
            }}
            onEnded={() => {
              if (lastAudioUrlRef.current) {
                URL.revokeObjectURL(lastAudioUrlRef.current);
                lastAudioUrlRef.current = null;
              }
              if (audioBlobRef.current) {
                audioBlobRef.current = null;
              }
              setAudioUrl(null);
              setIsSystemSpeaking(false); // Stop system animation for audio
              setIsSystemAudioPaused(true);
              // Start listening for next user prompt after audio ends
              if (communicationMode) {
                setIsUserSpeaking(true);
                startUserRecognition();
              }
            }}
            onError={(e) => {
              captureSentryError(
                `${DOUBT_SOLVER_FILE} | audioElement::onError | shared`,
                new Error(
                  `Audio element error: ${e?.target?.error?.code ?? "unknown"}`
                ),
                {
                  audioErrorCode: e?.target?.error?.code ?? null,
                  audioErrorMessage: e?.target?.error?.message ?? null,
                  audioUrl,
                  communicationMode,
                  liveAssistance,
                }
              );
              console.error("❌ Audio element error:", e);
              console.error("Audio error details:", e.target.error);
              // Clean up on error
              if (lastAudioUrlRef.current) {
                URL.revokeObjectURL(lastAudioUrlRef.current);
                lastAudioUrlRef.current = null;
              }
              if (audioBlobRef.current) {
                audioBlobRef.current = null;
              }
              setAudioUrl(null);
              setIsSystemSpeaking(false);
              setIsSystemAudioPaused(true);
            }}
          />
        )}
      </div>
    </MathJaxContext>
  );
};

function WorkbookSelectionTimeline({ steps = [] }) {
  if (!steps.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <Header />
      <div className="relative">
        {steps.length > 1 && (
          <div className="pointer-events-none absolute left-6 top-0 bottom-0 w-px bg-primary/20" />
        )}
        <div className="space-y-6">
          {steps.map((step, index) => {
            const options = step.options ?? [];
            const hasOptions = options.length > 0;
            const isDisabled = Boolean(step.disabled);
            const isActive = Boolean(step.selectedValue);

            return (
              <div
                key={step.id ?? `${index}`}
                className="flex items-stretch gap-4"
              >
                <div className="flex flex-col items-center relative">
                  <div
                    className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 shadow-sm ${
                      isActive
                        ? "bg-primary border-primary text-white"
                        : "bg-white border-primary/30 text-primary"
                    }`}
                  >
                    {step.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">{step.title}</p>
                  {step.subtitle && (
                    <p className="text-sm text-gray-500">{step.subtitle}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3">
                    {hasOptions ? (
                      options.map((option) => {
                        const optionDisabled = Boolean(option.disabled);
                        const isSelected = step.selectedValue === option.value;
                        const bubbleStateClasses = isSelected
                          ? "bg-primary border-primary text-white shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary hover:text-primary";

                        return (
                          <button
                            type="button"
                            key={option.value}
                            disabled={isDisabled || optionDisabled}
                            onClick={() => {
                              if (
                                isDisabled ||
                                optionDisabled ||
                                !step.onSelect
                              ) {
                                return;
                              }
                              const nextValue =
                                isSelected && step.allowClear !== false
                                  ? ""
                                  : option.value;
                              step.onSelect(nextValue);
                            }}
                            className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                              isDisabled || optionDisabled
                                ? "opacity-60 cursor-not-allowed"
                                : "cursor-pointer"
                            } ${bubbleStateClasses}`}
                          >
                            {option.label}
                          </button>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        {step.emptyText || "Options will appear here"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DoubtSolver;
